<template>
  <Modal
    class="editions workbench"
    :class="{ 'studio-docked': studioDocked }"
    v-if="modals.edition"
    @close="onShellClose"
  >
    <!-- FT-1501 (Clocktower's FT-1476): while the Lot editor stands dirty,
         the shell's own × steps aside for Discard (a revert mark) and Save.
         A clean form, or no form at all, leaves this empty and Modal falls
         back to its own ×. -->
    <template v-if="roleFormDirty" slot="corner">
      <div class="button-group forge-acts corner-acts">
        <div
          class="button forge-discard"
          role="button"
          tabindex="0"
          title="Discard the edits — back to the last saved state"
          @click="discardRoleForm"
          @keydown.enter.prevent="discardRoleForm"
          @keydown.space.prevent="discardRoleForm"
        >
          <font-awesome-icon icon="undo" /> Discard
        </div>
        <div
          class="button forge-save"
          role="button"
          tabindex="0"
          :title="saveLabel"
          @click="saveRoleForm"
          @keydown.enter.prevent="saveRoleForm"
          @keydown.space.prevent="saveRoleForm"
        >
          <font-awesome-icon icon="feather-alt" /> Save
        </div>
      </div>
    </template>
    <!-- Golem fork (FT-854): the Almanac WORKBENCH — the full script-editing
         surface, and the modal's ONLY page (upstream's edition-tile screen
         retired 2026-08-17, user call: the picker carries the same art).
         Top: script selector + actions + the composition meter. Left: every
         role (officials + your library) with search and tag filters. Main:
         the current script in three views. The meter INFORMS, never blocks —
         non-conforming scripts save, share and play. -->
    <div class="custom workbench">
      <div class="wb-top">
        <!-- Row 1: the title, CENTERED on its own line (only the shell's
             close × shares it). Row 2: THE shared ScriptPicker (identical
             component to the host panel's) + the action buttons. The
             composition meter lives down beside the view tabs. -->
        <div class="wb-row1">
          <!-- FT-1188: the cap comes from KeyCap, the app's ONE drop-cap
               component, exactly as the entry doors' own "S" and the
               Chronicle's "C" do. It used to be hand-rolled here (`almanacCap`
               below, stood down) and the hand-rolled version differed twice
               over: it sized through `glyphStyleFrom`, the WORD normalization
               that measures against the set's reference B, where a lone
               drop-cap needs `glyphCapStyleFrom` (FT-948) — and its blood
               fallback still served blood-A.png, the "A" of the surface's old
               name "Almanac", so in blood mode the title read A-cripts. One
               component, one letter, one size. -->
          <h3 class="almanac-title"><KeyCap letter="S" />cripts</h3>
        </div>
        <div class="wb-row2">
          <!-- `manage` (FT-970) turns on the per-card shelf-remove ×. Only the
               bench asks for it: the host panel and the intro pass nothing and
               get the picker exactly as it always was. -->
          <ScriptPicker
            class="wb-script-picker"
            :cards="wbScriptCards"
            :picked-id="wbPickedId"
            manage
            @pick="onScriptPick"
            @forget="forgetScript"
          />
          <!-- ONE plus (user call): everything script-creation lives in the
               New-script overlay it opens — name, icon, and a paste/upload
               seed. It is an authoring surface like the rest of the bench, so
               a small screen does not offer it. -->
          <div class="wb-actions" v-if="!smallScreen">
            <div class="button wb-plus" title="New script" @click="newScript">
              <font-awesome-icon icon="plus" />
            </div>
            <!-- DELETE FOR EVERYONE (FT-970) lives HERE, off the card, on
                 purpose. It acts on the ONE script currently loaded — the one
                 whose contents are on screen to be looked at before it goes —
                 and it appears only when this browser holds that script's edit
                 key, because without the key the server refuses anyway.
                 The card's × is the casual, local, reversible-by-relinking
                 one; this is the one that reaches everybody you shared with,
                 so it is a deliberate trip to the toolbar and a named panel. -->
            <div
              class="button wb-destroy"
              v-if="canDestroyLoaded"
              title="Delete this script for everyone"
              @click="askDestroy"
            >
              <font-awesome-icon icon="trash-alt" />
            </div>
            <!-- FT-1490: rehomed from the retired import view — the links
                 export stands with the script actions -->
            <button
              type="button"
              class="ns-export"
              v-if="recents.length"
              title="Copy every script link this browser holds (edit keys included)"
              @click="copyLinks"
            >
              Export my script links
            </button>
          </div>
        </div>
      </div>

      <!-- SMALL SCREEN: the bench steps aside and says so.
           The picker above it stays live — WHICH script is loaded is the one
           thing worth reading here, and switching scripts is a single tap on
           a component the host panel already ships to phones. Everything
           below the picker (the 270px role rail, the three script views, the
           drag that moves a character up the night order) needs a desk. -->
      <div class="wb-small" v-if="smallScreen">
        <h4 class="ws-lead">The Almanac wants a desk</h4>
        <p class="ws-body">
          Two columns, a rail of every character, and a drag to move one up the
          night order. None of that fits in a hand — and the drag does not exist
          on a touch screen at all.
        </p>
        <div class="ws-loaded">
          <img class="ws-mark" :src="loadedCard.icon" alt="" />
          <span class="ws-name">{{ loadedCard.name }}</span>
        </div>
        <div class="ws-counts">
          <span
            class="ws-count"
            v-for="t in smallCounts"
            :key="t.team"
            :class="'team-' + t.team"
            :title="t.label"
          >
            <img v-if="teamGlyph(t.team)" :src="teamGlyph(t.team)" alt="" />
            <font-awesome-icon v-else :icon="t.icon" />
            {{ t.count }}
          </span>
        </div>
        <p class="ws-serves" v-if="servableText">Plays {{ servableText }}</p>
        <p class="ws-note">
          Pick a script above and it loads for the table as it always did. To
          read what a character does, open the script drawer.
        </p>
        <button type="button" class="ws-close" @click="toggleModal('edition')">
          Close
        </button>
      </div>

      <div class="wb-body" v-else>
        <!-- FT-1040b: while the forge holds the main pane, the sidebar's
             clicks would edit the script view you cannot see — so it stays
             put but visibly paused until the forge closes. -->
        <aside class="wb-sidebar" :class="{ inert: !!roleForm }">
          <!-- team toggles are TRI-STATE on click (show only → hide → off);
               the + at the end forges a new role -->
          <div class="wb-team-row">
            <button
              v-for="t in teamRow"
              :key="t.team"
              class="wb-team-toggle"
              :class="[
                'team-' + t.team,
                { on: teamState[t.team] === 1, exc: teamState[t.team] === -1 },
              ]"
              :title="teamTitle(t)"
              @click="toggleTeam(t.team)"
            >
              <!-- our own team art for every team (golem/glyphs); t.icon
                   stays as the fallback for a team the map lacks -->
              <img
                v-if="teamGlyph(t.team)"
                class="demon-glyph"
                :src="teamGlyph(t.team)"
                alt=""
              />
              <font-awesome-icon v-else :icon="t.icon" />
              <span class="cnt">{{ t.count }}</span>
            </button>
            <div
              class="button wb-plus"
              title="New role"
              @click="openRoleForm()"
            >
              <font-awesome-icon icon="plus" />
            </div>
          </div>
          <!-- FT-1039: the FOLD DISSOLVED. The facets stand in the open as
               chip rows below — the plated idiom the Chronicles filters and
               the team row above already speak — and the result tally rides
               INSIDE the search field, where feedback on the search belongs.
               The pill row went with the fold: a lit chip IS the active
               filter, and every chip cycles the team row's own tri-state
               (neutral → shown → hidden). -->
          <div class="wb-searchwrap">
            <input
              ref="searchInput"
              v-model="roleQuery"
              class="wb-search"
              placeholder="Search name or ability…"
              title="Press / to jump here"
              @input="onSearchInput"
              @keyup.enter="searchRoles"
              @keydown.esc.stop="clearSearch"
            />
            <span class="ws-tally" aria-live="polite"
              >{{ filteredCount }} of {{ totalCount }}</span
            >
            <button
              type="button"
              class="ws-clear"
              v-if="roleQuery"
              title="Clear the search"
              @click="clearSearch"
            >
              ×
            </button>
          </div>
          <!-- SOURCE — naturally multi (a script borrows from several).
               Edition marks where an edition has one, words where not. -->
          <div class="wb-chips" role="group" aria-label="Source">
            <button
              v-for="tag in sourceTags"
              :key="tag.id"
              type="button"
              class="wb-chip"
              :class="chipClass(tag)"
              :title="chipTitle(tag)"
              :aria-pressed="chipState(tag.id) === 1 ? 'true' : 'false'"
              @click="cycleChip(tag)"
            >
              <img
                v-if="editionMark(tag.id)"
                class="mark"
                :src="editionMark(tag.id)"
                alt=""
              />
              <span v-else class="word">{{ chipWord(tag) }}</span>
              <span class="cnt">{{ countFor(tag) }}</span>
            </button>
          </div>
          <!-- NIGHT — a lens, one at a time; click the lit one to clear it.
               The two wakes wear the night tabs' own moon phases. -->
          <div class="wb-seg" role="group" aria-label="Night">
            <button
              v-for="tag in nightTags"
              :key="tag.id"
              type="button"
              class="wb-cell"
              :class="{
                on: chipState(tag.id) === 1,
                zero: countFor(tag) === 0,
              }"
              :title="chipTitle(tag)"
              :aria-pressed="chipState(tag.id) === 1 ? 'true' : 'false'"
              @click="toggleLens(tag)"
            >
              <img
                v-if="nightMark(tag.id)"
                class="moon"
                :src="nightMark(tag.id)"
                alt=""
              />
              <span class="word">{{ chipWord(tag) }}</span>
              <span class="cnt">{{ countFor(tag) }}</span>
            </button>
          </div>
          <!-- SPECIAL — independent flags, tri-state like the sources
               ("hidden" on In-this-script answers "what could I add?"). -->
          <div class="wb-seg" role="group" aria-label="Special">
            <button
              v-for="tag in flagTags"
              :key="tag.id"
              type="button"
              class="wb-cell"
              :class="chipClass(tag)"
              :title="chipTitle(tag)"
              :aria-pressed="chipState(tag.id) === 1 ? 'true' : 'false'"
              @click="cycleChip(tag)"
            >
              <span class="word">{{ chipWord(tag) }}</span>
              <span class="cnt">{{ countFor(tag) }}</span>
            </button>
          </div>
          <!-- grouped by team (user call), sticky group headers -->
          <ul class="wb-all-roles" v-blood-scroll @scroll.passive="hideRoleTip">
            <template v-for="group in sidebarGroups">
              <li class="wb-shelf-head" :key="'head-' + group.team">
                {{ group.label }} <small>({{ group.roles.length }})</small>
              </li>
              <li
                v-for="entry in group.roles"
                :key="entry.key"
                :class="['team-' + entry.team, { inscript: entry.inScript }]"
                @click="toggleRole(entry)"
                @mouseenter="showRoleTip($event, entry)"
                @mouseleave="hideRoleTip"
              >
                <span
                  class="icon"
                  v-if="entry.iconUrl"
                  :style="{ backgroundImage: `url(${entry.iconUrl})` }"
                ></span>
                <span class="wb-role-name">{{ entry.name }}</span>
                <!-- FT-1485 (user): no "yours" / "library" label — a Lot is
                     a Lot, wherever it is kept. -->
                <!-- FT-1484 step 2: your private Lot — listed only for you -->
                <small
                  v-if="entry.private"
                  class="wb-private"
                  title="Private — only you see it in the library; scripts that use it still load it"
                  >Private</small
                >
                <span class="wb-in" v-if="entry.inScript">✓</span>
                <!-- FT-1486 (user): the edit lives on the Lot in the list, not
                     on the Lot in the script. Yours: the pen opens its editor.
                     Not yours: make a copy of your own to edit. Its own click
                     target — it never adds or removes the Lot. -->
                <button
                  type="button"
                  class="wb-edit"
                  :class="{ busy: copyingEntry === entry.key }"
                  :title="entryActionTitle(entry)"
                  :aria-label="entryActionTitle(entry) + ' — ' + entry.name"
                  @click.stop="entryAction(entry)"
                >
                  <font-awesome-icon
                    :icon="ownsEntry(entry) ? 'pen' : 'copy'"
                  />
                </button>
              </li>
            </template>
            <!-- FT-981: NOTHING MATCHED SAYS SO. The rail used to go silently
                 blank — the meter read "0 of 139" in small text off to the
                 side and the list itself was an empty box, which reads as a
                 component that broke rather than a search that found nothing.
                 It also names the way OUT, and the way out differs: a query
                 you can clear, filters you can drop, or both at once. -->
            <li class="wb-none" v-if="!sidebarGroups.length" :key="'none'">
              <p class="wn-lead">
                No character matches<template v-if="searchActive">
                  “{{ roleQuery.trim() }}”</template
                ><template v-if="searchActive && filtersActive"> with</template
                ><template v-if="filtersActive">
                  the filters you have set</template
                >.
              </p>
              <div class="wn-outs">
                <button
                  type="button"
                  class="wn-out"
                  v-if="searchActive"
                  @click="clearSearch"
                >
                  Clear the search
                </button>
                <button
                  type="button"
                  class="wn-out"
                  v-if="filtersActive"
                  @click="clearFilters"
                >
                  Clear the filters
                </button>
              </div>
              <p class="wn-aside" v-if="browseDown">
                The shared role library is not answering, so this searched the
                official characters and your own shelf only.
              </p>
            </li>
          </ul>
        </aside>

        <!-- FT-857: THE script view — the same component the player-facing
             ScriptDrawer renders. Editable here (drag the night order, remove
             / edit a role, the dirty Save-Discard chips); read-only there.
             FT-1040b: v-show, not v-if, while the forge borrows the pane —
             the component stays mounted, so its scroll comes back where it
             was when Save or Discard hands the pane back.
             FT-1043: the view rides a PANE now — a column that puts the
             BUILDER HEAD (icon, name, the Save/Discard plate)
             above it whenever the loaded script is CUSTOM. The new-script
             flow is just this pane holding an empty script: the modal that
             used to ask for name-and-icon first retired, because the bench
             itself is where both live. -->
        <div class="wb-pane" v-show="!roleForm">
          <div class="builder-head" v-if="builderOpen">
            <!-- the script's mark: the stock custom art until an upload or a
                 pick replaces it. CLICK opens the official-art overlay; a
                 DROPPED image takes the upload path directly (FT-856 inking
                 included). -->
            <div
              class="bh-icon"
              :class="{ dragover: iconDragOver }"
              :style="builderIconStyle"
              title="Script icon — click to pick from the official art, or drop an image to upload"
              @click="openIconPick"
              @dragover.prevent="iconDragOver = true"
              @dragleave="iconDragOver = false"
              @drop.prevent="onIconDrop"
            ></div>
            <input
              ref="bhName"
              class="bh-name"
              v-model="builderName"
              placeholder="Name this script…"
              maxlength="60"
            />
            <!-- the standardized Save/Discard plate — the same two buttons,
                 the same purple frame, the forge's header wears (FT-1043) -->
            <div class="button-group acts-plate" v-if="scriptDirty">
              <div
                class="button"
                title="Save this script to the vault"
                @click="saveToVault"
              >
                <font-awesome-icon icon="feather-alt" /> Save
              </div>
              <div
                class="button"
                title="Discard the edits — back to the last saved state"
                @click="discardEdits"
              >
                <font-awesome-icon icon="times" /> Discard
              </div>
            </div>
          </div>
          <ScriptView
            :roles="scriptRoles"
            :editable="true"
            :dirty="scriptDirty"
            :initial-view="wbView"
            @view="wbView = $event"
            @set-night="onSetNight"
            @remove="removeRole"
            @save="saveToVault"
            @discard="discardEdits"
          />
        </div>

        <!-- Golem fork (FT-851): the custom-role library — author a role once,
             save it to the library, and drop it into the current script as a
             full snapshot (the script carries the whole role). Fork-on-edit
             like scripts: saving someone else's role forks your own copy.
             FT-1040b (user call): the forge works WHERE THE SCRIPT LIVES —
             it takes the script view's own pane instead of floating over it;
             Save or Discard brings the script view back.
             FT-1490: character import retired — a file dropped on the forge
             is swallowed, so the page never navigates to it.
             FT-1501 (user: "Give L.O.T.S. the same character preview, so
             both games share this layout"): ONE SHELL — every Lot, new or
             existing, opens as this LEFT DRAWER with the table on the right,
             running this Lot's night preview whenever it can (Clocktower's
             Role Studio). The art is an overlay over the right side (below). -->
        <!-- FT-1520 (ported, FT-1535): NO v-blood-scroll here. The directive
             reserves a 30px lane for the drip on whatever it is put on,
             unconditionally — and this box is `overflow: hidden`, so it never
             scrolls and its drip can never draw. It was paying the lane anyway,
             on top of the 30px the `.fc-identity` column inside it pays for the
             drip that IS real. -->
        <div
          class="role-form forge-drawer"
          v-if="roleForm"
          @dragover.prevent
          @drop.prevent
        >
          <!-- the drawer's GLASS, on its own layer behind the content: a
               backdrop filter on the form itself would make the form the box
               every fixed descendant is placed in, and the stage bar and the
               art overlay (fixed over the table's side) would be caught
               inside -->
          <div class="ed-glass" aria-hidden="true"></div>
          <!-- drag the drawer's right edge to resize (the right-hand drawers'
               own grip and mechanism, mirrored); double-click resets; the
               arrow keys nudge it -->
          <div
            class="ed-grip"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize the Lot editor"
            :aria-valuenow="editorWidth"
            :aria-valuemin="editorWMin"
            :aria-valuemax="editorWMax"
            tabindex="0"
            title="Drag to resize — double-click to reset"
            @pointerdown="startEditorResize"
            @dblclick="resetEditorWidth"
            @keydown.left.prevent="nudgeEditorWidth(-20)"
            @keydown.right.prevent="nudgeEditorWidth(20)"
          ></div>
          <!-- FT-1040c: the title, reachable while the pane scrolls.
               FT-1501 (Clocktower's FT-1476): Save/Discard moved to the
               shell's own corner — a dirty form replaces the × there. -->
          <div class="forge-head">
            <h3 class="almanac-title forge-title">
              <img
                v-if="forgeCap"
                :src="forgeCap.src"
                class="font-cap"
                :style="forgeCap.style"
                alt="N"
              />{{ forgeCap ? "ew Role" : "New Role" }}
            </h3>
          </div>
          <div class="role-error" v-if="roleError">{{ roleError }}</div>

          <!-- FT-1041b: ONE FORM COLUMN under the living coin on the left;
               the ENTIRE right side is the art feed, always open. The
               Change-art door retired with the second column. -->
          <div class="forge-cols">
            <!-- FT-1530 (ported, FT-1535): this host's right edge IS the
                 drawer's edge (.forge-cols bleeds through the drawer's right
                 padding), so it opts into the directive's edge inset — the
                 line rides 5px from the edge instead of centring in its lane -->
            <div
              class="forge-col fc-identity"
              v-blood-scroll="{ edgeInset: 5 }"
            >
              <!-- the LIVE COIN — the real Token component rendering the
                   draft role exactly as the grimoire will: coin ground,
                   picked art, curved name, team rim, wake moon, setup stone.
                   Reused, not redrawn; pointer-events off so the preview
                   neither hovers a card nor clicks. -->
              <!-- FT-1042: the coin is a CONTROL now — drag the art to place
                   it on the face (percent-of-coin units, so the fit travels
                   1:1 to every token in game). The Token itself stays inert;
                   the wrapper owns the pointer. -->
              <!-- FT-1501: …and a DOOR — a click (a press that doesn't
                   drag) opens the art overlay over the table -->
              <div
                ref="forgeCoin"
                class="forge-coin"
                :class="{ placing: artDragging }"
                role="button"
                tabindex="0"
                aria-label="Choose the art"
                title="Click to choose the art — drag to place it on the coin"
                @pointerdown="onArtDragStart"
                @keydown.enter.prevent="onCardIcon"
                @keydown.space.prevent="onCardIcon"
              >
                <Token :role="previewRole" :hover-card="false" />
              </div>
              <!-- the art's dials — size scrub (percent) + a reset chip, in
                   the forge's own chip/dial idiom -->
              <div class="art-dials">
                <span class="forge-label">Art size</span>
                <NumberScrub
                  class="ad-size"
                  preset="night"
                  :value="artSizePct"
                  :min="40"
                  :max="200"
                  title="Art size, percent — drag to scrub, click to type"
                  @input="(n) => (roleForm.artScale = n / 100)"
                />
                <span class="ad-unit">%</span>
                <!-- FT-1126 (user: "we need a offset X and offset Y controls
                     for the icons there. or did you put them somewhere
                     else?"). They were somewhere else: `artX`/`artY` have
                     existed since the dials landed, but the ONLY way to set
                     them was to drag the art on the coin above. A drag is a
                     fine way to find a position and a poor way to say one —
                     it cannot be nudged by a tenth, typed, repeated across
                     two roles, or read back. So the two offsets get dials
                     beside the size, bound to the same fields the drag
                     writes: whichever you use, the other shows the result.
                     Bounds are the sanitizer's own (±50% of the coin), so a
                     dial can only ask for what the model already accepts. -->
                <span class="forge-label ad-gap">X</span>
                <NumberScrub
                  class="ad-off"
                  preset="night"
                  :value="artOffX"
                  :min="-50"
                  :max="50"
                  title="Art offset across, percent of the coin — drag to scrub, click to type"
                  @input="(n) => (roleForm.artX = n)"
                />
                <span class="ad-unit">%</span>
                <span class="forge-label ad-gap">Y</span>
                <NumberScrub
                  class="ad-off"
                  preset="night"
                  :value="artOffY"
                  :min="-50"
                  :max="50"
                  title="Art offset down, percent of the coin — drag to scrub, click to type"
                  @input="(n) => (roleForm.artY = n)"
                />
                <span class="ad-unit">%</span>
                <button
                  type="button"
                  class="forge-chip ad-reset"
                  v-if="artDirty"
                  title="Back to the stock fit — centred, full size"
                  @click="resetArt"
                >
                  Reset
                </button>
              </div>
              <!-- FT-1494 (user: "instead of this lets show the hover card
                   exactly and just have user click in to edit it there"):
                   the identity fields ARE the Lot's hover card now —
                   RoleHoverCard in its editable mode, drawn from the draft,
                   so the Studio shows exactly what a reader will hover. The
                   name, team, the two abilities (FT-1468's Light and Shadow,
                   marked) and the sections edit on the card; the icon points
                   at the art; author and visibility ride as chips under it.
                   Every edit lands in roleForm, so Save / Discard, the checks
                   and the save path are the form's, unchanged. -->
              <div class="forge-card">
                <RoleHoverCard
                  editable
                  :role="studioCard"
                  :chips="studioCardChips"
                  :active-section-index="activeSectionIndex"
                  @edit="onCardEdit"
                  @edit-icon="onCardIcon"
                  @select-section="activeSectionIndex = $event"
                >
                  <!-- the team popover: the forge's own five team buttons -->
                  <template #team-picker="{ done }">
                    <div class="team-pick">
                      <button
                        v-for="t in [
                          'townsfolk',
                          'outsider',
                          'minion',
                          'demon',
                          'traveller',
                        ]"
                        :key="t"
                        type="button"
                        class="team-btn"
                        :class="[
                          'team-' + (t === 'traveller' ? 'traveler' : t),
                          { on: roleForm.roleType === t },
                        ]"
                        :aria-pressed="
                          roleForm.roleType === t ? 'true' : 'false'
                        "
                        @click="
                          roleForm.roleType = t;
                          done();
                        "
                      >
                        <!-- our own team art for every team (golem/glyphs);
                           the Font Awesome branch stays as the fallback -->
                        <img
                          v-if="teamGlyph(t)"
                          class="demon-glyph"
                          :src="teamGlyph(t)"
                          alt=""
                        />
                        <font-awesome-icon
                          v-else
                          :icon="
                            t === 'townsfolk'
                              ? 'users'
                              : t === 'minion'
                              ? 'mask'
                              : 'walking'
                          "
                        />
                        {{ t }}
                      </button>
                    </div>
                  </template>
                </RoleHoverCard>
                <!-- the two facts the card doesn't carry, as chips under it,
                     plus "+ Section". FT-1484 step 2: PUBLIC / PRIVATE — who
                     finds this Lot in the library. Private (every new one's
                     default) lists it only in your own script builder; a
                     script that already uses it loads it either way. An
                     official Lot is always public.
                     FT-1498 (user: "the add section probably shouldn't go
                     on the card itself" -> "move '+ Section' and section
                     editing under the card, keeping only section names on
                     the card"): the card shows titles only now — adding and
                     editing a section happen down here instead. -->
                <div class="card-meta">
                  <input
                    v-if="authorEditing"
                    ref="authorInput"
                    class="cm-author-input"
                    v-model="roleForm.authorName"
                    maxlength="200"
                    placeholder="Author's name"
                    aria-label="Author"
                    @keydown.enter.prevent="endAuthor(true)"
                    @keydown.esc.stop.prevent
                    @keyup.esc.stop="endAuthor(true)"
                    @blur="endAuthor(false)"
                  />
                  <button
                    v-else
                    ref="authorBtn"
                    type="button"
                    class="forge-chip cm-author"
                    :class="{ hint: !roleForm.authorName }"
                    title="The author — shown beside the Lot in the library"
                    @click="startAuthor"
                  >
                    {{
                      roleForm.authorName
                        ? "By " + roleForm.authorName
                        : "Add an author"
                    }}
                  </button>
                  <div
                    v-if="!visibilityFixed"
                    class="forge-seg cm-visibility"
                    role="group"
                    aria-label="Visibility"
                  >
                    <button
                      type="button"
                      class="forge-cell"
                      :class="{ on: roleForm.visibility === 'private' }"
                      :aria-pressed="
                        roleForm.visibility === 'private' ? 'true' : 'false'
                      "
                      title="Only you see it in the library — scripts that use it still load it"
                      @click="roleForm.visibility = 'private'"
                    >
                      Private
                    </button>
                    <button
                      type="button"
                      class="forge-cell"
                      :class="{ on: roleForm.visibility === 'public' }"
                      :aria-pressed="
                        roleForm.visibility === 'public' ? 'true' : 'false'
                      "
                      title="Listed in everyone's library"
                      @click="roleForm.visibility = 'public'"
                    >
                      Public
                    </button>
                  </div>
                  <span v-else class="cm-vis-fixed">
                    Public — official Lots are always listed
                  </span>
                  <!-- FT-1514 (Clocktower's, ported FT-1538): the old "Tags"
                       box's setup chip, home — a fact about the LOT, read off
                       the card by anyone building a script, not about its
                       night. (Clocktower's Official chip beside it has no
                       L.O.T.S. twin: this editor has no Official toggle.) -->
                  <button
                    type="button"
                    class="forge-chip cm-setup"
                    :class="{ on: roleForm.setup }"
                    :aria-pressed="roleForm.setup ? 'true' : 'false'"
                    title="This Lot changes the game's setup"
                    @click="roleForm.setup = !roleForm.setup"
                  >
                    Affects setup
                  </button>
                  <button
                    type="button"
                    class="forge-chip cm-add-section"
                    :disabled="roleForm.guideSections.length >= sectionsMax"
                    title="Notes, how to run it, tips, a jinx — anything a reader of this Lot should know. Storyteller sections never reach the players."
                    @click="addGuideSection"
                  >
                    + Section
                  </button>
                </div>
                <!-- FT-1498: the selected section's editor, under the chip
                     row (FT-1491's Sections group, moved off the card) —
                     title, text, who reads it, "Only with…", move, remove,
                     and a close button. Esc closes the panel, not the
                     Studio. -->
                <div
                  v-if="activeGuideSection"
                  class="card-sec-panel"
                  @keydown.esc.stop.prevent
                  @keyup.esc.stop="closeSectionPanel"
                >
                  <div class="cs-head">
                    <input
                      ref="secTitleInput"
                      class="cs-title-input"
                      v-model="activeGuideSection.title"
                      :maxlength="sectionTitleMax"
                      placeholder="Title"
                      :aria-label="
                        'Section ' + (activeSectionIndex + 1) + ' title'
                      "
                      @keydown.enter.prevent="focusSecText"
                    />
                    <button
                      type="button"
                      class="forge-chip cs-close"
                      title="Close the section editor"
                      aria-label="Close the section editor"
                      @click="closeSectionPanel"
                    >
                      <font-awesome-icon icon="times" />
                    </button>
                  </div>
                  <textarea
                    ref="secTextInput"
                    class="cs-text-input"
                    v-model="activeGuideSection.text"
                    :maxlength="sectionTextMax"
                    rows="2"
                    placeholder="What should a reader know?"
                    :aria-label="
                      'Section ' + (activeSectionIndex + 1) + ' text'
                    "
                  ></textarea>
                  <div class="card-sec-tools">
                    <div class="fgi-picks">
                      <button
                        v-for="t in sectionTitlePicks"
                        :key="t"
                        type="button"
                        class="forge-chip fgi-pick"
                        :class="{ on: activeGuideSection.title === t }"
                        @click="activeGuideSection.title = t"
                      >
                        {{ t }}
                      </button>
                    </div>
                    <div class="fgi-foot">
                      <div
                        class="forge-seg fgi-audience"
                        role="group"
                        aria-label="Who reads it"
                      >
                        <button
                          type="button"
                          class="forge-cell"
                          :class="{
                            on: activeGuideSection.audience !== 'storyteller',
                          }"
                          :aria-pressed="
                            activeGuideSection.audience !== 'storyteller'
                              ? 'true'
                              : 'false'
                          "
                          title="Everyone at the table reads it"
                          @click="activeGuideSection.audience = 'everyone'"
                        >
                          Everyone
                        </button>
                        <button
                          type="button"
                          class="forge-cell"
                          :class="{
                            on: activeGuideSection.audience === 'storyteller',
                          }"
                          :aria-pressed="
                            activeGuideSection.audience === 'storyteller'
                              ? 'true'
                              : 'false'
                          "
                          title="Only the storyteller reads it — it never reaches the players"
                          @click="activeGuideSection.audience = 'storyteller'"
                        >
                          Storyteller
                        </button>
                      </div>
                      <span class="fgi-with-label">Only with</span>
                      <CharacterPicker
                        class="fgi-with"
                        :roles="sectionRoster"
                        :picked-id="activeGuideSection.with"
                        :picked-name="activeGuideSection.withName"
                        :icon-for="sectionIcon"
                        title="Show this section only when this Lot is on the script too"
                        @pick="(p) => pickSectionWith(activeGuideSection, p)"
                      />
                    </div>
                    <div class="fgi-acts">
                      <button
                        type="button"
                        class="forge-chip fgi-move"
                        :disabled="activeSectionIndex === 0"
                        title="Move up"
                        aria-label="Move this section up"
                        @click="moveActiveSection(-1)"
                      >
                        <font-awesome-icon icon="chevron-up" />
                      </button>
                      <button
                        type="button"
                        class="forge-chip fgi-move"
                        :disabled="
                          activeSectionIndex ===
                          roleForm.guideSections.length - 1
                        "
                        title="Move down"
                        aria-label="Move this section down"
                        @click="moveActiveSection(1)"
                      >
                        <font-awesome-icon icon="chevron-down" />
                      </button>
                      <button
                        type="button"
                        class="forge-chip fgi-move fgi-remove"
                        title="Remove this section"
                        aria-label="Remove this section"
                        @click="removeActiveSection"
                      >
                        <font-awesome-icon icon="times" /> Remove
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <!-- FT-1546: THE CARD ART — the Lot's own illustration for its
                   tarot card (not the coin icon above), uploaded to the
                   platform's asset store and shown UNTOUCHED (no ink, no
                   Light/Shadow tint). The card beside the controls is the real
                   LotCard, drawn from the draft, so the author sees the art
                   on the card exactly as a reader will; no art shows the bare
                   tarot card. Uploading needs a golem-studios.com account;
                   signed out, the control says so and nothing else changes. -->
              <div class="forge-group fg-cardart">
                <span class="forge-label">Card art</span>
                <div class="ca-body">
                  <LotCard
                    class="ca-card"
                    :role="studioCard"
                    :art="cardArtPreview"
                    :width="cardArtWidth"
                    :style="cardArtCardStyle"
                  />
                  <div class="ca-controls">
                    <button
                      v-if="accountId"
                      type="button"
                      class="forge-chip ca-upload"
                      :disabled="cardArtBusy"
                      :aria-busy="cardArtBusy ? 'true' : 'false'"
                      :title="
                        roleForm.cardArt
                          ? 'Pick another picture for this Lot’s card'
                          : 'Pick a picture for this Lot’s card — PNG, JPEG, WebP, up to 10MB'
                      "
                      @click="pickCardArt"
                    >
                      {{
                        cardArtBusy
                          ? "Uploading…"
                          : roleForm.cardArt
                          ? "Replace card art"
                          : "Upload card art"
                      }}
                    </button>
                    <p v-else class="ca-note ca-signed-out">
                      {{ cardArtLoginMessage }} — sign in to add card art.
                    </p>
                    <button
                      v-if="roleForm.cardArt"
                      type="button"
                      class="forge-chip ca-remove"
                      :disabled="cardArtBusy"
                      title="Take the art off — the card goes back to the bare tarot card"
                      @click="removeCardArt"
                    >
                      Remove card art
                    </button>
                    <p
                      v-if="cardArtError"
                      class="ca-note ca-error"
                      role="alert"
                    >
                      {{ cardArtError }}
                    </p>
                    <p v-else class="ca-note">
                      Shown as uploaded, on this Lot's card, for everyone in the
                      town. Saves with the Lot.
                    </p>
                  </div>
                  <input
                    ref="cardArtInput"
                    type="file"
                    class="ca-file"
                    accept="image/png,image/jpeg,image/webp,image/gif,image/avif"
                    tabindex="-1"
                    aria-hidden="true"
                    @change="onCardArtFile"
                  />
                </div>
              </div>

              <!-- FT-1538: THE NIGHT PANEL — Clocktower's step builder, ported
                   (FT-1475 rounds 1-2, the FT-1514 parts that survived,
                   FT-1529's control row). It replaces FT-1040's six-button
                   "At night, this character…" picker: the Lot's night is a
                   short LIST OF STEPS (night.steps — FT-1480 already carries
                   the model), the six shapes live on as "Start from a
                   template", and each step is ONE COMPACT ROW OF CONTROLS:
                   who chooses · picks or is told · how many · what.
                   The old "Tags" box dissolved: the wakes are the Wakes line
                   below (with "While dead"), Affects setup sits with the card
                   above, the Ability spent token with the Reminder tokens.
                   A LOT IS TWO-SIDED, ITS NIGHT IS ONE: L.O.T.S. stores one
                   night per Lot (night.steps), whichever side its seat is on,
                   so the list is one list — the Light and Shadow abilities
                   differ in words, not in the controls a night draws. -->
              <div class="forge-group fg-night">
                <span class="forge-label">Night</span>
                <!-- ONE Wakes line (FT-1514): first night, other nights, and
                     whether a dead seat still wakes, read as one answer. -->
                <div class="fg-wakeline" role="group" aria-label="Wakes">
                  <span class="fgw-word">Wakes</span>
                  <button
                    type="button"
                    class="forge-chip"
                    :class="{ on: wakesFirstNight }"
                    :aria-pressed="wakesFirstNight ? 'true' : 'false'"
                    title="Wakes on the first night"
                    @click="wakesFirstNight = !wakesFirstNight"
                  >
                    <img class="moon" :src="moonFirstArt" alt="" /> First night
                  </button>
                  <button
                    type="button"
                    class="forge-chip"
                    :class="{ on: wakesOtherNights }"
                    :aria-pressed="wakesOtherNights ? 'true' : 'false'"
                    title="Wakes on every night after the first"
                    @click="wakesOtherNights = !wakesOtherNights"
                  >
                    <img class="moon" :src="moonOtherArt" alt="" /> Other nights
                  </button>
                  <button
                    type="button"
                    class="forge-chip fgw-dead"
                    :class="{ on: roleForm.wakesDead }"
                    :aria-pressed="roleForm.wakesDead ? 'true' : 'false'"
                    title="Keep this Lot's row on the night sheet once its seat is dead"
                    @click="roleForm.wakesDead = !roleForm.wakesDead"
                  >
                    While dead
                  </button>
                </div>
                <div
                  class="night-steps"
                  v-if="wakesFirstNight || wakesOtherNights"
                >
                  <span class="forge-label nst-head"
                    >At night, this Lot…</span
                  >
                  <ol class="nst-list" v-if="roleForm.nightSteps.length">
                    <li
                      v-for="(s, i) in roleForm.nightSteps"
                      :key="'st' + i"
                      class="nst-row"
                    >
                      <span class="nst-n">{{ i + 1 }}</span>
                      <div class="nst-ctrls">
                        <div class="nst-grp nst-grp-act">
                          <!-- WHO CHOOSES, coloured by who it is (FT-1529):
                               INK + EDGE on the chosen cell, never a fill —
                               a filled cell is this panel's plain
                               "selected". L.O.T.S. (FT-1535's rule): the
                               Reader wears the Reader's colour; the Player
                               wears their seat's side — and a Lot on the
                               bench has no seat, so Player carries no side
                               colour here. A told step is always the
                               Reader's telling, so Player is unavailable
                               there rather than hidden. -->
                          <div
                            class="forge-seg nst-by"
                            role="group"
                            :aria-label="'Step ' + (i + 1) + ': who chooses'"
                          >
                            <button
                              type="button"
                              class="forge-cell nst-by-player"
                              :class="{ on: s.by === 'player' }"
                              :aria-pressed="
                                s.by === 'player' ? 'true' : 'false'
                              "
                              :disabled="s.kind === 'receives'"
                              :title="
                                s.kind === 'receives'
                                  ? 'Only the Reader tells — a told step is always yours'
                                  : 'This Lot\'s player chooses'
                              "
                              @click="setStep(i, 'by', 'player')"
                            >
                              Player
                            </button>
                            <button
                              type="button"
                              class="forge-cell nst-by-reader"
                              :class="{ on: s.by === 'storyteller' }"
                              :aria-pressed="
                                s.by === 'storyteller' ? 'true' : 'false'
                              "
                              title="The Reader chooses"
                              @click="setStep(i, 'by', 'storyteller')"
                            >
                              Reader
                            </button>
                          </div>
                          <div
                            class="forge-seg nst-kind"
                            role="group"
                            :aria-label="
                              'Step ' + (i + 1) + ': picks or is told'
                            "
                          >
                            <button
                              type="button"
                              class="forge-cell"
                              :class="{ on: s.kind === 'selects' }"
                              :aria-pressed="
                                s.kind === 'selects' ? 'true' : 'false'
                              "
                              :disabled="s.what === 'grimoire'"
                              title="Someone picks"
                              @click="setStep(i, 'kind', 'selects')"
                            >
                              Picks
                            </button>
                            <button
                              type="button"
                              class="forge-cell"
                              :class="{ on: s.kind === 'receives' }"
                              :aria-pressed="
                                s.kind === 'receives' ? 'true' : 'false'
                              "
                              title="The player is told something"
                              @click="setStep(i, 'kind', 'receives')"
                            >
                              Told
                            </button>
                          </div>
                        </div>
                        <div class="nst-grp nst-grp-what">
                          <OptionSelect
                            v-if="stepIsCountable(s)"
                            class="nst-count"
                            :name="'nst-count-' + i"
                            :aria-label="'Step ' + (i + 1) + ': how many'"
                            title="How many"
                            :options="stepCountOptions"
                            :value="s.count"
                            @input="(v) => setStep(i, 'count', v)"
                          />
                          <OptionSelect
                            class="nst-noun"
                            :name="'nst-noun-' + i"
                            :aria-label="'Step ' + (i + 1) + ': what it takes'"
                            title="What this step takes"
                            :options="nounOptions(s)"
                            :value="nounIdOf(s)"
                            @input="(v) => setSlot(i, 'noun', v)"
                          />
                        </div>
                      </div>
                      <span class="nst-acts">
                        <!-- drive the live table to the state that shows
                             this step -->
                        <button
                          v-if="studioAvailable && !studioInTown"
                          type="button"
                          class="nst-act nst-preview"
                          :class="{ on: studioLive && previewStepIdx === i }"
                          :aria-label="'Preview step ' + (i + 1)"
                          title="Show this step on the live table"
                          @click="previewStep(i)"
                        >
                          <font-awesome-icon icon="eye" />
                        </button>
                        <button
                          type="button"
                          class="nst-act"
                          :disabled="i === 0"
                          :aria-label="'Move step ' + (i + 1) + ' up'"
                          title="Move up"
                          @click="moveStep(i, -1)"
                        >
                          <font-awesome-icon icon="chevron-up" />
                        </button>
                        <button
                          type="button"
                          class="nst-act"
                          :disabled="i === roleForm.nightSteps.length - 1"
                          :aria-label="'Move step ' + (i + 1) + ' down'"
                          title="Move down"
                          @click="moveStep(i, 1)"
                        >
                          <font-awesome-icon icon="chevron-down" />
                        </button>
                        <button
                          type="button"
                          class="nst-act"
                          :aria-label="'Remove step ' + (i + 1)"
                          title="Remove this step"
                          @click="removeStep(i)"
                        >
                          <font-awesome-icon icon="times" />
                        </button>
                      </span>
                    </li>
                  </ol>
                  <!-- FT-1514: the template dropdown SEEDS the list, so it
                       stands with "+ Add a step". -->
                  <div class="nst-foot">
                    <button
                      type="button"
                      class="forge-chip nst-add"
                      :disabled="roleForm.nightSteps.length >= stepsMax"
                      :title="
                        roleForm.nightSteps.length >= stepsMax
                          ? 'At most ' + stepsMax + ' steps'
                          : 'Add a step to the end'
                      "
                      @click="addStep"
                    >
                      <font-awesome-icon icon="plus" /> Add a step
                    </button>
                    <OptionSelect
                      class="nst-start"
                      name="nst-template"
                      aria-label="Start from a template"
                      title="Replace the list with a starting template"
                      :options="templateOptions"
                      value=""
                      @input="applyStepTemplate"
                    />
                  </div>
                </div>
                <!-- FT-1514: the viewpoint switch, at the BOTTOM and saying
                     what it does. It PREVIEWS; it edits nothing. On Reader
                     it draws the checklist row the steps will produce. -->
                <div
                  class="nst-previewfoot"
                  v-if="wakesFirstNight || wakesOtherNights"
                >
                  <NightRowPreview
                    v-if="nightView === 'storyteller' && rowPreview"
                    class="nst-rowpreview"
                    v-bind="rowPreview"
                  />
                  <div class="nst-viewbar">
                    <span class="nsv-word">Preview as</span>
                    <div
                      class="forge-seg nst-view"
                      role="group"
                      aria-label="Preview as"
                    >
                      <button
                        type="button"
                        class="forge-cell"
                        :class="{ on: nightView === 'storyteller' }"
                        :aria-pressed="
                          nightView === 'storyteller' ? 'true' : 'false'
                        "
                        title="The Reader's view — the checklist row above, and the live table"
                        @click="pickNightView('storyteller')"
                      >
                        Reader
                      </button>
                      <button
                        type="button"
                        class="forge-cell"
                        :class="{ on: nightView === 'acting' }"
                        :aria-pressed="
                          nightView === 'acting' ? 'true' : 'false'
                        "
                        title="This Lot's player's view of the live table"
                        @click="pickNightView('acting')"
                      >
                        Player
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              <!-- reminder tokens as REAL PILLS — type + Enter mints one,
                 the × removes it. FT-1120: the pill's NAME is a control now,
                 not just a label: clicking it opens that token's deal rule,
                 and a token the deal will place wears a mark. The pills ARE
                 the stored shape (see THE FORGE'S REMINDER ENTRY). -->
              <div class="forge-group fg-rem">
                <span class="forge-label">Reminder tokens</span>
                <span class="rem-pills" ref="remPills">
                  <span
                    v-for="(r, i) in reminderPills"
                    :key="'rem' + i"
                    class="rem-pill"
                    :class="{ dealt: !!r.deal, editing: dealPop === i }"
                  >
                    <button
                      type="button"
                      class="rem-name"
                      :aria-expanded="dealPop === i ? 'true' : 'false'"
                      :title="dealSummary(r)"
                      @click="toggleDealPop(i, $event)"
                    >
                      <font-awesome-icon
                        v-if="r.deal"
                        icon="hand-point-right"
                        class="rem-mark"
                      />
                      {{ r.name }}
                    </button>
                    <button
                      type="button"
                      class="rem-x"
                      :title="'Remove “' + r.name + '”'"
                      :aria-label="'Remove ' + r.name"
                      @click="removeReminderPill(i)"
                    >
                      ×
                    </button>
                  </span>
                  <input
                    v-model="reminderDraft"
                    class="rem-input"
                    maxlength="40"
                    placeholder="Reminder token — Enter adds"
                    @keydown.enter.prevent="addReminderPill"
                    @blur="addReminderPill"
                  />
                </span>
                <!-- THE DEAL POPOVER — hangs off the pill it was opened
                   from (the tail points at it), and IN FLOW rather than
                   absolutely positioned: the identity column is its own
                   `overflow-y: auto` scroll box and the reminder group is the
                   last thing in it, so a floating panel would be clipped by
                   the scroller exactly where it is always opened. In flow the
                   column simply grows and scrolls to it. -->
                <div class="rem-pop" v-if="openDealPill" ref="remPop">
                  <span class="rem-tail" :style="{ left: dealTailX + 'px' }"
                    >▲</span
                  >
                  <span class="rem-pop-title">{{ openDealPill.name }}</span>
                  <button
                    type="button"
                    class="forge-chip rem-pop-toggle"
                    :class="{ on: !!openDealPill.deal }"
                    :aria-pressed="openDealPill.deal ? 'true' : 'false'"
                    title="The deal hands this token out when the roles land — nobody has to place it"
                    @click="toggleDealRule"
                  >
                    <!-- FT-1242: FA `hand-point-right` stood down — the
                           app's own dealing hand (ui-deal.png), which is
                           exactly what this toggle promises will happen. -->
                    <img class="rem-deal-mark" :src="uiDeal" alt="" />
                    The deal places this
                  </button>
                  <div class="rem-pop-rows" v-if="openDealPill.deal">
                    <label class="rem-pop-row">
                      <span class="rem-pop-lbl">How many seats</span>
                      <NumberScrub
                        class="rem-seats"
                        preset="night"
                        :value="openDealPill.deal.seats"
                        :min="1"
                        :max="20"
                        title="How many chairs get one — drag to scrub, click to type"
                        @input="(n) => setDeal('seats', n)"
                      />
                    </label>
                    <div class="rem-pop-row">
                      <span class="rem-pop-lbl">Who can get it</span>
                      <OptionSelect
                        name="rem-eligible"
                        aria-label="Who can get this token"
                        title="Which players are in the draw"
                        :options="dealEligibleOptions"
                        :value="openDealPill.deal.eligible"
                        @input="(v) => setDeal('eligible', v)"
                      />
                    </div>
                    <div class="rem-pop-row">
                      <span class="rem-pop-lbl">This role's own seat</span>
                      <OptionSelect
                        name="rem-self"
                        aria-label="This role's own seat"
                        title="Whether the player holding this role is in the draw"
                        :options="dealSelfOptions"
                        :value="openDealPill.deal.self"
                        @input="(v) => setDeal('self', v)"
                      />
                    </div>
                  </div>
                  <button
                    type="button"
                    class="forge-chip rem-pop-done"
                    title="Close"
                    @click="closeDealPop"
                  >
                    <font-awesome-icon icon="check" /> Done
                  </button>
                </div>
                <!-- FT-1514 (ported FT-1538): the third of the old "Tags"
                     chips, home. It offers an extra reminder token, so it
                     belongs where the tokens are. Nothing is enforced. -->
                <button
                  type="button"
                  class="forge-chip fg-spent"
                  :class="{ on: roleForm.spentToken }"
                  :aria-pressed="roleForm.spentToken ? 'true' : 'false'"
                  title="Offer an “Ability spent” reminder token for this Lot (nothing is enforced)"
                  @click="roleForm.spentToken = !roleForm.spentToken"
                >
                  Ability spent token
                </button>
              </div>
              <!-- FT-1494: FT-1491's Sections group retired — the sections
                   are edited on the card at the top of this column (its
                   titles, "+ Add section", and each section's controls). -->
            </div>
          </div>
          <!-- FT-1501: THE STAGE BAR — the preview's controls, floating along
               the table's foot, right of the drawer. The preview borrows the
               Night lab (golem/nightLab, enter/exit — never a second render):
               the real table IS the preview. No tabs — the preview is simply
               on whenever it can be; where it can't (a town, a Lot that never
               wakes, someone else's Lot, a stopped preview) the bar carries
               one line saying why, and the real table is left untouched. -->
          <div class="forge-stage" :class="{ 'in-town': studioInTown }">
            <div class="stage-bar" v-if="studioLive">
              <!-- a dot where this state (in the view shown) carries this
                   Lot's night text — hollow on its default, filled where any
                   of it is yours -->
              <div
                class="forge-seg stage-states"
                role="group"
                aria-label="Night state"
              >
                <button
                  v-for="s in studioGrammar"
                  :key="s"
                  type="button"
                  class="forge-cell"
                  :class="{ on: lab.stateId === s }"
                  :aria-pressed="lab.stateId === s ? 'true' : 'false'"
                  :aria-label="studioStateLabel(s) + dotWords(stateDots[s])"
                  :title="dotTitle(stateDots[s])"
                  @click="pickStudioState(s)"
                >
                  {{ studioStateLabel(s) }}
                  <span
                    v-if="stateDots[s]"
                    class="stage-dot"
                    :class="{ own: stateDots[s] === 'own' }"
                    aria-hidden="true"
                  ></span>
                </button>
              </div>
              <div
                class="forge-seg stage-views"
                role="group"
                aria-label="Seen by"
              >
                <button
                  v-for="v in studioViews"
                  :key="v.id"
                  type="button"
                  class="forge-cell"
                  :class="{ on: lab.perspective === v.id }"
                  :aria-pressed="lab.perspective === v.id ? 'true' : 'false'"
                  :aria-label="v.label + dotWords(viewDots[v.id])"
                  :title="dotTitle(viewDots[v.id])"
                  @click="pickStudioView(v.id)"
                >
                  {{ v.label }}
                  <span
                    v-if="viewDots[v.id]"
                    class="stage-dot"
                    :class="{ own: viewDots[v.id] === 'own' }"
                    aria-hidden="true"
                  ></span>
                </button>
              </div>
              <button
                type="button"
                class="forge-chip stage-replay"
                title="Play this state's arrival again"
                @click="replayStudio"
              >
                Replay
              </button>
            </div>
            <div class="stage-bar stage-note" v-else role="status">
              {{ studioNote }}
              <button
                v-if="previewStopped && previewPossible"
                type="button"
                class="forge-chip stage-resume"
                title="Run this Lot's night on the table again"
                @click="resumePreview"
              >
                Preview again
              </button>
            </div>
          </div>
          <!-- FT-1501: THE ART, AS AN OVERLAY — the coin (a click) or the
               card's icon opens it over the right side, above the table. A
               pick applies and closes it; Esc or its × close it too (and Esc
               stops here, so the editor stays open). The art's size / X / Y
               dials and the coin's drag stay in the drawer.
               FT-1041b: ONE live search across every group; the library's
               categories (and the officials' editions) stand as small sticky
               headers in the stream; the Official-art chip swaps the SOURCE.
               FT-1042 (user call): the WHOLE feed renders at once (no
               sentinel batching); the thumbs are cheap SVG data URLs and the
               imgs load lazily, so first paint stays light. v-show, so a
               reopen is instant and the feed keeps its place. -->
          <div
            ref="artPanel"
            class="forge-art"
            :class="{ 'in-town': studioInTown }"
            v-show="artOpen"
            role="dialog"
            aria-label="Choose the art"
          >
            <div class="art-head">
              <h4 class="art-title">Art</h4>
              <button
                type="button"
                class="art-close"
                title="Close the art (Esc)"
                aria-label="Close the art"
                @click="closeArt"
              >
                <CloseX />
              </button>
            </div>
            <div class="forge-feed" v-blood-scroll>
              <div class="feed-top">
                <input
                  ref="feedSearch"
                  v-model="ilSearch"
                  class="feed-search"
                  placeholder="Search the art…"
                />
                <button
                  type="button"
                  class="forge-chip feed-source"
                  :class="{ on: iconTab === 'official' }"
                  :aria-pressed="iconTab === 'official' ? 'true' : 'false'"
                  title="Borrow an official character's art instead of the icon library"
                  @click="toggleOfficialSource"
                >
                  Official art
                </button>
                <span class="ip-current" v-if="currentArtSrc">
                  <img :src="currentArtSrc" alt="" />
                  <span
                    class="ip-reroll"
                    v-if="roleForm.iconRef"
                    title="Re-roll the texture — same art, fresh grain"
                    @click="rerollIcon"
                    ><font-awesome-icon icon="redo-alt"
                  /></span>
                </span>
              </div>
              <template v-if="feedReady">
                <template v-for="group in feedGroups">
                  <div class="feed-head" :key="'fh-' + group.key">
                    {{ group.label }} <small>({{ group.items.length }})</small>
                  </div>
                  <div class="feed-grid" :key="'fg-' + group.key">
                    <template v-if="iconTab === 'official'">
                      <div
                        class="icon-cell"
                        v-for="official in group.items"
                        :key="'off-' + official.id"
                        :class="{ selected: roleForm.icon === official.id }"
                        @click="pickIcon(official.id)"
                      >
                        <span
                          class="icon"
                          :style="{
                            backgroundImage: `url(${iconUrl(official.id)})`,
                          }"
                        ></span>
                        <span class="label">{{ official.name }}</span>
                      </div>
                    </template>
                    <template v-else>
                      <div
                        class="icon-cell"
                        v-for="e in group.items"
                        :key="'lib-' + e.n"
                        :class="{ selected: roleForm.iconRef === e.n }"
                        :title="e.n.replace(/-/g, ' ')"
                        @click="pickLibraryIcon(e)"
                      >
                        <!-- FT-1537: loading BEFORE src. Vue sets attributes
                             in template order, and an img whose src lands
                             first starts fetching before it is lazy — every
                             one of the hidden thumbs then decoded on open
                             (the reopen freeze). -->
                        <img
                          class="il-thumb"
                          loading="lazy"
                          :src="ilThumb(e)"
                          alt=""
                        />
                        <span class="label">{{ e.n.replace(/-/g, " ") }}</span>
                      </div>
                    </template>
                  </div>
                </template>
                <div class="feed-none" v-if="!feedGroups.length">
                  Nothing in the art matches “{{ ilSearch.trim() }}”.
                </div>
              </template>
              <div class="il-loading" v-else>Loading the library…</div>
            </div>
          </div>
        </div>
      </div>
      <!-- FT-1043: the ICON OVERLAY — clicking the builder head's icon
           floats a browser over the bench; a pick lands on the header and
           the overlay closes. FT-1043b: that browser shows the ICON
           LIBRARY (the forge feed's own set + thumbs), not official role
           art — a pick bakes through the same shadedRender/stylizeIcon
           path a forge pick gets, neutral-tinted like an upload. The
           upload door lives here too (FT-856: an upload arrives twice —
           inked to the official look, and untouched), and dropping an
           image on the header icon never needs this open at all. -->
      <div class="role-form icon-overlay" v-if="iconPickOpen" v-blood-scroll>
        <h3>Script icon</h3>
        <div class="io-tools">
          <div class="button" @click="$refs.nsUpload.click()">
            <font-awesome-icon icon="file-upload" /> Upload an image
          </div>
          <div class="ns-style-toggle" v-if="iconUp.styled">
            <span
              :class="{ on: builderLogo === iconUp.styled }"
              @click="setScriptIcon(iconUp.styled)"
              >Inked</span
            >
            <span
              :class="{ on: builderLogo === iconUp.original }"
              @click="setScriptIcon(iconUp.original)"
              >Original</span
            >
          </div>
          <div
            class="button"
            v-if="builderLogo"
            title="Back to the stock custom-script mark"
            @click="setScriptIcon('')"
          >
            <font-awesome-icon icon="undo" /> Stock mark
          </div>
        </div>
        <div class="ns-browse">
          <input
            v-model="nsIconSearch"
            class="ns-search"
            placeholder="…or search the icon library"
          />
          <template v-if="ilLoaded">
            <div class="icon-grid ns-grid">
              <div
                class="icon-cell"
                v-for="e in nsIconMatches"
                :key="'ns-' + e.n"
                :title="e.n.replace(/-/g, ' ')"
                @click="pickScriptLibraryIcon(e)"
              >
                <!-- FT-1537: loading before src, or the thumbs load eagerly -->
                <img class="il-thumb" loading="lazy" :src="ilThumb(e)" alt="" />
                <span class="label">{{ e.n.replace(/-/g, " ") }}</span>
              </div>
            </div>
            <div class="feed-none" v-if="!nsIconMatches.length">
              Nothing in the library matches “{{ nsIconSearch.trim() }}”.
            </div>
          </template>
          <div class="il-loading" v-else>Loading the library…</div>
        </div>
        <div class="fk-acts">
          <div class="button" @click="iconPickOpen = false">
            <font-awesome-icon icon="times" /> Close
          </div>
        </div>
      </div>

      <!-- NAME YOUR COPY. Saving a script this browser holds no edit key for
           makes a FORK, and a fork used to inherit the original's name in
           silence — which is how a shelf ends up with three scripts wearing
           the same name and no way to tell them apart. The save stops here
           instead and asks.

           An INLINE panel, never prompt(): a browser dialog is silently
           auto-dismissed in dialog-less contexts (driven panes, embeds) —
           Menu.leaveSession records that exact bug — and a dismissed prompt
           returned empty, so Save simply did nothing and looked broken.

           Cancel abandons the SAVE, not the work: the edits stay on the
           bench, still unsaved, and Save can be pressed again. -->
      <div class="role-form fork-form" v-if="forkForm">
        <h3>{{ forkForm.forking ? "Name your copy" : "Name this script" }}</h3>
        <p class="fk-note" v-if="forkForm.forking">
          <strong>{{ forkForm.original }}</strong> is not yours to change —
          saving makes your own copy of it, and the copy needs a name of its
          own. The original is left exactly as it is.
        </p>
        <label>Name</label>
        <input
          ref="forkName"
          class="fk-name"
          v-model="forkForm.name"
          maxlength="60"
          @keyup.enter="confirmFork"
        />
        <div class="role-error" v-if="forkError">
          {{ forkError }}
          <span class="fk-fix" v-if="forkSuggestion" @click="useForkSuggestion"
            >use “{{ forkSuggestion }}”</span
          >
        </div>
        <div class="fk-acts">
          <div class="button" @click="cancelFork">
            <font-awesome-icon icon="times" /> Cancel
          </div>
          <!-- FT-1043: dark until the copy's name DIFFERS from the source's —
               the field opens on the original, selected, so one keystroke
               replaces it -->
          <div
            class="button fk-go"
            :class="{ disabled: forkGoDisabled }"
            @click="confirmFork"
          >
            <font-awesome-icon :icon="forkForm.forking ? 'copy' : 'check'" />
            {{ forkForm.forking ? "Fork" : "Save" }}
          </div>
        </div>
      </div>

      <!-- ASK FOR A LINK HERE, never prompt(). Same reason the fork panel
           above gives, and the same shape: a browser dialog is silently
           auto-dismissed in dialog-less contexts, so the caller read an empty
           string and did nothing at all.
           Its one caller (promptVaultLoad) has no door in the workbench
           today — the vault shelf replaced it — so this panel adds no
           control that was not already there. -->
      <div class="role-form fork-form" v-if="askForm">
        <h3>{{ askForm.title }}</h3>
        <label>{{ askForm.label }}</label>
        <input
          ref="askInput"
          class="fk-name"
          v-model="askForm.value"
          :placeholder="askForm.placeholder"
          spellcheck="false"
          @keyup.enter="confirmAsk"
          @keyup.esc="cancelAsk"
        />
        <div class="role-error" v-if="askError">{{ askError }}</div>
        <div class="fk-acts">
          <div class="button" @click="cancelAsk">
            <font-awesome-icon icon="times" /> Cancel
          </div>
          <div class="button fk-go" @click="confirmAsk">
            <font-awesome-icon icon="check" /> {{ askForm.okText }}
          </div>
        </div>
      </div>

      <!-- DELETE FOR EVERYONE — the confirm (FT-970). Same inline-panel shape
           as the two above and for the same reason: a native confirm() comes
           back false unseen in a driven pane or an embed, and a destructive
           control that silently does nothing is worse than no control.

           This one wears BLOOD where the fork panel wears purple. The fork
           panel is constructive — it makes a thing. This ends one, for people
           who are not in the room. The note names both halves of the cost:
           the link dies for everyone, and the copy on this browser goes too.

           Cancel abandons the delete, not the work; the script stays loaded
           and unchanged either way. -->
      <!-- FT-1501 (Clocktower's FT-1475 round 2): THE UNSAVED-CHANGES ASK —
           closing the Lot editor with edits not yet saved asks first, in the
           workbench's own confirm panel (the delete-for-everyone panel's
           dress), never the browser's window.confirm. -->
      <div
        class="role-form fork-form destroy-form discard-form"
        v-if="discardForm"
        role="alertdialog"
        aria-label="Unsaved changes"
      >
        <h3>Discard your changes to “{{ discardForm.name }}”?</h3>
        <p class="fk-note">
          You have edits that aren't saved. Closing the editor now throws them
          away.
        </p>
        <div class="fk-acts">
          <div
            class="button discard-keep"
            role="button"
            tabindex="0"
            @click="discardForm = null"
            @keydown.enter.prevent="discardForm = null"
            @keydown.space.prevent="discardForm = null"
          >
            <font-awesome-icon icon="times" /> Keep editing
          </div>
          <div
            class="button fk-go destroy-go discard-go"
            role="button"
            tabindex="0"
            @click="confirmDiscard"
            @keydown.enter.prevent="confirmDiscard"
            @keydown.space.prevent="confirmDiscard"
          >
            <font-awesome-icon icon="exclamation-triangle" />
            Discard changes
          </div>
        </div>
      </div>
      <div class="role-form fork-form destroy-form" v-if="destroyForm">
        <h3>Delete “{{ destroyForm.name }}” for everyone?</h3>
        <p class="fk-note">
          This removes the script from the server. The share link stops working
          for everyone you sent it to, and it leaves this browser as well. It
          cannot be undone. Copies anyone forked from it keep working — they
          just stop crediting this one as their source.
        </p>
        <div class="role-error" v-if="destroyError">{{ destroyError }}</div>
        <div class="fk-acts">
          <div class="button" @click="cancelDestroy">
            <font-awesome-icon icon="times" /> Cancel
          </div>
          <div class="button fk-go destroy-go" @click="confirmDestroy">
            <font-awesome-icon icon="exclamation-triangle" />
            Delete for everyone
          </div>
        </div>
      </div>

      <!-- the shelf's hover card: icon + bold name + ability (the almanac
           read), replacing the native title tooltip. FT-858: it IS
           RoleHoverCard now — the same component the seats and the grimoire
           drawer hover (user-directed: one component, every surface). The
           shelf hands it the tag-filter chips, since library entries carry
           facts the role object alone cannot prove. -->
      <RoleHoverCard
        v-if="roleTip"
        :role="roleTip"
        :chips="roleTipChips"
        :anchor="roleTipAnchor"
        fallback-ability="A library role — its ability arrives when added."
        @dismiss="hideRoleTip"
      />

      <div class="role-error wb-error" v-if="roleError">{{ roleError }}</div>

      <!-- the script-icon image intake (icon overlay's Upload button) -->
      <input type="file" ref="nsUpload" accept="image/*" @change="onNsUpload" />
    </div>
  </Modal>
</template>

<script>
import editionJSON from "../../editions.json";
import rolesJSON from "../../roles.json";
import { mapMutations, mapState } from "vuex";
import Modal from "./Modal.vue";
// FT-1501: the art overlay's close — the one close mark every surface wears
import CloseX from "../CloseX.vue";
import * as vault from "../../golem/scripts";
import * as roleLib from "../../golem/roles";
// FT-1485: which library rows ARE official Lots (listed once, as the official)
import * as houseRoles from "../../golem/houseRoles";
import * as towns from "../../golem/towns";
import { flashHint } from "../../golem/hint";
// FT-1117: a role's reminder entry is a plain string OR `{ name, deal }` (a
// character declaring the deal should place the token itself). `reminderName`
// / `reminderNames` / `reminderDeal` are THE readers for both shapes — every
// place the Forge touches a role's reminders goes through one of them, so
// there is never a second interpretation of the data to keep in step.
// FT-1120 makes the Forge AUTHOR the object form; see THE FORGE'S REMINDER
// ENTRY below.
import {
  reminderNames,
  reminderName,
  reminderDeal,
} from "../../golem/dealReminders";
// FT-1188: STOOD DOWN, not removed (house rule) — it still rides `data` and
// still feeds `almanacCap`, but nothing renders either any more. The blood "A"
// was the drop-cap of this surface's first name, the Almanac; the bench is
// "Scripts" now and its cap comes from KeyCap like every other cap in the app.
import bloodA from "../../assets/blood/blood-A.png";
// FT-1188: THE app's one drop-cap component — the same one the entry doors,
// the grimoire drawer and the Chronicle print their title letters through.
import KeyCap from "../KeyCap";
// FT-854: THE shared script picker + its art — the same component the host
// panel renders (user-directed: one component, both surfaces).
import ScriptPicker from "../ScriptPicker";
// FT-858: THE role hover card — the shelf's card, now shared with the seats
// and the grimoire drawer (user-directed: one component, every surface).
import RoleHoverCard from "../RoleHoverCard";
// FT-1491: the sections' "Only with…" picker — the night sheet's own
// character picker — and the sections' shape, caps and tidy-up. (FT-1494:
// the title and text caps moved onto the card's own inputs. FT-1498: onto
// the Studio's own panel under the card instead.)
import CharacterPicker from "../CharacterPicker";
import {
  SECTION_TITLE_PICKS,
  SECTIONS_MAX,
  SECTION_TITLE_MAX,
  SECTION_TEXT_MAX,
  cleanSections,
  sectionsOf,
  sectionsProblem,
} from "../../golem/roleSections";
// FT-857: THE shared script view — the workbench's main pane IS the player
// drawer's body (user-directed: one component, both surfaces).
import ScriptView from "../ScriptView";
// FT-857: the composition maths moved beside it, so the meter counts the same
// way wherever it renders.
import { countTeams, servableFor, servableText } from "../../golem/composition";
import {
  EDITION_ICONS,
  edCustom,
  OFFICIAL_BLURBS,
} from "../../golem/editionArt";
// FT-856: uploads take the official engraving look (ink/tint/parchment).
import { stylizeIcon } from "../../golem/iconStyle";
import * as iconLib from "../../golem/iconLibrary";
// The all-of-BOTC card wears the creative director's gold logo.
import goldLogo from "../../assets/gold/botc-logo-icon.png";
// FT-1242: the deal-rule chip wears the app's own dealing hand — the same
// art the tray's Deal button wears (RoleActions.vue).
import uiDeal from "../../assets/ui-deal.png";
// The user's demon mask + outsider face (design/red/*, cut + baked).
import demonGlyph from "../../assets/blood/demon-glyph.png";
import outsiderGlyph from "../../assets/blood/outsider-glyph.png";
// One definition of "the glyph for team X" (golem/glyphs), shared with
// TownInfo, ScriptView and RoleDrawer.
import { teamGlyph } from "../../golem/glyphs";
// FT-1039: the source chips wear the edition marks already imported above
// (EDITION_ICONS), and the night lens wears the night tabs' own moon phases.
import moonFirst from "../../assets/moon-first.png";
import moonOther from "../../assets/moon-other.png";
// FT-887: the shelf's night filter labels read "Wakes first night" — the same
// claim the hover card's chip makes, so it comes from the same function.
// FT-1040: plus the forge's night action composer — the six-shape palette and
// the compose/decompose pair that turn its dials into a schema entry riding
// the forged role (role.golemNight) and back.
// FT-1410: ...and the two labels that composed row wears on the storyteller's
// night sheet, whose shipped words are derived from the very entry the dials
// compose (splitLabelsForEntry) — so the forge's two boxes show the real
// default as their placeholder without restating a single string.
// FT-1538 (Clocktower's FT-1475 part 2 / round 2, FT-1514, FT-1529): the
// composer became the STEP BUILDER — the step vocabulary and its nouns, the
// six templates, the steps ↔ entry maps, and the checklist row the steps
// will produce (NightRowPreview).
import NightRowPreview from "../NightRowPreview.vue";
import {
  wakesOn,
  FIELD_OWNERS,
  FIELD_TYPES,
  NIGHT_STEP_TEMPLATES,
  NIGHT_STEPS_MAX,
  STEP_NOUNS,
  cleanStep,
  entryFromSteps,
  nounAllowed,
  nounForStep,
  splitLabelsForEntry,
  stepCountable,
  stepWithNoun,
  stepsFromEntry,
  // FT-1501: the steps on the bench stand in for the Lot's saved night while
  // the preview runs (the Studio's draft, Clocktower's FT-1475)
  setDraftNight,
  clearDraftNight,
} from "../../golem/nightInfo";
// FT-1410 → FT-1486: the labels themselves are COPY, and they live on the
// Lot (night.copy), washed through the copy layer's one sanitizer.
// FT-1501: …and the preview's live draft of them (setDraftCopy), every key.
import {
  COPY_MAX,
  COPY_KEYS,
  clearDraftCopy,
  sanitizeCopyEntry,
  setDraftCopy,
} from "../../golem/nightSigns";
// FT-1501 (Clocktower's FT-1475): THE LOT EDITOR's preview IS the Night lab —
// its store takeover (enter/exit), its stepper and viewpoints, its shipped
// defaults. Borrowed whole; nothing here re-renders a night.
import {
  nightLab,
  enter as enterLab,
  exit as exitLab,
  grammarOf,
  applyState as applyLabState,
  setPerspective as setLabPerspective,
  replay as replayLab,
  patchActorRole,
  copyDefaultFor as labCopyDefault,
  // where THIS Lot's night text shows on the table, per view and state —
  // the stepper's dots
  copyKeysShown,
} from "../../golem/nightLab";
// FT-1501: the left drawer resizes by the right-hand drawers' own drag
import { dragWidth, storedWidth } from "../../golem/rightDrawer";
// FT-1040: the players shape's 1–3 count wears the night sheet's own scrub.
import NumberScrub from "../NumberScrub";
// FT-1120: the deal popover's two pickers. The panel's OWN dropdown — the one
// every multi-option setting in the app wears (OptionSelect.vue) — rather than
// a fourth recipe for "pick one of these" inside the Forge.
import OptionSelect from "../OptionSelect";
// FT-1040c: the forge's LIVE COIN — the real Token component renders the
// draft role exactly as the grimoire will. Reused, never redrawn.
import Token from "../Token";
// FT-1546: THE tarot card, for the card art's preview, and the art's own
// reader + upload (the platform asset store)
import LotCard from "../LotCard";
import {
  CARD_ART_LOGIN_MESSAGE,
  cardArtDisplay,
  cardArtOf,
  uploadCardArt,
} from "../../golem/cardArt";
// The app-wide PNG-font choice — the Almanac's A wears the caps' font.
import {
  fontState,
  glyphFrom,
  glyphStyleFrom,
  resolvedCapKey,
  CAP_SHRINK,
} from "../../golem/titleFonts";
// Golem fork (FT-854): the official setup table — players: [townsfolk,
// outsiders, minions, demons]. The meter measures a script's POOL against it:
// a count is servable when the pool covers each column. Purely informational.
// FT-857: the table + its maths live in golem/composition.js now, shared with
// the player-facing drawer's copy of the same meter.
const TEAM_ORDER = ["townsfolk", "outsider", "minion", "demon", "traveler"];
// FT-854: the shelf's tri-state tag filter. Every tag cycles
// neutral → include → exclude. Includes OR within a group and AND across
// groups; an exclude always wins. Library roles carry no night/setup data,
// so they only match tags we can actually prove.
const LUF_ROLES = new Set(
  (editionJSON.find((e) => e.id === "luf") || { roles: [] }).roles,
);
const TAG_GROUPS = [
  {
    key: "team",
    label: "Team",
    tags: [
      { id: "team:townsfolk", label: "Townsfolk" },
      { id: "team:outsider", label: "Outsiders" },
      { id: "team:minion", label: "Minions" },
      { id: "team:demon", label: "Demons" },
    ],
  },
  {
    key: "source",
    label: "Source",
    tags: [
      { id: "src:tb", label: "Trouble Brewing" },
      { id: "src:bmr", label: "Bad Moon Rising" },
      { id: "src:snv", label: "Sects & Violets" },
      { id: "src:luf", label: "Laissez un Faire" },
      { id: "src:exp", label: "Experimental" },
      { id: "src:mine", label: "Your library" },
      { id: "src:lib", label: "Community" },
    ],
  },
  {
    key: "night",
    label: "Night",
    tags: [
      { id: "night:first", label: "Wakes first night" },
      { id: "night:other", label: "Wakes other nights" },
      { id: "night:never", label: "Never wakes" },
    ],
  },
  {
    key: "flags",
    label: "Special",
    tags: [
      { id: "flag:setup", label: "Affects setup" },
      { id: "flag:inscript", label: "In this script" },
    ],
  },
];
// FT-1039: what a chip WEARS — a 270px rail cannot spell "Trouble Brewing"
// seven times, so editions wear their marks (EDITION_ICONS above), the rest
// wear one short word, and every chip's full label rides its tooltip.
const CHIP_WORDS = {
  "src:exp": "Exp",
  "src:mine": "Yours",
  "src:lib": "Shared",
  "night:first": "First",
  "night:other": "Other",
  "night:never": "Never",
  "flag:setup": "Setup",
  "flag:inscript": "In script",
};

// ── FT-1120: THE FORGE'S REMINDER ENTRY ─────────────────────────────────────
//
// FT-1117 let a character DECLARE that the deal should place one of its
// reminder tokens — `{ name, deal: { seats, eligible, self } }` in place of the
// bare `"Red herring"`. The Fortune Teller says it in roles.json and the deal
// obeys. The Forge could not SAY it, and worse, could not carry it: its pill
// list was a comma-joined STRING of names, and a string cannot hold a rule.
// Three paths destroyed one: the form's save (split the string back apart), the
// JSON import, and forking an existing role — which is why forking the Fortune
// Teller gave you a copy that kept the token and lost the herring.
//
// THE PILL LIST IS THE STORAGE SHAPE NOW. `roleForm.reminders` is an array of
// `{ name, deal }` — `deal` null for the plain-string majority — and the
// comma-joined string is no longer a store at all. Where a joined string is
// still wanted (the shelf's search index) it is RENDERED from the entries with
// `reminderNames`; nothing parses one back into structure.
//
// A PILL WITH NO RULE SERIALISES AS A BARE STRING, byte for byte what the Forge
// has always written. An author who never opens the popover authors exactly
// what every existing role authors, so there is no migration and no new shape
// in the common case.
//
// ONLY `reminders` CARRIES RULES, never `remindersGlobal` — dealReminders.js's
// deliberate FT-1117 call (a global reminder is offered whether or not its role
// is in play, so `self`, "the role's own seat", has no referent). The Forge has
// no remindersGlobal field at all, so the control cannot appear there; this
// note is here so that stays a decision rather than an accident if one is ever
// added.

/** The rule's own defaults, matching dealReminders.js's reader exactly. */
const DEAL_DEFAULTS = { seats: 1, eligible: "any", self: "allow" };
/** Who may receive it — the author's words for `deal.eligible`. */
const DEAL_ELIGIBLE = [
  { value: "any", label: "Anyone" },
  { value: "good", label: "Good players" },
  { value: "evil", label: "Evil players" },
  { value: "townsfolk", label: "Townsfolk" },
  { value: "outsider", label: "Outsiders" },
  { value: "minion", label: "Minions" },
  { value: "demon", label: "Demons" },
];
/** Whether the role's own chair is in the draw — the author's words for
 *  `deal.self`. "Only them" is how a marker that belongs on its owner's own
 *  seat is written. */
const DEAL_SELF = [
  { value: "allow", label: "Can receive it" },
  { value: "exclude", label: "Never" },
  { value: "only", label: "Only them" },
];
/** The most seats one rule may claim — the town's own upper bound, so the
 *  number box cannot author a rule the deal could never satisfy. */
const DEAL_SEATS_MAX = 20;

/** An authored rule, bounded and defaulted. Anything unrecognised falls back
 *  to the default rather than travelling on into the role's data. */
const normalizeDeal = (deal) => {
  const d = deal || {};
  const seats = Math.floor(Number(d.seats));
  const eligible = DEAL_ELIGIBLE.some((o) => o.value === d.eligible)
    ? d.eligible
    : DEAL_DEFAULTS.eligible;
  const self = DEAL_SELF.some((o) => o.value === d.self)
    ? d.self
    : DEAL_DEFAULTS.self;
  return {
    seats: Number.isFinite(seats)
      ? Math.min(DEAL_SEATS_MAX, Math.max(1, seats))
      : DEAL_DEFAULTS.seats,
    eligible,
    self,
  };
};

/** A stored reminder entry (either shape) → the Forge's editable pill. */
const toReminderPill = (entry) => {
  const deal = reminderDeal(entry);
  return { name: reminderName(entry), deal: deal ? normalizeDeal(deal) : null };
};

/** A whole `role.reminders` array → pills. Tolerates a non-array (an imported
 *  role may spell it anything at all). */
const toReminderPills = (list) =>
  Array.isArray(list) ? list.map(toReminderPill) : [];

/** A pill → what the role actually stores: a BARE STRING unless it carries a
 *  rule. This is the line that keeps the ruleless majority byte-identical. */
const fromReminderPill = (pill) => {
  if (!pill) return "";
  return pill.deal
    ? { name: pill.name, deal: { ...pill.deal } }
    : pill.name || "";
};

// ── FT-1501: THE LOT EDITOR'S PREVIEW (Clocktower's Role Studio) ───────────
/** The editor drawer's width — its storage key, default and clamps. */
const EDITOR_W_KEY = "golem.roleEditorW";
const EDITOR_W_DEFAULT = 520;
const EDITOR_W_MIN = 420;
const EDITOR_W_MAX = 900;
/** The viewpoint switch — the lab's three perspectives, in the editor's
 *  words (Spectator is the lab's bystander). */
const STUDIO_VIEWS = [
  { id: "acting", label: "Player" },
  { id: "storyteller", label: "Reader" },
  { id: "bystander", label: "Spectator" },
];
/** The stepper's beats in the user's words (invitation → picked →
 *  confirmed → sent → told); the ids are the lab's grammar. */
const STUDIO_STATE_LABELS = {
  invite: "Invitation",
  staged: "Picked",
  sealed: "Confirmed",
  "st-sent": "Sent",
  reopened: "Reopened",
  before: "Asleep",
  telling: "Told",
  settled: "Settled",
  residue: "Next day",
};
/** Every copy key, on its default — the form's reactive shape for the Lot's
 *  words. FT-1536: null is "use the default"; "" is a line LEFT EMPTY on
 *  purpose (only the copy layer's BLANKABLE_KEYS ever hold one —
 *  NightCopyText decides). */
const blankWords = () =>
  COPY_KEYS.reduce((out, key) => {
    out[key] = null;
    return out;
  }, {});

const TEAM_LABELS = {
  townsfolk: "Townsfolk",
  outsider: "Outsiders",
  minion: "Minions",
  demon: "Demons",
  traveler: "Travellers",
};
// The officials' editions, as the feed's sticky group headers. Anything
// without one of the three marked editions (carousel included) is
// Experimental — the same reading the sidebar's src:exp chip makes.
const FEED_EDITIONS = [
  ["tb", "Trouble Brewing"],
  ["bmr", "Bad Moon Rising"],
  ["snv", "Sects & Violets"],
  ["exp", "Experimental"],
];
// roles.json spells it "traveler"; the server's roleType vocabulary spells it
// "traveller". Normalize to the app side everywhere the two meet.
const normTeam = (t) => (t || "").replace("traveller", "traveler");

// FT-1042: the art's FIT — one sanitizer pair for every door the fields come
// through (forge open, JSON import, save). Scale is a multiplier clamped to
// 0.4–2; offsets are percent of the coin's width clamped to ±50 (the coin's
// own radius), so a bad import can never fling the art off-coin. Token.vue
// clamps identically at render — belt and braces, one vocabulary.
const sanArtScale = (v) => {
  const n = Number(v);
  return isFinite(n) && n ? Math.min(2, Math.max(0.4, n)) : 1;
};
const sanArtOffset = (v) => {
  const n = Number(v);
  return isFinite(n) ? Math.min(50, Math.max(-50, Math.round(n * 10) / 10)) : 0;
};
// FT-1501: how far a press on the coin may travel and still be a CLICK (the
// art overlay's door) rather than a drag (placing the art)
const ART_CLICK_SLOP = 4;

/**
 * FT-981: ONE spelling of a searchable string, for the query and for the text
 * it is matched against.
 *
 * The bench used to compare the raw query to the raw name with `includes`, and
 * that failed on the two things every real character name carries: an
 * apostrophe and a hyphen. Measured on the shipped build — `devils advocate`
 * returned NOTHING while `devil's advocate` returned the Devil's Advocate, and
 * `pit hag` returned nothing while `pit-hag` returned the Pit-Hag. Nobody types
 * the punctuation, so the two best-known Minions in the game were unreachable
 * by the search box.
 *
 * THE APOSTROPHE AND THE HYPHEN FOLD DIFFERENTLY, and the difference is the
 * whole point. The first pass folded both to a space and the probe caught it
 * STILL failing on the name it was written to fix:
 *
 *   - An APOSTROPHE drops to NOTHING, so `Devil's` becomes `devils` — which
 *     is what a person types. Folding it to a space yields the tokens `devil`
 *     and `s`, and the query `devils` matches neither.
 *   - EVERY OTHER separator folds to a SPACE, so `Pit-Hag` becomes `pit` plus
 *     `hag`, reachable from `pit hag`, `pit-hag` or `hag pit`.
 *
 * Diacritics fold too — the library is author-supplied and will carry them.
 * The curly apostrophe is listed beside the straight one because that is the
 * one roles.json actually uses.
 */
const normalizeSearch = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/['\u2019]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

/**
 * WHEN THE WORKBENCH STEPS ASIDE.
 *
 * Measured against the real layout, not guessed (sweep, 2026-08-18): the body
 * is a fixed 270px role rail plus the script column, and the column measures
 * 334px at a 700px window, 298 at 640, 147 at 480 and 85 at 414 — a role card
 * stops being a card somewhere just under 700, and by 480 the ability text is
 * three words to a line. Below that the tabs run off the modal's right edge.
 *
 * The second clause is the LANDSCAPE PHONE: 812x375 has all the width and none
 * of the height, and the gesture the bench is built around — dragging a role
 * up the night order — is HTML5 drag-and-drop, which does not fire on a touch
 * screen at all. `pointer: coarse` keeps a short desktop window out of it.
 */
const SMALL_BENCH =
  "(max-width: 699px), (pointer: coarse) and (max-height: 519px)";

export default {
  components: {
    CharacterPicker,
    CloseX,
    KeyCap,
    LotCard,
    Modal,
    NightRowPreview,
    NumberScrub,
    OptionSelect,
    RoleHoverCard,
    ScriptPicker,
    ScriptView,
    Token,
  },
  data: function () {
    return {
      // the official editions, for the picker's cards
      editions: editionJSON,
      // FT-1242: the deal-rule chip's mark — the app's own dealing hand.
      uiDeal,
      // the workbench is the modal's only page now; the flag stays because
      // ensureOpen (and old muscle memory in methods) still sets it
      isCustom: true,
      bloodA,
      demonGlyph,
      outsiderGlyph,
      fontStateRef: fontState,
      // Is the window too small to hold the bench? (see SMALL_BENCH). Live —
      // a rotation or a desktop resize flips it either way.
      smallScreen: false,
      // Golem fork: the vault shelf + which vault script is currently loaded
      // (the fork/update decision key on save).
      recents: vault.getRecents(),
      // Golem fork (FT-851): the custom-role library state — the form (null
      // when closed), the library id being edited (fork/update decision), the
      // icon-picker filter, browse query/filter/results, the roles shelf.
      roleForm: null,
      editingLibId: null,
      // FT-1484: the library Lot on the bench is THIS account's — Save takes
      // the owner path, in place, no key. `libOwnedCheck` is the pending ask;
      // Save waits on it.
      editingLibOwned: false,
      libOwnedCheck: null,
      // FT-1486: the form was opened from the Lot LIST (its pen or Make a
      // copy) — Save leaves a Lot this script doesn't use out of it.
      // `copyingEntry` is the list entry whose copy is being made.
      formFromList: false,
      copyingEntry: "",
      roleQuery: "",
      roleTypeFilter: "",
      roleResults: [],
      roleShelf: roleLib.getRecents(),
      // FT-1484 step 2: the signed-in account's own library Lots, public and
      // private — "Yours" beside this browser's shelf, from any device.
      myRoles: [],
      roleError: "",
      // FT-1546: the card-art upload's state — busy while a picture is on its
      // way to the asset store; the last failure, in the upload's own words
      cardArtBusy: false,
      cardArtError: "",
      cardArtLoginMessage: CARD_ART_LOGIN_MESSAGE,
      // the card preview's width (px) — a card beside its controls in the
      // column; its type is scaled to match (see cardArtCardStyle)
      cardArtWidth: 240,
      // FT-1491: a section's quick-pick titles (FT-1494: offered on the
      // card while that section is open)
      sectionTitlePicks: SECTION_TITLE_PICKS,
      sectionsMax: SECTIONS_MAX,
      sectionTitleMax: SECTION_TITLE_MAX,
      sectionTextMax: SECTION_TEXT_MAX,
      // FT-1494: the author chip under the card is being typed into
      authorEditing: false,
      // FT-1498: which section (index into roleForm.guideSections) the
      // panel under the chip row is editing. -1 for none.
      activeSectionIndex: -1,
      // FT-1040: the forge's chips and composer — the wakes chips wear the
      // night tabs' moons; the palette is the schema's own six shapes; the
      // reminder pill being typed lives outside roleForm (it is not a token
      // until Enter mints it).
      moonFirstArt: moonFirst,
      moonOtherArt: moonOther,
      // FT-1538 (Clocktower's FT-1475): the step builder's fixed lists — the
      // ceiling, the count dropdown, and the six templates as one dropdown
      // (its first row is the prompt)
      stepsMax: NIGHT_STEPS_MAX,
      stepCountOptions: [1, 2, 3].map((n) => ({
        value: n,
        label: String(n),
      })),
      templateOptions: [{ value: "", label: "Start from a template…" }].concat(
        NIGHT_STEP_TEMPLATES.map((t) => ({
          value: t.id,
          label: t.label,
          title: t.hint + " — replaces the list",
        })),
      ),
      // which step's Preview last drove the table
      previewStepIdx: -1,
      // the Night panel's Reader / Player switch when no preview runs (with
      // one running, the lab's own perspective is the answer)
      localNightView: "acting",
      // FT-1410: the two label boxes take the copy layer's own cap, not a
      // second number invented here
      copyMax: COPY_MAX,
      reminderDraft: "",
      // FT-1120: which reminder pill's deal rule is open, -1 for none, and
      // where its tail points (pixels from the pill row's left edge).
      dealPop: -1,
      dealTailX: 0,
      // FT-1042: is the pointer currently placing the art on the coin?
      artDragging: false,
      // FT-981: the last library browse failed. Not an error banner (see
      // searchRoles) — just enough for the empty state to say why the
      // haystack is smaller than the author expects.
      browseDown: false,
      // Golem fork (FT-854): the workbench — active view, sidebar team filter,
      // and the non-conforming marks by script id
      // (derived from the setup table; informational only, never a gate).
      wbView: "team",
      wbTeam: "",
      // the shelf's hover card — the entry it describes, and the row it is
      // pinned to (RoleHoverCard measures and places itself)
      roleTip: null,
      roleTipAnchor: null,
      // FT-857: the night-order drag state + the by-type fold state moved
      // into ScriptView with the markup they drive.
      // FT-855 polish: the bespoke demon glyph — horns, head, hollow eyes
      // (no FA solid reads "demon"; drawn to match the solid-icon weight).
      DEMON_PATH:
        "M178 112 C 210 98 302 98 334 112 C 338 148 346 164 368 176 L 352 200 C 342 220 338 232 336 252 C 328 310 298 348 256 368 C 214 348 184 310 176 252 C 174 232 170 220 160 200 L 144 176 C 166 164 174 148 178 112 Z " +
        "M182 114 C 170 64 198 30 244 24 C 232 54 230 80 236 102 C 216 104 198 108 182 114 Z " +
        "M330 114 C 342 64 314 30 268 24 C 280 54 282 80 276 102 C 296 104 314 108 330 114 Z " +
        "M186 200 C 202 176 240 182 254 204 C 236 220 202 216 186 200 Z " +
        "M326 200 C 310 176 272 182 258 204 C 276 220 310 216 326 200 Z " +
        "M162 366 C 172 442 258 482 348 458 C 380 448 402 432 414 410 L 388 396 C 376 414 352 428 322 434 C 254 446 192 416 180 362 Z " +
        "M396 366 L 448 362 L 428 418 Z",
      // dirty tracking: the last loaded/saved state, serialized
      scriptBaseline: null,
      // FT-1043: the New-script modal retired — the pane IS the builder.
      // What survives of it: the official-art browser, floating as the icon
      // overlay (open/closed), its search, and the last upload's inked/
      // original pair (the FT-856 toggle needs both at hand).
      iconPickOpen: false,
      nsIconSearch: "",
      iconUp: { styled: "", original: "" },
      // is a drag hovering the header icon? (it lights up)
      iconDragOver: false,
      // the stock mark a custom script wears until an upload/pick replaces it
      edCustomArt: edCustom,
      // the "name your copy" panel — non-null while a save is waiting on a
      // name. { forking, original, name }
      forkForm: null,
      forkError: "",
      // a free name offered next to a clash, one click to take it
      forkSuggestion: "",
      // the workbench's own inline ask — { title, label, placeholder, value,
      // okText, onOk }; null when nothing is being asked.
      askForm: null,
      askError: "",
      // FT-970: the delete-for-everyone confirm — { id, name }, null when
      // nothing is being asked. Separate from askForm on purpose: that one is
      // an input panel with a purple go-button, this one is a blood-red
      // question with no field to fill in.
      destroyForm: null,
      destroyError: "",
      // FT-856 slice B: the icon tabs — official borrow vs the new-icon
      // library (game-icons.net curation, lazy chunk).
      iconTab: "library",
      ilSearch: "",
      ilLoaded: false,
      // FT-855: tri-state team toggles (1 show-only, -1 hide, absent off)
      // + structured filter pills [{ id: 'src:tb', not: false }].
      teamState: {},
      pills: [],
      ncMap: JSON.parse(localStorage.getItem("golem.scriptNC") || "{}"),
      officials: [
        ["trouble-brewing", "Trouble Brewing"],
        ["bad-moon-rising", "Bad Moon Rising"],
        ["sects-and-violets", "Sects & Violets"],
      ],
      vaultSourceId: null,
      // FT-1501: THE LOT EDITOR'S PREVIEW — the Night lab's own observable
      // (read, never written, so the live flag and the stepper repaint), the
      // viewpoints, the drawer's width, the unsaved-changes ask and the
      // snapshot it measures from, the art overlay, a preview stopped from
      // the menu's Exit preview, and the ownership ask still out.
      lab: nightLab,
      studioViews: STUDIO_VIEWS,
      editorWidth: storedWidth(
        EDITOR_W_KEY,
        EDITOR_W_DEFAULT,
        EDITOR_W_MIN,
        EDITOR_W_MAX,
      ),
      editorWMin: EDITOR_W_MIN,
      editorWMax: EDITOR_W_MAX,
      discardForm: null,
      formBaseline: "",
      artOpen: false,
      previewStopped: false,
      ownerPending: false,
    };
  },
  computed: {
    ...mapState(["modals"]),
    /** FT-1484 step 2: the signed-in account's id (null signed out) — the
     *  library re-asks for the account's own Lots when it changes. */
    accountId() {
      const a = this.$store.state.session.account;
      return a ? a.id : null;
    },
    /** FT-1484 step 2: an official Lot is always public — the Public /
     *  Private toggle is fixed for it (the server forces it too). */
    visibilityFixed() {
      return !!(this.roleForm && this.roleForm.officialRow);
    },
    /**
     * FT-1410: the two labels the composed row would wear if the author
     * typed nothing — the placeholders of the pair of boxes under the prompt.
     * Composed from the live dials and handed straight to the schema's own
     * derivation, so the forge shows the same word the sheet will.
     */
    composedLabels() {
      const f = this.roleForm;
      if (!f) return { seats: "", told: "" };
      // FT-1538: composed from the step list now
      return splitLabelsForEntry(entryFromSteps(f.nightSteps || []));
    },
    /** FT-1538 (Clocktower's FT-1475 round 2): whose eyes the Night panel
     *  shows — the running preview's own perspective, else the panel's
     *  switch. */
    nightView() {
      return this.studioLive ? this.lab.perspective : this.localNightView;
    },
    /**
     * FT-1538 (Clocktower's FT-1475 round 2): THE CHECKLIST ROW THIS LOT
     * WILL PRODUCE — the props NightRowPreview draws from, read the way the
     * night sheet reads a row: the steps' field list, its seat count, and
     * the instruction line in the copy layer's order (the Lot's own line,
     * the entry's line, the night-sheet reminder it carries).
     */
    rowPreview() {
      const f = this.roleForm;
      if (!f) return null;
      const entry = entryFromSteps(f.nightSteps);
      const fields =
        entry && Array.isArray(entry.fields)
          ? entry.fields
          : [{ type: FIELD_TYPES.TEXT, by: FIELD_OWNERS.STORYTELLER }];
      const first = !(f.otherNight > 0);
      const slots = Math.min(
        fields.filter((x) => x.type === FIELD_TYPES.PLAYER).length,
        3,
      );
      const own = entry && entry.line;
      const entryLine =
        typeof own === "string"
          ? own
          : (own && (first ? own.first : own.other)) || "";
      const authored =
        (first ? f.nightReminders.first : f.nightReminders.other) || "";
      return {
        name: f.name.trim() || "New Lot",
        team: f.roleType === "traveller" ? "traveler" : f.roleType,
        icon: f.iconData || (f.icon ? this.iconUrl(f.icon) : ""),
        line: (f.nightCopy.line || "").trim() || entryLine || authored.trim(),
        fields,
        slots,
        label: (entry && entry.label) || "",
        seatsLabel: (f.nightCopy.seats || "").trim(),
        toldLabel: (f.nightCopy.told || "").trim(),
      };
    },
    // Golem fork (FT-851): the current script's custom roles (editable rows).
    // state.roles is REPLACED wholesale on every setCustomRoles, so this
    // recomputes despite being a Map.
    editionCustomRoles() {
      const list = [];
      this.$store.state.roles.forEach((role) => {
        if (role.isCustom) list.push(role);
      });
      return list;
    },
    // ── FT-1041b: the ART FEED ───────────────────────────────────────────
    /** One live search across the whole source; the groups carry the feed's
     *  sticky headers — the library's seven themes, or the officials'
     *  editions. (The theme filter pills retired with the tabs.) */
    feedGroups() {
      const q = this.ilSearch.trim().toLowerCase();
      if (this.iconTab === "official") {
        const byKey = {};
        const groups = FEED_EDITIONS.map(([key, label]) => {
          return (byKey[key] = { key, label, items: [] });
        });
        rolesJSON.forEach((role) => {
          if (q && !role.name.toLowerCase().includes(q)) return;
          (byKey[role.edition] || byKey.exp).items.push(role);
        });
        return groups.filter((g) => g.items.length);
      }
      if (!this.ilLoaded) return [];
      const list = this.$options.ilList || [];
      return iconLib.THEMES.map((t) => ({
        key: t,
        label: t,
        items: list.filter((e) => e.t === t && (!q || e.n.includes(q))),
      })).filter((g) => g.items.length);
    },
    /** The officials are bundled; the library arrives as its own chunk. */
    feedReady() {
      return this.iconTab === "official" || this.ilLoaded;
    },
    /** FT-1042: the size scrub speaks whole percent; the form keeps the
     *  multiplier the role stores. */
    artSizePct() {
      const f = this.roleForm;
      return Math.round((f && f.artScale ? f.artScale : 1) * 100);
    },
    /**
     * FT-1126: the two offsets, read for the dials. Rounded to whole percent
     * for the readout because the drag writes tenths and a scrubbing dial
     * that reads "-12.3" is harder to aim than one that reads "-12"; the
     * stored value keeps whatever precision the drag gave it until a dial is
     * actually touched.
     */
    artOffX() {
      const f = this.roleForm;
      return Math.round((f && f.artX) || 0);
    },
    artOffY() {
      const f = this.roleForm;
      return Math.round((f && f.artY) || 0);
    },
    /** Does the draft wear a non-stock fit? (the Reset chip's gate) */
    artDirty() {
      const f = this.roleForm;
      return !!f && (f.artScale !== 1 || f.artX !== 0 || f.artY !== 0);
    },
    /** What the coin currently wears — the feed's corner swatch. */
    currentArtSrc() {
      const f = this.roleForm;
      if (!f) return "";
      return f.iconData || (f.icon ? this.iconUrl(f.icon) : "");
    },
    /** The hovered role's tags, as readable chip labels (team rides the
     *  card border already, so it stays off the chips). */
    roleTipChips() {
      return this.roleTip ? this.tipChipsFor(this.roleTip) : [];
    },
    /**
     * FT-1494: the Studio card's tag row — the list's own tags for THIS Lot
     * (tipChipsFor, the builder hover's one definition), with the draft's
     * wakes and setup standing in for the saved ones. A Lot the list doesn't
     * hold yet (a new one) reads as one of yours, not on the script.
     */
    studioCardChips() {
      const f = this.roleForm;
      if (!f) return [];
      const entry =
        (this.editingLibId &&
          this.allShelfEntries.find((e) => e.libId === this.editingLibId)) ||
        null;
      const draft = entry
        ? { ...entry }
        : { isLib: true, mine: true, inScript: false };
      delete draft.tags;
      draft.team = normTeam(f.roleType);
      draft.firstNight = f.firstNight;
      draft.otherNight = f.otherNight;
      draft.setup = !!f.setup;
      return this.tipChipsFor(draft);
    },
    /** Would the next save FORK? A script loaded from the vault that this
     *  browser holds no edit key for cannot be updated in place — saving it
     *  makes a copy, and a copy has to be named before it is made. */
    willFork() {
      return !!this.vaultSourceId && !vault.editKeyFor(this.vaultSourceId);
    },
    /** FT-970: can the loaded script be destroyed for everyone? Only with its
     *  edit key — without one the server refuses, so offering the button would
     *  be offering a guaranteed error. `recents` is in the expression so the
     *  button disappears the moment the key is forgotten. */
    canDestroyLoaded() {
      if (!this.vaultSourceId) return false;
      return this.recents.some(
        (e) => e.id === this.vaultSourceId && !!e.editKey,
      );
    },
    // ── FT-1501: THE ONE SHELL + THE LIVE PREVIEW ────────────────────────
    /** The editor is open as the LEFT drawer (every Lot, new or existing):
     *  the Scripts window docks and the table fills the room on the right. */
    studioDocked() {
      return !!(this.modals.edition && this.roleForm && !this.smallScreen);
    },
    /** Who may preview — whoever can edit the Lot on the bench (Clocktower's
     *  FT-1497 rule): a new Lot, a library Lot this account owns, or one
     *  this browser holds the edit key for. Someone else's Lot saves as a
     *  copy, so its bench shows the art alone. */
    studioAvailable() {
      if (!this.roleForm) return false;
      if (!this.editingLibId || this.editingLibOwned) return true;
      return !!roleLib.editKeyFor(this.editingLibId);
    },
    /** The Lot on the bench wakes — first night, others, or both. */
    formWakes() {
      const f = this.roleForm;
      return !!f && (f.firstNight > 0 || f.otherNight > 0);
    },
    /** A Lot that never wakes has no night to preview. A blank new Lot (no
     *  app id yet) is still being drafted, so it previews regardless. */
    neverWakes() {
      const f = this.roleForm;
      return !!f && !!f.appId && !this.formWakes;
    },
    /** This browser is in a town — the lab's belt: no preview at all. */
    studioInTown() {
      return !!this.$store.state.session.sessionId;
    },
    /** Some other door's lab is running — the editor doesn't borrow it. */
    studioLabBusy() {
      return this.lab.open && !this.lab.studio;
    },
    /** The editor's preview is running (the table shows this Lot's night). */
    studioLive() {
      return this.lab.open && this.lab.studio;
    },
    /** This bench can preview at all — someone who can edit it, outside a
     *  town, a Lot with a night. */
    previewPossible() {
      return this.studioAvailable && !this.studioInTown && !this.neverWakes;
    },
    /** …and the preview should be running right now — the one switch the
     *  watcher follows (the drawer open, possible, not stopped). */
    previewWanted() {
      return (
        this.studioDocked &&
        this.previewPossible &&
        !this.studioLabBusy &&
        !this.previewStopped
      );
    },
    /** The role the preview shows: the script Lot's id, or the draft's. */
    studioRoleId() {
      const f = this.roleForm;
      if (!f) return "";
      return f.appId || "golemforgepreview";
    },
    studioGrammar() {
      return this.lab.roleId ? grammarOf(this.lab.roleId) : [];
    },
    /** The stage bar's one line wherever the preview isn't running — why
     *  not, in the order the reasons outrank each other (Clocktower's
     *  FT-1499 words). */
    studioNote() {
      if (this.studioInTown) return "Night preview is available outside a town";
      if (this.neverWakes) return "Never wakes — no night to preview";
      if (!this.studioAvailable) {
        return this.ownerPending
          ? "Preview is getting the table ready…"
          : "Save a copy to preview its night";
      }
      if (this.studioLabBusy) return "Another preview is running on the table";
      if (this.previewStopped) return "Preview stopped";
      return "Preview is getting the table ready…";
    },
    /** The stepper's dots — per state (in the view shown) and per view
     *  (across its states): "own" where the table shows this Lot's night
     *  text with some of it in your words, "text" where it shows text still
     *  on its default, "" where it shows none. */
    stateDots() {
      const out = {};
      this.studioGrammar.forEach((s) => {
        out[s] = this.textDot(this.lab.perspective, s);
      });
      return out;
    },
    viewDots() {
      const out = {};
      this.studioViews.forEach((v) => {
        const dots = this.studioGrammar.map((s) => this.textDot(v.id, s));
        out[v.id] = dots.includes("own")
          ? "own"
          : dots.includes("text")
          ? "text"
          : "";
      });
      return out;
    },
    /** FT-1501 (Clocktower's FT-1476): is the shell's corner the Save /
     *  Discard plate, or the plain ×? formDirty() as a computed, so the
     *  Modal slot re-renders on every edit. */
    roleFormDirty() {
      return this.formDirty();
    },
    /** The corner Save's tooltip — what it saves and where it lands. */
    saveLabel() {
      const f = this.roleForm;
      if (!f) return "Save";
      const name = f.name.trim() || "this Lot";
      return this.editingLibId
        ? `Save ${name} — every script that uses it`
        : `Save ${name} — a new Lot`;
    },
    /** The forge header's drop-cap N, in the caps' font (Almanac-style). */
    forgeCap() {
      const key = resolvedCapKey();
      const g = glyphFrom(key, "N");
      if (!g) return null;
      return { src: g.src, style: glyphStyleFrom(key, "N", CAP_SHRINK) };
    },
    roleShelfFiltered() {
      const q = this.roleQuery.trim().toLowerCase();
      if (!q) return this.roleShelf;
      return this.roleShelf.filter((entry) =>
        (entry.name || "").toLowerCase().includes(q),
      );
    },
    // ── Golem fork (FT-854): the workbench ───────────────────────────────
    myScripts() {
      return this.recents.filter((e) => e.editKey);
    },
    viewedScripts() {
      return this.recents.filter((e) => !e.editKey);
    },
    /** What the picker should show as current. */
    wbPickedId() {
      const edition = this.$store.state.edition;
      if (edition && edition.id !== "custom") return edition.id;
      if (edition && edition.logo === "__gold") return "__all";
      return this.vaultSourceId || "";
    },
    /** The picker's cards: officials, then your scripts, then viewed ones.
     *  Non-conforming scripts wear the warning right in their name. */
    wbScriptCards() {
      const cards = [];
      this.editions.forEach((e) => {
        cards.push({
          id: e.id,
          name: e.name,
          icon: EDITION_ICONS[e.id] || edCustom,
          blurb: OFFICIAL_BLURBS[e.id] || "",
          source: "OFFICIAL",
        });
      });
      // every official character on one script, behind the gold logo
      cards.push({
        id: "__all",
        name: "All of Blood on the Clocktower",
        icon: goldLogo,
        blurb: "Every official character — the whole book on one script.",
        source: "OFFICIAL",
      });
      const vaultCard = (entry, source) => ({
        id: entry.id,
        name: (this.ncMap[entry.id] ? "⚠ " : "") + (entry.name || entry.id),
        // the loaded script's own icon shows once known (edition.logo —
        // saved scripts carry it in _meta; role id or uploaded data URL)
        icon:
          entry.id === this.vaultSourceId && this.$store.state.edition.logo
            ? this.scriptLogoSrc(this.$store.state.edition.logo)
            : edCustom,
        blurb: this.ncMap[entry.id]
          ? "Outside the rules — still playable."
          : "",
        source,
        // FT-970: every SHELF card can be forgotten — that is a local edit and
        // it is the answer to a grid full of test clutter. The officials above
        // are not on the shelf and carry no flag, so they cannot be.
        forgettable: true,
        // ...and whether this browser holds the key, which is the whole
        // difference between "you lose your copy of the link" and "you lose
        // the only way you will ever edit this again".
        owned: !!entry.editKey,
      });
      this.myScripts.forEach((e) => cards.push(vaultCard(e, "yours")));
      this.viewedScripts.forEach((e) => cards.push(vaultCard(e, "viewed")));
      return cards;
    },
    /** The picker's own card for what is loaded — the small-screen note's
     *  whole read. A vault script that is not in the recents shelf still has
     *  a name on the edition, so the fallback is never a blank plate. */
    loadedCard() {
      const id = this.wbPickedId;
      const card = this.wbScriptCards.find((c) => c.id === id);
      if (card) return card;
      const edition = this.$store.state.edition || {};
      return { id: "", name: edition.name || "Custom script", icon: edCustom };
    },
    /** The four core teams with their counts, for the small-screen note. */
    smallCounts() {
      const icons = {
        townsfolk: "users",
        outsider: "walking",
        minion: "mask",
        demon: "user-secret",
      };
      return ["townsfolk", "outsider", "minion", "demon"].map((team) => ({
        team,
        label: TEAM_LABELS[team],
        icon: icons[team],
        count: this.teamCounts[team],
      }));
    },
    /** The current script as a list (state.roles is replaced wholesale). */
    /**
     * FT-1491: every character a section can be linked to ("Only with…"),
     * keyed by the id a section stores — an official's own id, a custom
     * character's library id. This script's characters first, then every
     * other official.
     */
    sectionRoster() {
      const out = [];
      const seen = new Set();
      const add = (id, role, icon) => {
        if (!id || seen.has(id)) return;
        seen.add(id);
        out.push({ id, name: role.name, team: role.team, icon });
      };
      const official = (role) =>
        add(role.id, role, this.iconUrl(role.imageAlt || role.id));
      this.scriptRoles.forEach((role) => {
        if (!role.isCustom) official(role);
        else if (role.golemRoleId) {
          add(
            role.golemRoleId,
            role,
            role.golemIconData || this.iconUrl(role.golemIcon || "custom"),
          );
        }
      });
      rolesJSON.forEach(official);
      return out;
    },
    scriptRoles() {
      const list = [];
      this.$store.state.roles.forEach((role) => list.push(role));
      return list;
    },
    // FT-857: the meter itself renders inside ScriptView; what stays here is
    // the same maths, from the same module, for the non-conforming MARK the
    // vault selector badges with.
    teamCounts() {
      return countTeams(this.scriptRoles);
    },
    /** Player counts the pool can serve under the official table. */
    servableCounts() {
      return servableFor(this.teamCounts);
    },
    /** "5–15" / "5, 7, 10–13" — collapse runs for the meter. */
    servableText() {
      return servableText(this.servableCounts);
    },
    // Travellers left the script surface (user call 2026-08-17): a script is
    // the town's regular menu; travellers join IN the town — the seat's role
    // picker already lists every traveller, in-script or not (otherTravelers).
    /** FT-855: the team toggle row — icon, live count (self-excluded, the
     *  faceted-search convention: a facet's own selection never zeroes its
     *  siblings). */
    teamRow() {
      const icons = {
        townsfolk: "users",
        outsider: "walking",
        minion: "mask",
        demon: "", // bespoke horned-head SVG (DEMON_PATH)
      };
      // FT-981: ONE pass, tallying by team — this was four passes over every
      // entry, one per team button, each re-running the same two matchers on
      // the same rows. Same numbers, a quarter of the work, on a computed that
      // re-runs on every keystroke.
      const counts = { townsfolk: 0, outsider: 0, minion: 0, demon: 0 };
      this.allShelfEntries.forEach((e) => {
        if (counts[e.team] === undefined) return;
        if (!this.matchesSearch(e)) return;
        if (!this.matchesPills(e, null)) return;
        counts[e.team]++;
      });
      return ["townsfolk", "outsider", "minion", "demon"].map((team) => ({
        team,
        label: TEAM_LABELS[team],
        icon: icons[team],
        count: counts[team],
      }));
    },
    // FT-1039: the three always-visible chip rows (the fold retired).
    sourceTags() {
      return TAG_GROUPS.find((g) => g.key === "source").tags;
    },
    nightTags() {
      return TAG_GROUPS.find((g) => g.key === "night").tags;
    },
    flagTags() {
      return TAG_GROUPS.find((g) => g.key === "flags").tags;
    },
    filteredCount() {
      return this.sidebarRoles.length;
    },
    searchActive() {
      return !!this.roleQuery.trim();
    },
    /** FT-981: the query, normalized and split, ONCE per change — not once
     *  per entry per matcher call. */
    searchTokens() {
      const q = normalizeSearch(this.roleQuery);
      return q ? q.split(" ") : [];
    },
    /**
     * FT-981: EVERY facet value's live count, tallied in one pass per FACET
     * instead of one pass per VALUE.
     *
     * `countFor` used to walk all 139 entries for each value on screen, and
     * with twelve values open that is twelve full passes running the same
     * three matchers over the same rows on every keystroke. Only the
     * `matchesPills` exclusion differs between values of the same facet — and
     * that is constant WITHIN a facet — so one pass per facet gives identical
     * numbers.
     *
     * Standard faceted counting is preserved exactly: a facet's own pills are
     * excluded from its own counts, so choosing one value never zeroes its
     * siblings.
     */
    facetCounts() {
      const out = {};
      TAG_GROUPS.forEach((group) => {
        if (group.key === "team") return;
        this.allShelfEntries.forEach((e) => {
          if (!this.matchesSearch(e)) return;
          if (!this.matchesTeams(e)) return;
          if (!this.matchesPills(e, group.key)) return;
          const tags = this.entryTags(e);
          group.tags.forEach((t) => {
            if (tags.has(t.id)) out[t.id] = (out[t.id] || 0) + 1;
          });
        });
      });
      return out;
    },
    /** FT-981: is anything OTHER than the search narrowing the rail? The
     *  empty state needs to name the right way out, and "clear the search"
     *  is useless advice when it was a team toggle that emptied the list. */
    filtersActive() {
      return (
        !!this.pills.length ||
        Object.keys(this.teamState).some((t) => this.teamState[t])
      );
    },
    totalCount() {
      return this.allShelfEntries.length;
    },
    /** Every shelf entry, UNFILTERED — the base set filters and counts read. */
    allShelfEntries() {
      const inScriptIds = new Set(this.scriptRoles.map((r) => r.id));
      const inScriptLibIds = new Set(
        this.scriptRoles.map((r) => r.golemRoleId).filter(Boolean),
      );
      const entries = [];
      rolesJSON.forEach((role) => {
        if (normTeam(role.team) === "traveler") return; // town-side, not script
        entries.push({
          key: "off-" + role.id,
          id: role.id,
          name: role.name,
          team: normTeam(role.team),
          ability: role.ability,
          iconUrl: this.iconUrl(role.id),
          official: true,
          inScript: inScriptIds.has(role.id),
          // FT-1039: the search reads the reminder tokens too — "poisoned"
          // finds every role that leaves that marker on a seat.
          reminders: role.reminders,
          // the tag filter's raw material
          edition: role.edition,
          firstNight: role.firstNight,
          otherNight: role.otherNight,
          setup: !!role.setup,
        });
      });
      const seen = new Set();
      // FT-1485: ONE ENTRY PER LOT. An official Lot's library row IS the
      // official entry above, so no library list names it again (see botc).
      const officialRowIds = new Set(houseRoles.officials.rowIds);
      // FT-1485: every library entry wears its icon (libIconUrl)
      const scriptByLib = new Map(
        this.scriptRoles
          .filter((r) => r.golemRoleId)
          .map((r) => [r.golemRoleId, r]),
      );
      const rowsById = new Map(
        [...this.roleResults, ...this.myRoles].map((r) => [r.id, r]),
      );
      const libIcon = (libId, icon, team) =>
        this.libIconUrl(
          scriptByLib.get(libId),
          icon || (rowsById.get(libId) || {}).icon,
          team,
        );
      // FT-1484 step 2: YOURS = this account's Lots plus this browser's shelf
      // (private ones included, badged); SHARED = public Lots from others.
      // Someone else's private Lot never appears in a list. What the server
      // says of the account's own rows beats the shelf's memory.
      const ownRows = new Map(this.myRoles.map((r) => [r.id, r]));
      this.roleShelf.forEach((entry) => {
        seen.add(entry.id);
        if (entry.officialId || officialRowIds.has(entry.id)) return;
        const own = ownRows.get(entry.id);
        // The Lot's TEAM: the account's row, else the team the shelf
        // remembers. (The shelf's `role` is its relationship — viewed /
        // forked / created — which grouped every shelf entry under no team
        // and so hid it from the rail; kept only as the legacy fallback.)
        const team = normTeam(
          (own && own.roleType) || entry.roleType || entry.role,
        );
        if (team === "traveler") return;
        const mine = !!entry.editKey || !!own;
        const visibility =
          (own && own.visibility) || entry.visibility || "public";
        if (!mine && visibility === "private") return;
        entries.push({
          key: "lib-" + entry.id,
          libId: entry.id,
          name: entry.name,
          team,
          iconUrl: libIcon(entry.id, entry.icon, team),
          // The recents shelf stores {id, name, editKey, role, lastSeen} and
          // no ability text, so a library role the author has only VIEWED is
          // reachable by name alone. Browse rows below do carry it. Passed
          // through rather than assumed absent, so a shelf entry that grows an
          // ability later starts matching without another change here.
          ability: entry.ability || "",
          isLib: true,
          mine,
          private: mine && visibility === "private",
          inScript: inScriptLibIds.has(entry.id),
        });
      });
      // the account's own Lots this browser has no shelf entry for
      this.myRoles.forEach((row) => {
        if (seen.has(row.id)) return;
        seen.add(row.id);
        if (normTeam(row.roleType) === "traveler") return;
        entries.push({
          key: "mine-" + row.id,
          libId: row.id,
          name: row.name,
          team: normTeam(row.roleType),
          iconUrl: libIcon(row.id, row.icon, normTeam(row.roleType)),
          ability: row.ability || "",
          isLib: true,
          mine: true,
          private: row.visibility === "private",
          inScript: inScriptLibIds.has(row.id),
        });
      });
      this.roleResults.forEach((row) => {
        if (seen.has(row.id)) return;
        if (normTeam(row.roleType) === "traveler") return;
        if (row.visibility === "private" && !row.ownedByYou) return;
        entries.push({
          key: "browse-" + row.id,
          libId: row.id,
          name: row.name,
          team: normTeam(row.roleType),
          iconUrl: libIcon(row.id, row.icon, normTeam(row.roleType)),
          ability: row.ability || "",
          isLib: true,
          mine: !!row.ownedByYou,
          private: !!row.ownedByYou && row.visibility === "private",
          inScript: inScriptLibIds.has(row.id),
        });
      });
      // FT-981: the searchable text and the tag set, built ONCE per entry here
      // rather than per keystroke in the matchers.
      //
      // Measured on the shipped build: one keystroke ran `matchesSearch` 278
      // times — every one of them re-lowercasing the same query and the same
      // name — and `countFor` rebuilt an entry's tag Set once per facet value
      // on screen. Both are pure functions of the entry, so they belong with
      // the entry. `entryTags` stays the single definition of the tag
      // vocabulary and simply reads this cache when it is present.
      entries.forEach((e) => {
        e.searchName = normalizeSearch(e.name);
        e.search =
          e.searchName +
          " " +
          normalizeSearch(
            // FT-1117: `reminderNames` because an entry may be an authored
            // object now — a raw join would index the shelf on "[object
            // Object]" and quietly stop finding "herring".
            (e.ability || "") + " " + reminderNames(e.reminders).join(" "),
          );
        e.tags = this.entryTags(e);
      });
      return entries;
    },
    /** The shelf, filtered by search + team row + pills, sorted team/name.
     *  (roles.json arrives grouped by edition — "weirdly sorted".) */
    sidebarRoles() {
      const teamRank = (t) => {
        const i = TEAM_ORDER.indexOf(t);
        return i < 0 ? TEAM_ORDER.length : i;
      };
      return this.allShelfEntries
        .filter(
          (e) =>
            this.matchesSearch(e) &&
            this.matchesTeams(e) &&
            this.matchesPills(e, null),
        )
        .sort(
          (a, b) =>
            teamRank(a.team) - teamRank(b.team) ||
            // FT-981: THE THING YOU NAMED COMES FIRST. Now that a search reads
            // ability text, an alphabetical list buries the obvious answer:
            // searching "poison" put the Boffin above the POISONER, because B
            // sorts before P and the Boffin's ability happens to say poison.
            // A name hit outranks an ability-only hit inside each team group;
            // with no query both sides are 0 and the order is unchanged.
            this.nameHitRank(b) - this.nameHitRank(a) ||
            (a.name || "").localeCompare(b.name || ""),
        );
    },
    /** The forge asks WHETHER a role wakes; WHERE it wakes is the script's
     *  night order (drag in the night views — the official model too:
     *  _meta.firstNight/otherNight arrays). A fresh wake lands at the end
     *  (100); pasted official numbers pass through untouched. */
    wakesFirstNight: {
      get() {
        return !!this.roleForm && this.roleForm.firstNight > 0;
      },
      set(v) {
        if (!this.roleForm) return;
        this.roleForm.firstNight = v ? this.roleForm.firstNight || 100 : 0;
      },
    },
    /**
     * FT-1040c: the DRAFT ROLE the live coin renders — the same shape a
     * forged role wears in the script, rebuilt from the form on every
     * keystroke so the preview tracks name, team, art, wakes and setup.
     * `imageAlt` always resolves to a bundled asset (an official borrow when
     * one is picked and known, the team-generic token otherwise), because
     * Token's icon lookup `require`s it — an unresolvable id would throw.
     */
    previewRole() {
      const f = this.roleForm;
      if (!f) return null;
      const team = f.roleType === "traveller" ? "traveler" : f.roleType;
      return {
        id: "golemforgepreview",
        name: f.name.trim() || "New Role",
        team,
        ability: f.ability,
        firstNight: f.firstNight,
        otherNight: f.otherNight,
        // FT-1120: the preview coin carries what the role would STORE, so a
        // rule-bearing token previews as the object the deal reads.
        reminders: this.reminderPills.map(fromReminderPill),
        setup: f.setup,
        // a baked icon is a data: URL — Token renders it with no opt-in
        image: f.iconData || "",
        // FT-1042: the live coin wears the draft's fit — the same fields,
        // the same Token, so the preview IS the in-game render
        golemArtScale: f.artScale,
        golemArtX: f.artX,
        golemArtY: f.artY,
        imageAlt:
          (f.icon && this.$store.getters.rolesJSONbyId.has(f.icon)
            ? f.icon
            : null) ||
          {
            townsfolk: "good",
            outsider: "outsider",
            minion: "minion",
            demon: "evil",
          }[team] ||
          "custom",
      };
    },
    /** FT-1494: the Studio's card — the draft as the hover card reads a
     *  Lot: the coin's own draft (so card and coin agree on team and art),
     *  with the raw name and both abilities (an empty one shows the card's
     *  hint, not "New Role") and the form's section rows, every one. */
    studioCard() {
      const f = this.roleForm;
      const p = this.previewRole;
      if (!f || !p) return null;
      return {
        ...p,
        name: f.name,
        ability: f.ability,
        shadowAbility: f.shadowAbility,
        golemSections: f.guideSections,
        // FT-1546: the card art, so the tarot preview and the card agree
        golemCardArt: f.cardArt,
      };
    },
    /** FT-1546: the card preview's art — the display rendition, as a full
     *  card loads it; "" shows the bare tarot card. */
    cardArtPreview() {
      return this.roleForm ? cardArtDisplay(this.roleForm.cardArt) : "";
    },
    /** FT-1546: LotCard's type is set for its 384 px default; the preview
     *  is narrower, so its name, ability text and band padding scale down
     *  with the width (the card's own tuning vars, shipped defaults × scale)
     *  rather than spilling past the card. */
    cardArtCardStyle() {
      const k = this.cardArtWidth / 384;
      return {
        "--lot-name-size": Math.round(32 * k) + "px",
        "--lot-ability-font-size": Math.round(16 * k * 10) / 10 + "px",
        "--lot-ability-padding": Math.round(15 * k) + "px",
      };
    },
    /** FT-1498: the section the panel under the card is editing, or null —
     *  the same row `roleForm.guideSections` holds, so the panel's fields
     *  write straight into the form the Save path reads. */
    activeGuideSection() {
      const list = this.roleForm && this.roleForm.guideSections;
      if (!list) return null;
      return list[this.activeSectionIndex] || null;
    },
    /** FT-1040 / FT-1120: the reminder pills. They used to be a VIEW over a
     *  comma-joined string; they are the storage now, so this is the array
     *  itself and the string form is only ever rendered back out of it. */
    reminderPills() {
      return (this.roleForm && this.roleForm.reminders) || [];
    },
    /** FT-1120: the pill whose deal rule is open, or null. */
    openDealPill() {
      const pills = this.reminderPills;
      return this.dealPop >= 0 && this.dealPop < pills.length
        ? pills[this.dealPop]
        : null;
    },
    dealEligibleOptions() {
      return DEAL_ELIGIBLE;
    },
    dealSelfOptions() {
      return DEAL_SELF;
    },
    wakesOtherNights: {
      get() {
        return !!this.roleForm && this.roleForm.otherNight > 0;
      },
      set(v) {
        if (!this.roleForm) return;
        this.roleForm.otherNight = v ? this.roleForm.otherNight || 100 : 0;
      },
    },
    /**
     * The Almanac's drop-cap wears the caps' font (right-click a door to
     * cycle); blood keeps the pixel-tuned baked class.
     *
     * FT-1188: STOOD DOWN — nothing renders it any more; the title prints
     * through `KeyCap` (see wb-row1). Kept, unreferenced, because it records
     * the two ways this cap was wrong: `glyphStyleFrom` is the WORD
     * normalization (every letter against the set's reference B), where a
     * lone drop-cap needs `glyphCapStyleFrom`; and the blood tier returned
     * `bloodA` — the "A" of the surface's old name — under `alt="S"`.
     */
    almanacCap() {
      const key = resolvedCapKey();
      if (key !== "blood" && key !== "logo") {
        const g = glyphFrom(key, "S");
        if (g)
          return {
            src: g.src,
            cls: "font-cap",
            style: glyphStyleFrom(key, "S", CAP_SHRINK),
          };
      }
      return { src: this.bloodA, cls: "blood-cap-a", style: null };
    },
    /** The script's editable surface, serialized — dirty = differs from the
     *  baseline taken at load/save/new. */
    currentScriptSnapshot() {
      const e = this.$store.state.edition;
      return JSON.stringify({
        e: e.id,
        n: e.name || "",
        l: e.logo || "",
        // FT-1403 phase 1: the script's night copy is part of what a
        // discard must restore — it rides the edition, so it rides the
        // snapshot too.
        c: e.nightCopy || null,
        r: this.collapseScript(),
      });
    },
    scriptDirty() {
      return (
        this.scriptBaseline !== null &&
        this.scriptBaseline !== this.currentScriptSnapshot
      );
    },
    /** FT-1043b: the overlay browses the ICON LIBRARY now, not the official
     *  art — same list the forge feed's library tab shows, same search. */
    nsIconMatches() {
      if (!this.ilLoaded) return [];
      const q = this.nsIconSearch.trim().toLowerCase();
      const list = this.$options.ilList || [];
      if (!q) return list;
      return list.filter((e) => e.n.includes(q));
    },
    // ── FT-1043: the builder head ────────────────────────────────────────
    /** The head shows wherever the loaded script is CUSTOM — officials have
     *  nothing renameable up there, and the new-script flow is just this
     *  with an empty script. */
    builderOpen() {
      return this.$store.state.edition.id === "custom";
    },
    /** What the script's icon slot holds (edition.logo — travels in _meta). */
    builderLogo() {
      return this.$store.state.edition.logo || "";
    },
    /** The header icon: the script's own mark, or the stock custom art. */
    builderIconStyle() {
      const logo = this.builderLogo;
      const src = logo ? this.scriptLogoSrc(logo) : this.edCustomArt;
      return { backgroundImage: `url(${src})` };
    },
    /** The script's name, editing the store in place. setEdition closes the
     *  modal as a side effect (upstream's flow ended there), so every write
     *  is chased by ensureOpen — the same dance every bench mutation does. */
    builderName: {
      get() {
        return this.$store.state.edition.name || "";
      },
      set(name) {
        const meta = this.$store.state.edition;
        this.$store.commit("setEdition", { ...meta, id: "custom", name });
        this.ensureOpen();
      },
    },
    /** FT-1043: the fork panel's go-button gate — dark until the copy's name
     *  is real and differs from the source's. */
    forkGoDisabled() {
      const f = this.forkForm;
      if (!f) return false;
      const name = (f.name || "").trim();
      if (!name) return true;
      const orig = (f.original || "").trim().toLowerCase();
      return f.forking && name.toLowerCase() === orig;
    },
    /** The shelf grouped by team, headers included (user call). */
    sidebarGroups() {
      return ["townsfolk", "outsider", "minion", "demon"]
        .map((team) => ({
          team,
          label: TEAM_LABELS[team],
          roles: this.sidebarRoles.filter((r) => r.team === team),
        }))
        .filter((g) => g.roles.length);
    },
    // FT-857: the by-team groups, the night wakers/sleepers and the drag
    // bookkeeping moved into ScriptView — the component that renders them.
  },
  // Golem fork: a ?script=<id> share link auto-loads its script on arrival.
  // The QUERY string is used (not the hash) because the hash is the live
  // session's join token upstream — the two must coexist on one URL.
  created() {
    const id = new URLSearchParams(window.location.search).get("script");
    if (id) this.loadFromVault(id);
    this.setBaseline();
    // FT-1484 step 3: the live overlay (golem/houseRoles) moving this
    // script's library Lots to their latest is not the author's edit — a
    // script that stood clean before it landed is re-baselined clean, so a
    // freshly opened script never greets its author with Save / Discard.
    this.$options.liveUnsub = this.$store.subscribe(({ type }) => {
      if (type !== "liveRolesApplied" && type !== "houseRolesApplied") return;
      if (this.$options.liveClean) this.setBaseline();
    });
    // FT-1484 step 2: the account's own Lots for the library's "Yours"
    this.refreshMyRoles();
  },
  mounted() {
    // The bench asks the window whether it has room, and keeps asking: a
    // phone rotated into landscape, or a desktop window dragged narrow, has
    // to flip live rather than at the next reload.
    const mq = window.matchMedia(SMALL_BENCH);
    const onChange = (e) => {
      this.smallScreen = e.matches;
    };
    this.smallScreen = mq.matches;
    // Safari before 14 has only the deprecated listener API.
    if (mq.addEventListener) mq.addEventListener("change", onChange);
    else mq.addListener(onChange);
    this.$options.benchMQ = mq;
    this.$options.benchOnChange = onChange;
    // FT-1039: `/` jumps to the search — the app's hotkey guard idiom
    // (App.vue keyup): keys typed into a field are typing, not hotkeys. The
    // overlays (forge, new-script, fork/ask/destroy panels) also swallow it —
    // focusing a field UNDER an overlay would type into the dark.
    const onSlash = (e) => {
      if (e.key !== "/" || e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      if (t && /^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName)) return;
      if (!this.modals.edition || this.smallScreen) return;
      if (
        this.roleForm ||
        this.iconPickOpen ||
        this.forkForm ||
        this.askForm ||
        this.destroyForm
      )
        return;
      const el = this.$refs.searchInput;
      if (!el) return;
      e.preventDefault();
      el.focus();
      el.select();
    };
    window.addEventListener("keydown", onSlash);
    this.$options.benchOnSlash = onSlash;
  },
  beforeDestroy() {
    // FT-1484 step 3: the live overlay's re-baseline
    if (this.$options.liveUnsub) this.$options.liveUnsub();
    // FT-1501: a running preview gives the table back, the dock stands down
    this.stopStudio();
    try {
      document.body.classList.remove("studio-docked", "studio-live");
    } catch (e) {
      // no document (tests): the dock is layout, never a guard
    }
    this.publishDock(false);
    window.removeEventListener("keydown", this.onArtKey, true);
    window.removeEventListener("keyup", this.onArtKey, true);
    // FT-1120: the deal popover's click-out listener, if one is open
    document.removeEventListener("mousedown", this.onDealPopDown);
    if (this.$options.benchOnSlash)
      window.removeEventListener("keydown", this.$options.benchOnSlash);
    const mq = this.$options.benchMQ;
    if (!mq) return;
    if (mq.removeEventListener)
      mq.removeEventListener("change", this.$options.benchOnChange);
    else mq.removeListener(this.$options.benchOnChange);
  },
  watch: {
    // FT-1484 step 3: whether the script stood clean at the last flush — the
    // live overlay's re-baseline (created) runs AFTER its commit, so it reads
    // the state from before it here
    scriptDirty: {
      immediate: true,
      handler(dirty) {
        this.$options.liveClean = !dirty;
      },
    },
    // FT-1484 step 2: signing in or out changes whose Lots are "Yours"
    accountId() {
      this.refreshMyRoles();
    },
    // FT-856: a team switch re-prints the baked library icon in the new tint
    "roleForm.roleType"() {
      this.rebakeForTeam();
    },
    // a new search or a source swap rewinds the feed to its top
    ilSearch() {
      this.resetFeed();
    },
    iconTab() {
      this.resetFeed();
    },
    // FT-1501: THE PREVIEW'S LIVE WIRING — the Lot's words, name and
    // abilities repaint the table on the keystroke…
    "roleForm.nightCopy": {
      deep: true,
      handler() {
        this.syncStudio();
      },
    },
    "roleForm.name"() {
      this.syncStudio();
    },
    "roleForm.ability"() {
      this.syncStudio();
    },
    "roleForm.shadowAbility"() {
      this.syncStudio();
    },
    // …the steps (and "wakes while dead") re-play (or restart) its night…
    "roleForm.nightSteps": {
      deep: true,
      handler() {
        this.syncStudioNight();
      },
    },
    "roleForm.wakesDead"() {
      this.syncStudioNight();
    },
    // …and a changed wake moves the night it is previewed on
    "roleForm.firstNight"() {
      this.restartStudio();
    },
    "roleForm.otherNight"() {
      this.restartStudio();
    },
    // THE PREVIEW IS ON WHENEVER IT CAN BE — one watch covers a late
    // ownership answer, a night given or cleared, the workbench closing and
    // reopening over the bench, and a stopped preview asked again
    previewWanted(on) {
      if (on) this.$nextTick(() => this.syncPreview());
      else this.stopStudio();
    },
    // the art overlay's Esc (see onArtKey) listens only while it is open
    artOpen(open) {
      const how = open ? "addEventListener" : "removeEventListener";
      window[how]("keydown", this.onArtKey, true);
      window[how]("keyup", this.onArtKey, true);
    },
    // a drag of the drawer's grip moves the table with it
    editorWidth() {
      if (this.studioDocked) this.publishDock(true);
    },
    // `studio-live` is the preview's own flag on <body>: the lab is running
    studioLive: {
      immediate: true,
      handler(on) {
        try {
          document.body.classList.toggle("studio-live", !!on);
        } catch (e) {
          // no document (tests)
        }
      },
    },
    // THE DOCK: the body class the unscoped rule at the foot of this file
    // keys on — the Scripts window becomes the left drawer, the table
    // slides right
    studioDocked: {
      immediate: true,
      handler(on) {
        try {
          document.body.classList.toggle("studio-docked", !!on);
          this.publishDock(!!on);
        } catch (e) {
          // no document (tests): the dock is layout, never a guard
        }
      },
    },
  },
  methods: {
    /** The team's own art, one definition for the whole app (golem/glyphs). */
    teamGlyph,
    // ── Golem fork: the script vault ─────────────────────────────────────
    async loadFromVault(id, attach = true) {
      try {
        const script = await vault.loadScript(id);
        // roles verbatim; carry the vault name in as _meta so the script's
        // name survives the round trip and seeds the save prompt later.
        const roles = Array.isArray(script.roles) ? script.roles.slice() : [];
        if (!roles.some((r) => r && r.id === "_meta")) {
          roles.unshift({
            id: "_meta",
            name: script.name,
            author: script.author,
          });
        }
        this.parseRoles(roles);
        this.vaultSourceId = script.id;
        this.recents = vault.getRecents();
        this.markNC(script.id);
        this.setBaseline();
        // FT-847: the host of an OWNED town picked a vault script → save it
        // to the town (skipped when the town itself supplied the script).
        if (attach) this.maybeAttachToTown(script.id);
      } catch (e) {
        alert("Could not load that script: " + e.message);
      }
    },
    /**
     * FT-847: attach the loaded script to the current town — only when this
     * browser HOSTS the session AND holds the town's edit key. Spectators are
     * never prompted; failures are silent (best-effort, like all town calls).
     */
    maybeAttachToTown(scriptId) {
      const { session } = this.$store.state;
      if (session.isSpectator || !session.sessionId) return;
      if (!towns.editKeyFor(session.sessionId)) return;
      towns
        .updateTown(session.sessionId, { scriptId })
        .then((town) => flashHint(`Script saved to ${town.name || town.id}`))
        .catch(() => {});
    },
    /** Open the workbench's inline ask (see its markup for why it exists). */
    openAsk(opts) {
      this.askError = "";
      this.askForm = { placeholder: "", value: "", okText: "OK", ...opts };
      this.$nextTick(() => {
        const el = this.$refs.askInput;
        if (el) {
          el.focus();
          el.select();
        }
      });
    },
    cancelAsk() {
      this.askForm = null;
      this.askError = "";
    },
    confirmAsk() {
      const f = this.askForm;
      if (!f) return;
      const value = (f.value || "").trim();
      if (!value) {
        this.askError = "Paste it in first, or cancel.";
        return;
      }
      // The handler may keep the panel open to show its own complaint — it
      // gets the last word by returning a string.
      const problem = f.onOk(value);
      if (problem) {
        this.askError = problem;
        return;
      }
      this.askForm = null;
      this.askError = "";
    },
    // ── FT-970: the two removals ─────────────────────────────────────────
    /**
     * FORGET — the picker's card ×. Local only: the entry leaves this
     * browser's shelf and the script stays on the server untouched, which is
     * what clears a grid full of test clutter without reaching anybody else.
     *
     * The picker already made the user click twice, so there is no second ask
     * here. The loaded script is deliberately NOT unloaded if it is the one
     * forgotten — the work on the bench is not the shelf's to throw away. It
     * does become fork-on-save, since the key went with the entry, and
     * `willFork` already tells the save path exactly that.
     */
    forgetScript(card) {
      vault.forget(card.id);
      this.recents = vault.getRecents();
    },
    /** Open the delete-for-everyone confirm for the LOADED script. */
    askDestroy() {
      if (!this.canDestroyLoaded) return;
      const entry = this.recents.find((e) => e.id === this.vaultSourceId);
      this.destroyError = "";
      this.destroyForm = {
        id: this.vaultSourceId,
        name:
          (entry && entry.name) ||
          (this.$store.state.edition || {}).name ||
          "this script",
      };
    },
    cancelDestroy() {
      this.destroyForm = null;
      this.destroyError = "";
    },
    /**
     * Destroy it. The server is the one that decides — this browser sends the
     * key it holds and a 403 means the key is no longer good, which is worth
     * saying rather than swallowing.
     *
     * On success the script is gone everywhere, so the bench stops claiming it
     * came from the vault: `vaultSourceId` clears, and the next save creates a
     * new script rather than trying to update a row that is not there.
     */
    async confirmDestroy() {
      const f = this.destroyForm;
      if (!f) return;
      this.destroyError = "";
      try {
        await vault.deleteScript(f.id);
      } catch (e) {
        this.destroyError =
          e && e.code === "not-yours"
            ? "This browser no longer holds the key for that script."
            : `Could not delete it — ${e.message}`;
        return;
      }
      this.recents = vault.getRecents();
      if (this.vaultSourceId === f.id) this.vaultSourceId = null;
      this.destroyForm = null;
    },
    promptVaultLoad() {
      this.openAsk({
        title: "Load a script",
        label: "Script link or id",
        placeholder: "https://… or an id",
        okText: "Load",
        onOk: (ref) => {
          const id = vault.parseScriptRef(ref);
          if (!id) return "That does not look like a script link.";
          this.loadFromVault(id);
        },
      });
    },
    /**
     * The dirty control's Save. An update in place goes straight through on
     * the name the script already carries; a FORK stops and asks for a name
     * first (see the fork panel's markup for why it is a panel and not a
     * prompt). `willFork` is knowable up front: no edit key for the source
     * means the save can only become a copy.
     */
    saveToVault() {
      const custom = this.$store.state.roles;
      if (this.$store.state.edition.id !== "custom" || !custom.size) {
        alert(
          "Load or build a custom script first — the vault stores custom scripts.",
        );
        return;
      }
      const name = (this.$store.state.edition.name || "").trim();
      if (this.willFork || !name) return this.openForkForm();
      return this.commitSave(name);
    },
    /** Open the name panel pre-filled with the ORIGINAL's name, focused and
     *  selected — one keystroke replaces it (FT-1043; the derived "(fork)"
     *  prefill retired with it: the go-button stays dark until the name
     *  differs, so confirming without typing can never re-use the
     *  original's). A nameless script gets its own blank. */
    openForkForm(forcing) {
      const original = (this.$store.state.edition.name || "").trim();
      // `forcing` is the stale-key case: the browser still holds a key, so
      // willFork reads false, but the server has already refused it.
      const forking = forcing === true || this.willFork;
      this.forkError = "";
      this.forkSuggestion = "";
      this.forkForm = {
        forking,
        original,
        name: original,
      };
      this.$nextTick(() => {
        const el = this.$refs.forkName;
        if (el) {
          el.focus();
          el.select();
        }
      });
    },
    /** "<name> (fork)", bumped past anything already on this browser's shelf
     *  — (fork 2), (fork 3) — so the panel never opens on a taken name.
     *  Forking a fork re-uses the stem rather than stacking suffixes. */
    suggestForkName(original) {
      const base =
        (original || "").replace(/\s*\(fork(?:\s+\d+)?\)\s*$/i, "").trim() ||
        "My script";
      let candidate = `${base} (fork)`;
      let n = 2;
      while (this.nameTaken(candidate)) candidate = `${base} (fork ${n++})`;
      return candidate;
    },
    /** Is that name already on the shelf? The script being saved in place
     *  does not clash with itself. */
    nameTaken(name) {
      const want = name.trim().toLowerCase();
      return this.recents.some(
        (e) =>
          e.id !== this.vaultSourceId &&
          (e.name || "").trim().toLowerCase() === want,
      );
    },
    /** Confirm the panel. The name must be there, must not be the original's,
     *  and must not be one already on the shelf — a clash offers a free name
     *  rather than dead-ending. */
    confirmFork() {
      const f = this.forkForm;
      if (!f) return;
      // FT-1043: the Enter key answers to the same gate the button shows
      if (this.forkGoDisabled) return;
      const name = (f.name || "").trim();
      this.forkSuggestion = "";
      if (!name) {
        this.forkError = f.forking
          ? "Your copy needs a name."
          : "A script needs a name.";
        return;
      }
      if (
        f.forking &&
        name.toLowerCase() === (f.original || "").toLowerCase()
      ) {
        this.forkError =
          "That is the original's name — your copy needs its own.";
        this.forkSuggestion = this.suggestForkName(f.original);
        return;
      }
      if (this.nameTaken(name)) {
        this.forkError = `You already have a script called “${name}”.`;
        this.forkSuggestion = this.suggestForkName(name);
        return;
      }
      this.forkError = "";
      this.forkForm = null;
      return this.commitSave(name, { forceFork: f.forking });
    },
    useForkSuggestion() {
      if (!this.forkForm || !this.forkSuggestion) return;
      this.forkForm.name = this.forkSuggestion;
      this.forkSuggestion = "";
      this.forkError = "";
    },
    /** Cancel abandons the SAVE, not the edits — the bench stays dirty. */
    cancelFork() {
      this.forkForm = null;
      this.forkError = "";
      this.forkSuggestion = "";
    },
    async commitSave(name, { forceFork = false } = {}) {
      // The CURRENT custom script = what the store holds. Plain base-role
      // entries collapse back to id references; custom roles ship whole.
      const meta = this.$store.state.edition;
      // A role whose id exists upstream collapses back to an id reference
      // (the Script Tool convention) — kept as a bare string when it carries
      // no night-order override; a custom role ships whole, minus the
      // store's derived display fields.
      const roles = this.collapseScript().map((entry) =>
        Object.keys(entry).length === 1 && entry.id !== undefined
          ? entry.id
          : entry,
      );
      // FT-854: the script's icon travels in _meta (script-tool convention),
      // so it survives the save/load round trip. FT-1403 phase 1: the
      // script's night copy (the sanitized nightCopy the edition carries)
      // rides the same pseudo-role for the same reason.
      if (
        this.$store.state.edition.logo ||
        this.$store.state.edition.nightCopy
      ) {
        const metaRow = {
          id: "_meta",
          name,
          author: meta.author,
        };
        if (this.$store.state.edition.logo) {
          metaRow.logo = this.$store.state.edition.logo;
        }
        if (this.$store.state.edition.nightCopy) {
          metaRow.nightCopy = this.$store.state.edition.nightCopy;
        }
        roles.unshift(metaRow);
      }
      try {
        const { script, created, forked } = await vault.saveScript({
          name,
          author: meta.author,
          roles,
          sourceId: this.vaultSourceId,
          forceFork,
        });
        // the confirmed name belongs to the script on the bench now, so the
        // title, the picker and the next save all read the copy, not the
        // original. setEdition closes the modal (upstream's flow ended
        // there) — ensureOpen puts it back, as everywhere else here.
        if (name !== meta.name) {
          this.$store.commit("setEdition", { ...meta, id: "custom", name });
          this.ensureOpen();
        }
        this.vaultSourceId = script.id;
        this.recents = vault.getRecents();
        // FT-854: stamp (or clear) the non-conforming mark — a marker, not a
        // gate; the save above already succeeded whatever the composition.
        this.markNC(script.id);
        this.setBaseline();
        // FT-847: a save/fork lands a (possibly new) script id — keep the
        // owned town pointing at what its host actually plays.
        this.maybeAttachToTown(script.id);
        const link = vault.shareLink(script.id);
        const what = forked
          ? "Forked into your own copy"
          : created
          ? "Saved"
          : "Updated";
        try {
          await navigator.clipboard.writeText(link);
          alert(`${what}. Share link copied:\n${link}`);
        } catch (e) {
          alert(`${what}. Share link:\n${link}`);
        }
      } catch (e) {
        // the stored key was revoked or wrong: the save can only be a fork
        // now, so come back round through the panel for a name.
        if (e && e.code === "fork-required") {
          this.forkForm = null;
          this.openForkForm(true);
          return;
        }
        alert("Save failed: " + e.message);
      }
    },
    async copyLinks() {
      try {
        await navigator.clipboard.writeText(vault.exportLinks());
        alert(
          "Your script links (edit keys included) are on the clipboard — paste them somewhere safe.",
        );
      } catch (e) {
        alert(vault.exportLinks());
      }
    },
    // ── Golem fork (FT-851): the custom-role library ─────────────────────
    /** Open the form — blank, or seeded from an existing edition role. */
    openRoleForm(role) {
      // FT-1501: a fresh bench starts clean — a preview left running by the
      // last one gives the table back first, the art overlay closes
      this.stopStudio();
      this.closeArt();
      this.previewStopped = false;
      this.discardForm = null;
      this.ownerPending = false;
      // FT-1484: a custom library Lot this account OWNS saves in place from
      // any device — one ask while the form opens; Save waits on the answer.
      this.editingLibOwned = false;
      this.libOwnedCheck = null;
      this.formFromList = false;
      const libAccount = this.$store.state.session.account;
      // FT-1484 step 2: the same ask brings the row's visibility (and whether
      // it is an official, always public). The toggle takes it when the Lot
      // is yours — account or edit key; a fork of someone else's keeps the
      // new-Lot default, PRIVATE. Asked signed out too (the key's case).
      if (role && role.isCustom && role.golemRoleId) {
        const libId = role.golemRoleId;
        // FT-1501: owning it is what opens the preview — the stage bar reads
        // "getting ready" until the answer lands, and the previewWanted
        // watch starts it the moment it does
        this.ownerPending = true;
        this.libOwnedCheck = roleLib.libraryRow(libId).then((row) => {
          const owned = !!libAccount && !!(row && row.ownedByYou);
          if (this.editingLibId === libId) {
            this.editingLibOwned = owned;
            if (row && (owned || roleLib.editKeyFor(libId))) {
              this.applyRowVisibility(row);
            }
          }
          return owned;
        });
        const settle = () => {
          if (this.editingLibId === libId) this.ownerPending = false;
        };
        this.libOwnedCheck.then(settle, settle);
      }
      this.roleError = "";
      this.reminderDraft = "";
      // FT-1120: a fresh open never inherits the last role's open rule panel
      this.closeDealPop();
      // FT-1041b: every fresh open lands with the feed rewound to its top
      this.resetFeed();
      // the library tab is the default view — have its chunk ready
      this.openIconLibrary();
      // FT-1538: the step builder reopens where it was left — see seedNight.
      // FT-1410 → FT-1486: the row's two labels are not part of the composed
      // entry — they are COPY, and they live on the LOT's own words
      // (night.copy.seats / .told), never on the script. The rest of the
      // Lot's words and night ride back on Save exactly as the row gave them.
      const copy = (role && sanitizeCopyEntry(role.golemNightCopy)) || {};
      let nightRest = null;
      if (
        role &&
        role.golemRoleNight &&
        typeof role.golemRoleNight === "object"
      ) {
        nightRest = { ...role.golemRoleNight };
        delete nightRest.copy;
      }
      const night = {
        ...this.seedNight(role),
        // FT-1501: the Lot's own words, every copy key present so each is
        // reactive — the table's night text edits into these in place (the
        // sheet's two labels among them; their boxes left the drawer).
        // `nightCopyHad`: it carried words when it opened, so a Save that
        // clears them all still sends the empty copy.
        nightCopy: { ...blankWords(), ...copy },
        nightCopyHad: Object.keys(copy).length > 0,
        nightRest,
      };
      if (role) {
        this.editingLibId = role.golemRoleId || null;
        this.roleForm = {
          name: role.name,
          roleType: roleLib.roleTypeFromTeam(role.team),
          ability: role.ability,
          // FT-1468: the Shadow side rides the form like the Light ability;
          // FT-1467 persists it, so an edit re-opens whatever was saved.
          shadowAbility: role.shadowAbility || "",
          firstNight: role.firstNight || 0,
          otherNight: role.otherNight || 0,
          // FT-1120: THE FORK KEEPS THE RULE. This line used to be
          // `reminderNames(role.reminders).join(", ")` — names only, because
          // the form's pills were text — so forking the Fortune Teller gave
          // you a copy that kept "Red herring" and quietly lost the herring.
          // The pills carry the whole entry now, so a fork carries the whole
          // declaration.
          reminders: toReminderPills(role.reminders),
          setup: !!role.setup,
          authorName: localStorage.getItem("golem.playerName") || "",
          icon: role.golemIcon || "",
          iconData: role.golemIconData || "",
          iconRef: role.golemIconRef || "",
          iconSeed: role.golemIconSeed || 0,
          // FT-1042: the art's fit rides the role — sanitized on the way in
          artScale: sanArtScale(role.golemArtScale),
          artX: sanArtOffset(role.golemArtX),
          artY: sanArtOffset(role.golemArtY),
          ...night,
          // FT-1484 step 2: Public / Private (see seedVisibility), and
          // whether the library row is an official — always public.
          visibility: this.seedVisibility(role),
          officialRow: false,
          // FT-1491: the Lot's sections, as editable rows
          guideSections: this.formSections(role),
          // FT-1546: the Lot's card art ({ display, thumb }), or null
          cardArt: cardArtOf(role.golemCardArt),
          // the app-side id to replace in the script (a fork mints a new
          // library id, so the library id alone can't find the old row)
          appId: role.id,
        };
      } else {
        this.editingLibId = null;
        this.roleForm = {
          name: "",
          roleType: "townsfolk",
          ability: "",
          shadowAbility: "",
          firstNight: 0,
          otherNight: 0,
          reminders: [],
          setup: false,
          authorName: localStorage.getItem("golem.playerName") || "",
          icon: "",
          iconData: "",
          iconRef: "",
          iconSeed: 0,
          artScale: 1,
          artX: 0,
          artY: 0,
          ...night,
          // FT-1484 step 2: every new Lot starts PRIVATE
          visibility: "private",
          officialRow: false,
          guideSections: [],
          cardArt: null,
          appId: null,
        };
      }
      // FT-1501: "unsaved" is measured from the form as opened (and again
      // after this tick's own touches); then the preview starts wherever it
      // can, and a Lot that never wakes — no night to show — opens on its art
      this.cardArtError = "";
      this.markFormClean();
      this.$nextTick(() => {
        this.markFormClean();
        this.syncPreview();
        if (this.neverWakes) this.openArt({ focus: false });
      });
    },
    // ── FT-1041b: the art feed + whole-forge drop ────────────────────────
    /** The one surviving pill: swap the feed's SOURCE. Swapping back to the
     *  library goes through openIconLibrary so its chunk is there. */
    toggleOfficialSource() {
      if (this.iconTab === "official") this.openIconLibrary();
      else this.iconTab = "official";
    },
    /** Top of the feed — a new search or source starts the reader over.
     *  ($el is a COMMENT node while the workbench is closed — its v-if is
     *  the template root — and a comment has no querySelector.) */
    resetFeed() {
      const root = this.$el;
      const el =
        root && root.querySelector && root.querySelector(".forge-feed");
      if (el) el.scrollTop = 0;
    },
    // ── FT-1042: the art's fit — drag the coin, scrub the size ───────────
    /** Drag the art around the coin's face. Pixel deltas become percent of
     *  the coin's own width, so the fit stored on the role is size-blind:
     *  the same numbers place the art identically on a 40px bluff mini and
     *  a 172px forge preview. Clamped to ±50% — the coin's radius. */
    onArtDragStart(e) {
      const f = this.roleForm;
      if (!f) return;
      const el = e.currentTarget;
      const w = el.offsetWidth || 1;
      const startX = f.artX || 0;
      const startY = f.artY || 0;
      const px = e.clientX;
      const py = e.clientY;
      el.setPointerCapture(e.pointerId);
      // FT-1501: a press that doesn't travel is a CLICK — the coin's door to
      // the art overlay; only a real drag (past ART_CLICK_SLOP) places the art
      let moved = false;
      const onMove = (ev) => {
        const dx = ev.clientX - px;
        const dy = ev.clientY - py;
        if (!moved && Math.hypot(dx, dy) < ART_CLICK_SLOP) return;
        if (!moved) {
          moved = true;
          this.artDragging = true;
        }
        f.artX = sanArtOffset(startX + (dx / w) * 100);
        f.artY = sanArtOffset(startY + (dy / w) * 100);
      };
      const onUp = (ev) => {
        this.artDragging = false;
        el.removeEventListener("pointermove", onMove);
        el.removeEventListener("pointerup", onUp);
        el.removeEventListener("pointercancel", onUp);
        if (!moved && ev.type === "pointerup" && e.button === 0) {
          this.openArt({ opener: el });
        }
      };
      el.addEventListener("pointermove", onMove);
      el.addEventListener("pointerup", onUp);
      el.addEventListener("pointercancel", onUp);
      e.preventDefault();
    },
    /** Back to the stock fit — centred, full size. */
    resetArt() {
      const f = this.roleForm;
      if (!f) return;
      f.artScale = 1;
      f.artX = 0;
      f.artY = 0;
    },
    /** FT-1040: pick a night action shape — clicking the lit one clears it
     *  (a composed action is optional; none means the fallback free box). */
    pickNightShape(id) {
      this.roleForm.nightShape = this.roleForm.nightShape === id ? "" : id;
    },
    /** FT-1040 / FT-1120: the reminder pills. Each pill is `{name, deal}` and
     *  the pill list IS the storage — a fresh one carries no rule, which is
     *  what makes it serialise as the bare string it always did. */
    addReminderPill() {
      const token = this.reminderDraft.trim().replace(/,/g, "");
      if (!token) return;
      this.roleForm.reminders = this.reminderPills.concat({
        name: token,
        deal: null,
      });
      this.reminderDraft = "";
    },
    removeReminderPill(i) {
      const list = this.reminderPills.slice();
      list.splice(i, 1);
      this.roleForm.reminders = list;
      // the open rule belonged to a pill that no longer exists (or has
      // shuffled down one), so the panel closes rather than re-pointing
      this.closeDealPop();
    },
    // ── FT-1120: the deal rule, authored on the pill ─────────────────────
    /** What the pill's tooltip says — the rule in the author's own words, or
     *  the invitation to make one. */
    dealSummary(pill) {
      if (!pill || !pill.deal) return `“${pill.name}” — set how it is dealt`;
      const d = pill.deal;
      const who = (DEAL_ELIGIBLE.find((o) => o.value === d.eligible) || {})
        .label;
      const self = (DEAL_SELF.find((o) => o.value === d.self) || {}).label;
      const seats = d.seats === 1 ? "1 seat" : `${d.seats} seats`;
      return `The deal places “${pill.name}” on ${seats} — ${who}; this role's own seat: ${self}`;
    },
    /** Click a pill's name: open its rule, or close it if it is already the
     *  open one. The tail is pointed at the pill it came from. */
    toggleDealPop(i, e) {
      if (this.dealPop === i) {
        this.closeDealPop();
        return;
      }
      this.dealPop = i;
      const row = this.$refs.remPills;
      const pill = e && e.currentTarget && e.currentTarget.parentElement;
      if (row && pill) {
        this.dealTailX = Math.max(
          6,
          pill.offsetLeft - row.offsetLeft + pill.offsetWidth / 2 - 6,
        );
      }
      // click-out, the dropdown's own contract (OptionSelect.doOpen) — the
      // pickers INSIDE the panel are inside its DOM, so their menus do not
      // count as a click outside
      document.addEventListener("mousedown", this.onDealPopDown);
    },
    closeDealPop() {
      this.dealPop = -1;
      document.removeEventListener("mousedown", this.onDealPopDown);
    },
    onDealPopDown(e) {
      const pop = this.$refs.remPop;
      const row = this.$refs.remPills;
      const inside = (el) => el && el.contains && el.contains(e.target);
      if (!inside(pop) && !inside(row)) this.closeDealPop();
    },
    /** The one toggle. ON mints the rule at its defaults (1 seat / anyone /
     *  the owner may receive it — dealReminders.js's own defaults, so an
     *  untouched rule and an absent field mean the same thing); OFF drops it
     *  and the token goes back to being a plain string. */
    toggleDealRule() {
      const pill = this.openDealPill;
      if (!pill) return;
      this.setPill(this.dealPop, {
        ...pill,
        deal: pill.deal ? null : { ...DEAL_DEFAULTS },
      });
    },
    /** Write one field of the open rule. */
    setDeal(key, value) {
      const pill = this.openDealPill;
      if (!pill || !pill.deal) return;
      this.setPill(this.dealPop, {
        ...pill,
        deal: normalizeDeal({ ...pill.deal, [key]: value }),
      });
    },
    /** Replace one pill — a new array, so the form's own watchers (and the
     *  live preview coin) see the change the way Vue 2 needs them to. */
    setPill(i, pill) {
      const list = this.reminderPills.slice();
      list.splice(i, 1, pill);
      this.roleForm.reminders = list;
    },
    // ── FT-1491: THE SECTIONS ─────────────────────────────────────────────
    /** A role's sections as the form's rows (the picker's names looked up). */
    formSections(role) {
      return sectionsOf(role).map((s) => ({
        title: s.title || "",
        text: s.text || "",
        audience: s.audience === "storyteller" ? "storyteller" : "everyone",
        with: s.with || "",
        withName: s.with
          ? (this.sectionRoster.find((r) => r.id === s.with) || {}).name || ""
          : "",
      }));
    },
    /** FT-1498: the "+ Section" chip — adds a row and opens its editor. */
    addGuideSection() {
      const list = this.roleForm && this.roleForm.guideSections;
      if (!list || list.length >= SECTIONS_MAX) return;
      list.push({
        title: "",
        text: "",
        audience: "everyone",
        with: "",
        withName: "",
      });
      this.activeSectionIndex = list.length - 1;
      this.$nextTick(() => {
        if (this.$refs.secTitleInput) this.$refs.secTitleInput.focus();
      });
    },
    /** One step up (`by` -1) or down (+1). */
    moveGuideSection(i, by) {
      const list = this.roleForm.guideSections;
      const j = i + by;
      if (j < 0 || j >= list.length) return;
      const [row] = list.splice(i, 1);
      list.splice(j, 0, row);
    },
    removeGuideSection(i) {
      this.roleForm.guideSections.splice(i, 1);
    },
    /** FT-1498: the open panel's own move — follows the section it edits. */
    moveActiveSection(by) {
      const i = this.activeSectionIndex;
      const j = i + by;
      if (i < 0 || j < 0 || j >= this.roleForm.guideSections.length) return;
      this.moveGuideSection(i, by);
      this.activeSectionIndex = j;
    },
    /** FT-1498: the open panel's own remove — closes the panel after. */
    removeActiveSection() {
      const i = this.activeSectionIndex;
      if (i < 0) return;
      this.removeGuideSection(i);
      this.closeSectionPanel();
    },
    /** FT-1498: Esc, or the panel's own close button. */
    closeSectionPanel() {
      this.activeSectionIndex = -1;
    },
    focusSecText() {
      if (this.$refs.secTextInput) this.$refs.secTextInput.focus();
    },
    /** The picker's answer — a character, or "" (the dash) to clear. */
    pickSectionWith(row, { id, name }) {
      row.with = id || "";
      row.withName = id ? name || "" : "";
    },
    sectionIcon(role) {
      return role.icon || null;
    },
    // ── FT-1494: THE EDITABLE CARD ────────────────────────────────────────
    /** The card's name / Light / Shadow edits, into the form. */
    onCardEdit(field, value) {
      const fields = ["name", "ability", "shadowAbility"];
      if (!this.roleForm || !fields.includes(field)) return;
      this.roleForm[field] = value;
    },
    /** The card's icon (and the coin's keyboard door): FT-1501 — the art
     *  overlay over the table, its search ready. */
    onCardIcon() {
      this.openArt();
    },
    startAuthor() {
      this.authorEditing = true;
      this.$nextTick(() => {
        const input = this.$refs.authorInput;
        if (input) input.focus();
      });
    },
    /** Enter / Esc hand the focus back to the chip; a blur leaves it be. */
    endAuthor(refocus) {
      if (!this.authorEditing) return;
      this.authorEditing = false;
      if (!refocus) return;
      this.$nextTick(() => {
        if (this.$refs.authorBtn) this.$refs.authorBtn.focus();
      });
    },
    /** The hover card's tag row for a list entry — readable labels, the
     *  team left off (the card says it in words). FT-1494: one definition,
     *  read by the builder's hover and the Studio's card alike.
     *  FT-1496: each chip now carries its icon too (editionMark/nightMark's
     *  own table, plus the custom fallback for a source with no mark of its
     *  own) so the hover card can draw it before the label. */
    tipChipsFor(entry) {
      return [...this.entryTags(entry)]
        .filter((id) => !id.startsWith("team:"))
        .map((id) => ({
          label: this.pillValueLabel({ id }),
          icon: this.tipChipIcon(id),
        }));
    },
    /** FT-1496: the icon a tip chip wears — an edition's own mark (falling
     *  back to the stock custom mark for a source with none, e.g. a library
     *  or experimental role), or a night tab's own moon. Every other tag
     *  ("Never wakes", "Affects setup", "In this script") stays text-only. */
    tipChipIcon(id) {
      const night = this.nightMark(id);
      if (night) return night;
      if (id.startsWith("src:")) return EDITION_ICONS[id.slice(4)] || edCustom;
      return null;
    },
    closeRoleForm() {
      // FT-1501: the bench closes → the preview gives the table back, and
      // the art overlay goes with it
      this.stopStudio();
      this.closeArt();
      this.previewStopped = false;
      this.ownerPending = false;
      this.roleForm = null;
      // nothing left to ask about
      this.discardForm = null;
      this.formBaseline = "";
      this.roleError = "";
      this.cardArtError = "";
      this.closeDealPop();
    },
    // ── FT-1501: THE ONE SHELL + THE LIVE PREVIEW (Clocktower's Studio) ──
    /** The preview follows `previewWanted` — on whenever it can be, off the
     *  moment it can't (startStudio re-checks, and never starts a second
     *  one). */
    syncPreview() {
      if (this.previewWanted) this.startStudio();
      else this.stopStudio();
    },
    /**
     * Enter the lab for the Lot on the bench — only for whoever can edit it,
     * only outside a town (the lab's belt, re-checked by enter() itself).
     * A Lot the bundle doesn't know rides in as an object: the script's own
     * Lot, or the draft of a new one.
     */
    startStudio() {
      const f = this.roleForm;
      if (!f || !this.previewWanted || this.lab.open) return;
      const store = this.$store;
      let id = this.studioRoleId;
      let role = null;
      if (!store.getters.rolesJSONbyId.has(id)) {
        role =
          (id !== "golemforgepreview" && store.state.roles.get(id)) || null;
        if (!role) {
          id = "golemforgepreview";
          role = this.previewRole;
        }
      }
      // the dials on the bench stand in before the lab builds its stepper,
      // so the preview opens on THIS night
      setDraftNight(id, this.draftNightEntry());
      if (
        !enterLab(store, id, {
          studio: true,
          role,
          // the menu's Exit preview ends THIS preview through its owner
          stop: () => this.onPreviewStopped(),
          // the table's night text edits into this form
          copy: this.copyEditor(),
        })
      ) {
        clearDraftNight();
        return;
      }
      this.syncStudio();
    },
    /** Give the table back (the lab's exit restores it byte-for-byte) and
     *  drop the unsaved words and night from the copy and night layers. */
    stopStudio() {
      clearDraftCopy();
      clearDraftNight();
      if (this.lab.open && this.lab.studio) exitLab(this.$store);
    },
    /** A stop, then a fresh start (the Lot's wakes moved its night). */
    restartStudio() {
      if (!this.studioLive) return;
      this.stopStudio();
      this.$nextTick(() => this.startStudio());
    },
    /** The menu's Exit preview: it stays off — the drawer stays, the edits
     *  survive — until "Preview again". */
    onPreviewStopped() {
      this.previewStopped = true;
      this.stopStudio();
    },
    resumePreview() {
      this.previewStopped = false;
      this.syncPreview();
    },
    /**
     * THE ONE DOOR the table's night text edits through (golem/nightLab
     * `copy`, read by NightCopyText): its words are this form's `nightCopy`,
     * so Save / Discard, the unsaved-changes ask and the cap are the form's
     * own, and the watcher repaints the table from them.
     */
    copyEditor() {
      return {
        // FT-1536: null = the default, "" = left empty on purpose
        raw: (key) => {
          const f = this.roleForm;
          const v = f && f.nightCopy && f.nightCopy[key];
          return typeof v === "string" ? v : null;
        },
        write: (key, text) => {
          const f = this.roleForm;
          if (!f || !f.nightCopy || COPY_KEYS.indexOf(key) === -1) return;
          f.nightCopy[key] =
            typeof text === "string" ? text.slice(0, COPY_MAX) : null;
        },
        fallback: (key) => labCopyDefault(this.lab.roleId, key),
        max: COPY_MAX,
      };
    },
    /** One stepper dot — see stateDots. */
    textDot(view, state) {
      const f = this.roleForm;
      if (!f || !this.lab.roleId) return "";
      // the dials decide which keys the night speaks (an answer splits the
      // sheet row), so they are read for the dots to follow them
      // FT-1538: the steps decide which keys the night speaks
      if (!JSON.stringify(f.nightSteps)) return "";
      const keys = copyKeysShown(this.lab.roleId, view, state);
      if (!keys.length) return "";
      // FT-1536: a line left empty ("") is the author's choice too
      return keys.some((k) => typeof f.nightCopy[k] === "string")
        ? "own"
        : "text";
    },
    dotTitle(dot) {
      if (dot === "own") return "Night text here — some of it in your words";
      return dot ? "Night text here — click it on the table to edit it" : "";
    },
    dotWords(dot) {
      if (dot === "own") return ", has night text in your words";
      return dot ? ", has night text" : "";
    },
    /** The left side's live edits into the preview: the words as the Lot's
     *  draft copy, the name and abilities onto the acting seat's role. */
    syncStudio() {
      if (!this.studioLive || !this.roleForm) return;
      const f = this.roleForm;
      setDraftCopy(this.lab.roleId, f.nightCopy);
      patchActorRole(this.$store, {
        name: f.name.trim() || "New Role",
        ability: f.ability,
        shadowAbility: f.shadowAbility,
      });
    },
    /** The entry the preview answers from — the steps on the bench, or
     *  null where the Lot has none (FT-1538, Clocktower's FT-1475). */
    draftNightEntry() {
      const f = this.roleForm;
      if (!f || !f.nightSteps.length) return null;
      const wakes = [];
      if (f.firstNight > 0) wakes.push("first");
      if (f.otherNight > 0) wakes.push("other");
      return entryFromSteps(f.nightSteps, {
        wakes,
        wakesWhenDead: f.wakesDead,
      });
    },
    /** The dials changed while the preview runs: the draft takes over, and
     *  the lab re-plays its state — or restarts, when the change swapped the
     *  night's grammar (a chooser became a receiver, or back). */
    syncStudioNight() {
      if (!this.studioLive || !this.roleForm) return;
      const id = this.lab.roleId;
      const before = grammarOf(id).join(",");
      setDraftNight(id, this.draftNightEntry());
      if (grammarOf(id).join(",") === before) {
        applyLabState(this.$store, this.lab.stateId);
        return;
      }
      this.restartStudio();
    },
    /** FT-1501 (Clocktower's FT-1475 round 2): the window's × (and Esc)
     *  close the LOT EDITOR first — back to the Scripts workbench — and only
     *  close the Scripts window itself when no editor is open. */
    onShellClose() {
      if (this.roleForm) {
        this.requestCloseRoleForm();
        return;
      }
      this.toggleModal("edition");
    },
    /** The form as it stands, serialized — "unsaved" is this against the
     *  snapshot taken when the form opened (or last reverted). */
    formSnapshot() {
      return this.roleForm ? JSON.stringify(this.roleForm) : "";
    },
    markFormClean() {
      this.formBaseline = this.formSnapshot();
    },
    formDirty() {
      return !!this.roleForm && this.formSnapshot() !== this.formBaseline;
    },
    /** Every way OUT of the editor but Save — the ×, Esc — comes here:
     *  unsaved edits ask first (the in-app panel), a clean form just
     *  closes. */
    requestCloseRoleForm() {
      if (!this.roleForm) return;
      if (this.formDirty()) {
        this.discardForm = { name: this.roleForm.name.trim() || "New Lot" };
        return;
      }
      this.closeRoleForm();
    },
    confirmDiscard() {
      this.discardForm = null;
      this.closeRoleForm();
    },
    /** FT-1501 (Clocktower's FT-1476): the corner's Discard — reverts the
     *  bench to the snapshot taken when it opened, WITHOUT leaving the
     *  editor; the corner drops back to the plain × once it lands. */
    discardRoleForm() {
      if (!this.roleForm || !this.formBaseline) return;
      this.roleForm = JSON.parse(this.formBaseline);
    },
    /** The drawer's grip: golem/rightDrawer's drag, pinned to the LEFT, the
     *  width remembered per browser when the drag ends. */
    startEditorResize(e) {
      dragWidth(e, {
        startWidth: this.editorWidth,
        side: "left",
        min: EDITOR_W_MIN,
        max: EDITOR_W_MAX,
        onWidth: (w) => (this.editorWidth = w),
        onEnd: (w) => this.storeEditorWidth(w),
      });
    },
    resetEditorWidth() {
      this.editorWidth = EDITOR_W_DEFAULT;
      this.storeEditorWidth(EDITOR_W_DEFAULT);
    },
    /** The grip's keyboard: the arrows nudge the drawer's edge. */
    nudgeEditorWidth(by) {
      const w = Math.max(
        EDITOR_W_MIN,
        Math.min(EDITOR_W_MAX, this.editorWidth + by),
      );
      this.editorWidth = w;
      this.storeEditorWidth(w);
    },
    storeEditorWidth(w) {
      try {
        localStorage.setItem(EDITOR_W_KEY, String(w));
      } catch (err) {
        // a full or blocked store costs the memory, never the resize
      }
    },
    /** The table's room: the drawer's width as `--studio-dock` on <body>
     *  while the editor is docked (the unscoped dock rules and App.vue's
     *  --studio-shift read it), gone the moment it closes. */
    publishDock(on) {
      try {
        if (on) {
          document.body.style.setProperty(
            "--studio-dock",
            this.editorWidth + "px",
          );
        } else {
          document.body.style.removeProperty("--studio-dock");
        }
      } catch (err) {
        // no document (tests): the dock is layout, never a guard
      }
    },
    /** Clocktower's FT-1492: a change of the lab's view or state commits
     *  seat and spectator flips that make App.vue's diff physically MOVE an
     *  ancestor of this drawer, which resets the form column's scroll. The
     *  column's own scroll is snapshotted and put back across the call. */
    preserveFormScroll(run) {
      const el =
        this.$el &&
        this.$el.querySelector &&
        this.$el.querySelector(".fc-identity");
      const top = el ? el.scrollTop : null;
      run();
      if (top === null) return;
      const restore = () => {
        if (el.isConnected && el.scrollTop !== top) el.scrollTop = top;
      };
      this.$nextTick(restore);
      requestAnimationFrame(() => {
        restore();
        requestAnimationFrame(restore);
      });
    },
    studioStateLabel(s) {
      return STUDIO_STATE_LABELS[s] || s;
    },
    pickStudioState(s) {
      this.preserveFormScroll(() => applyLabState(this.$store, s));
    },
    pickStudioView(v) {
      this.preserveFormScroll(() => setLabPerspective(this.$store, v));
    },
    /** FT-1538 (Clocktower's FT-1475 round 2): the Night panel's Reader /
     *  Player switch — the checklist-row preview's gate, and the lab's
     *  viewpoint (restarting a stopped preview first). */
    pickNightView(view) {
      this.localNightView = view;
      if (this.studioLive) {
        this.preserveFormScroll(() => setLabPerspective(this.$store, view));
        return;
      }
      if (!this.previewStopped) return;
      this.preserveFormScroll(() => this.resumePreview());
      this.$nextTick(() => {
        if (this.studioLive) {
          this.preserveFormScroll(() => setLabPerspective(this.$store, view));
        }
      });
    },
    /** FT-1538: a step's Preview — drive the live table to the state that
     *  shows it: a pick to the pick (Picked where the night has one, else the
     *  Invitation), a telling to the answer arriving (Sent / Told). */
    previewStep(i) {
      const step = this.roleForm && this.roleForm.nightSteps[i];
      if (!step) return;
      this.previewStepIdx = i;
      const go = () => {
        if (!this.studioLive) return;
        const g = grammarOf(this.lab.roleId);
        const want =
          step.kind === "receives"
            ? ["st-sent", "telling"]
            : step.by === "player"
            ? ["staged", "invite", "before"]
            : ["invite", "before"];
        const state = want.find((x) => g.includes(x)) || g[0];
        if (state) applyLabState(this.$store, state);
        this.previewStepIdx = i;
      };
      const goPreserving = () => this.preserveFormScroll(go);
      if (this.studioLive) {
        goPreserving();
        return;
      }
      if (!this.previewStopped) return;
      this.preserveFormScroll(() => this.resumePreview());
      this.$nextTick(goPreserving);
    },
    // ── FT-1538: THE STEP BUILDER (Clocktower's FT-1475 / FT-1514 / FT-1529)
    /**
     * The night half of a fresh form, off whatever the Lot carries:
     *   steps      the row's saved steps; else a composed entry (an older
     *              Lot authored with the six shapes) read back as steps.
     *   wakesDead  the row's saved answer, else the entry's own.
     *   reminders  the row's night-sheet text (the snapshot's too) — read
     *              only, for the checklist-row preview's line.
     *   spentToken the row's saved option, else the snapshot's.
     * L.O.T.S. has no built-in (shipped) Lot nights, so Clocktower's "Use
     * built-in" half does not apply: every Lot's list is its own.
     */
    seedNight(role) {
      const rowNight =
        role && role.golemRoleNight && typeof role.golemRoleNight === "object"
          ? role.golemRoleNight
          : null;
      let steps = [];
      if (rowNight && Array.isArray(rowNight.steps)) {
        steps = rowNight.steps.map(cleanStep).filter(Boolean);
      } else if (role && role.golemNight) {
        steps = stepsFromEntry(role.golemNight);
      }
      const deadSaved =
        !!rowNight && typeof rowNight.wakesWhenDead === "boolean";
      const wakesDead = deadSaved
        ? rowNight.wakesWhenDead
        : !!(role && role.golemNight && role.golemNight.wakesWhenDead);
      const rem = (rowNight && rowNight.reminders) || {};
      const spentSaved = !!rowNight && typeof rowNight.spentToken === "boolean";
      return {
        nightSteps: steps,
        wakesDead,
        wakesDeadBase: wakesDead,
        wakesDeadSaved: deadSaved,
        nightReminders: {
          first: rem.first || (role && role.firstNightReminder) || "",
          other: rem.other || (role && role.otherNightReminder) || "",
        },
        spentToken: spentSaved
          ? rowNight.spentToken
          : !!(role && role.golemSpentToken),
        spentTokenSaved: spentSaved,
      };
    },
    stepIsCountable(step) {
      return stepCountable(step);
    },
    /** Change one field of one step. A grimoire is always a view you open;
     *  a telling is always the Reader's; flipping to Picks starts on the
     *  player's own pick. */
    setStep(i, key, value) {
      const list = this.roleForm.nightSteps.slice();
      const was = list[i];
      if (!was) return;
      const next = { ...was, [key]: value };
      if (next.what === "grimoire") next.kind = "receives";
      if (next.kind === "receives") next.by = "storyteller";
      else if (key === "kind" && was.kind === "receives") next.by = "player";
      list[i] = cleanStep(next) || was;
      this.roleForm.nightSteps = list;
    },
    addStep() {
      const list = this.roleForm.nightSteps;
      if (list.length >= NIGHT_STEPS_MAX) return;
      this.roleForm.nightSteps = list.concat({
        kind: "selects",
        what: "player",
        count: 1,
        by: "player",
      });
    },
    removeStep(i) {
      const list = this.roleForm.nightSteps.slice();
      list.splice(i, 1);
      this.roleForm.nightSteps = list;
    },
    moveStep(i, d) {
      const list = this.roleForm.nightSteps.slice();
      const j = i + d;
      if (j < 0 || j >= list.length) return;
      [list[i], list[j]] = [list[j], list[i]];
      this.roleForm.nightSteps = list;
    },
    /** A template REPLACES the list — one click to a working night. */
    applyStepTemplate(id) {
      const t = NIGHT_STEP_TEMPLATES.find((x) => x.id === id);
      if (!t) return;
      this.roleForm.nightSteps = t.steps.map((st) => ({ ...st }));
    },
    /** The words this step's noun slot may take: the vocabulary its kind
     *  allows, plus the word it already wears even when that one is not on
     *  offer (a saved step is never quietly relabelled). */
    nounOptions(st) {
      const has = (nounForStep(st) || {}).id || "";
      return STEP_NOUNS.filter(
        (n) => (!n.hidden || n.id === has) && nounAllowed(n, st.kind),
      ).map((n) => ({ value: n.id, label: n.menu }));
    },
    /** FT-1529: the noun a step wears, as the dropdown's value. */
    nounIdOf(st) {
      return (nounForStep(st) || {}).id || "";
    },
    /** A word was changed. The noun sets three stored fields at once. */
    setSlot(i, slot, value) {
      if (slot !== "noun") {
        this.setStep(i, slot, value);
        return;
      }
      const list = this.roleForm.nightSteps.slice();
      const was = list[i];
      if (!was) return;
      const next = cleanStep(stepWithNoun(was, value));
      if (!next) return;
      list[i] = next;
      this.roleForm.nightSteps = list;
    },
    replayStudio() {
      replayLab(this.$store);
    },
    /** Open the art over the right side — from the coin or the card's icon
     *  (its search focused), or on its own for a Lot that never wakes
     *  (`focus: false`: nothing was pressed). Focus goes back to the opener
     *  when it closes. */
    openArt({ focus = true, opener = null } = {}) {
      if (!this.roleForm) return;
      if (!this.artOpen) {
        this.$options.artOpener =
          opener || (focus ? document.activeElement : null);
      }
      this.artOpen = true;
      if (!focus) return;
      this.$nextTick(() => {
        const search = this.$refs.feedSearch;
        if (search) search.focus();
      });
    },
    closeArt() {
      if (!this.artOpen) return;
      this.artOpen = false;
      const back = this.$options.artOpener;
      this.$options.artOpener = null;
      if (back && typeof back.focus === "function") {
        this.$nextTick(() => {
          if (back.isConnected) back.focus();
        });
      }
    },
    /** Esc while the art is open closes the ART and stops there — window
     *  capture, so it runs before anything that would close the Scripts
     *  window with it. A field in the DRAWER keeps its own Esc (finishing a
     *  card edit, a scrub, the author chip). */
    onArtKey(e) {
      if (!this.artOpen || (e.key !== "Escape" && e.key !== "Esc")) return;
      const t = e.target;
      const panel = this.$refs.artPanel;
      const inPanel = !!(panel && t && panel.contains(t));
      const field =
        !!t &&
        (/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) || !!t.isContentEditable);
      if (field && !inPanel) return;
      e.stopPropagation();
      e.preventDefault();
      if (e.type === "keyup") this.closeArt();
    },
    /** Click an icon to select it; click again to clear (icon is optional).
     *  FT-1041b: the pick lands on the coin immediately. FT-1501: a pick
     *  closes the overlay; a clear keeps it open, so the empty choice shows
     *  and the next pick is near. */
    pickIcon(id) {
      this.roleForm.icon = this.roleForm.icon === id ? "" : id;
      if (this.roleForm.icon) this.closeArt();
      // an official borrow replaces any baked library pick
      if (this.roleForm.icon) {
        this.roleForm.iconData = "";
        this.roleForm.iconRef = "";
      }
    },
    // ── FT-856 slice B: the new-icon library ─────────────────────────────
    async openIconLibrary() {
      this.iconTab = "library";
      if (this.ilLoaded) return;
      this.$options.ilList = await iconLib.loadIcons();
      this.$options.ilThumbs = new Map();
      this.$options.ilBakes = new Map();
      this.ilLoaded = true;
    },
    /** Thumbnails build once each into a non-reactive cache.
     *  FT-1042: an SVG data URL, not a rasterized canvas — the whole feed
     *  renders in one pass now (no batching), and 1.3k canvas bakes in one
     *  paint was the hitch the batching existed to dodge. Building a string
     *  is free; the browser rasterizes each glyph only when its lazy img
     *  nears the viewport. */
    ilThumb(entry) {
      const cache = this.$options.ilThumbs;
      if (!cache.has(entry.n)) {
        const svg =
          '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512">' +
          '<g fill="#e8e4da">' +
          entry.d.map((d) => `<path d="${d}"/>`).join("") +
          "</g></svg>";
        cache.set(entry.n, "data:image/svg+xml," + encodeURIComponent(svg));
      }
      return cache.get(entry.n);
    },
    /** A pick bakes in the CURRENT team's tint; the ref rides the role so a
     *  later team switch re-bakes (rule: one stored bake, source kept).
     *  FT-1041b: it lands on the coin immediately — the feed stays open.
     *  (The FT-856 hover-preview pane retired with the tabs; the coin is
     *  the preview now, and a pick is one cheap click to undo.) */
    async pickLibraryIcon(entry) {
      const f = this.roleForm;
      if (f.iconRef === entry.n) {
        f.iconRef = "";
        f.iconData = "";
        return;
      }
      f.iconRef = entry.n;
      f.icon = "";
      // FT-1501: the pick closes the overlay; the bake lands on the coin
      this.closeArt();
      f.iconData = await iconLib.bakeIcon(entry, f.roleType, {
        seed: f.iconSeed || 0,
      });
    },
    async rerollIcon() {
      const f = this.roleForm;
      if (!f || !f.iconRef || !this.$options.ilList) return;
      const entry = iconLib.findIcon(this.$options.ilList, f.iconRef);
      if (!entry) return;
      f.iconSeed = 1 + Math.floor(Math.random() * 1e6);
      f.iconData = await iconLib.bakeIcon(entry, f.roleType, {
        seed: f.iconSeed,
      });
    },
    /** Team changed — re-print the bake in the new tint. */
    async rebakeForTeam() {
      const f = this.roleForm;
      if (!f || !f.iconRef) return;
      if (!this.$options.ilList)
        this.$options.ilList = await iconLib.loadIcons();
      const entry = iconLib.findIcon(this.$options.ilList, f.iconRef);
      if (!entry) return;
      f.iconData = await iconLib.bakeIcon(entry, f.roleType, {
        seed: f.iconSeed || 0,
      });
    },
    /** The bundled icon URL for an official role id. */
    /**
     * FT-1485: a LIBRARY entry's icon, resolved the way the official entries
     * resolve theirs (see botc): the script's own copy's baked engraving,
     * else the official art the row's `icon` borrows, else its team's token.
     */
    libIconUrl(scriptRole, icon, team) {
      if (scriptRole && scriptRole.golemIconData) {
        return scriptRole.golemIconData;
      }
      const borrowed = icon || (scriptRole && scriptRole.golemIcon);
      if (borrowed && this.$store.getters.rolesJSONbyId.has(borrowed)) {
        return this.iconUrl(borrowed);
      }
      const token = {
        townsfolk: "good",
        outsider: "outsider",
        minion: "minion",
        demon: "evil",
      }[team];
      return this.iconUrl(token || "custom");
    },
    iconUrl(id) {
      try {
        return require("../../assets/icons/" + id + ".png");
      } catch (e) {
        return require("../../assets/icons/custom.png");
      }
    },
    /** Save to the library (create/update/fork), then into the script. */
    async saveRoleForm() {
      const f = this.roleForm;
      // a token still sitting in the pill input counts — Enter was implied
      this.addReminderPill();
      // FT-1120: THE SPLIT THAT DESTROYED THE RULE IS GONE. The pills are the
      // storage, so the save serialises them rather than re-parsing a string:
      // a ruleless pill comes out as the bare string it always was, and a
      // rule-bearing one as `{name, deal}` — the shape dealReminders.js reads.
      const reminders = f.reminders
        .map(fromReminderPill)
        .filter((r) => (typeof r === "string" ? r : r.name));
      const reminderTexts = reminderNames(reminders);
      // honest inline validation, mirroring the server's bounds
      const nightsOk = [f.firstNight, f.otherNight].every(
        (n) => Number.isInteger(n) && n >= 0 && n <= 200,
      );
      const problem = !f.name.trim()
        ? "A role needs a name."
        : f.name.trim().length > 40
        ? "Name is limited to 40 characters."
        : !f.ability.trim()
        ? "A role needs an ability."
        : f.ability.trim().length > 600
        ? "Ability is limited to 600 characters."
        : f.shadowAbility.trim().length > 600
        ? "Shadow ability is limited to 600 characters."
        : !nightsOk
        ? "Night positions are whole numbers 0–200."
        : reminders.length > 20
        ? "At most 20 reminder tokens."
        : reminderTexts.some((r) => r.length > 40)
        ? "Reminder tokens are limited to 40 characters."
        : sectionsProblem(f.guideSections);
      if (problem) {
        this.roleError = problem;
        return;
      }
      // FT-1491: the sections, tidied the server's way (blank rows dropped)
      const sections = cleanSections(f.guideSections);
      // FT-1486 → FT-1501: the Lot's night words save ON THE LOT —
      // night.copy, every key the table edited in place (the sheet's two
      // labels among them) — never on the script. The rest of the Lot's
      // night rides back exactly as the row gave it.
      const nightCopy = sanitizeCopyEntry(f.nightCopy) || {};
      // FT-1538 (Clocktower's FT-1475 part 2): the step builder's half — the
      // Lot always carries its list; "wakes while dead" and the spent token
      // only once set (or already saved). The row's stored reminders and look
      // ride back through the rest untouched.
      const rest = { ...(f.nightRest || {}) };
      ["steps", "spentToken", "wakesWhenDead"].forEach((k) => delete rest[k]);
      const night = { ...rest, copy: nightCopy };
      night.steps = f.nightSteps
        .map(cleanStep)
        .filter(Boolean)
        .slice(0, NIGHT_STEPS_MAX);
      if (f.wakesDead !== f.wakesDeadBase || f.wakesDeadSaved) {
        night.wakesWhenDead = !!f.wakesDead;
      }
      if (f.spentToken || f.spentTokenSaved) night.spentToken = !!f.spentToken;
      // FT-1484: a library Lot this account owns takes the owner path — in
      // place, no key, the session cookie as the credential.
      if (this.libOwnedCheck) await this.libOwnedCheck;
      try {
        const { role, created, forked } = await roleLib.saveRole({
          sourceId: this.editingLibId,
          owned: this.editingLibOwned,
          name: f.name.trim(),
          roleType: f.roleType,
          ability: f.ability.trim(),
          // FT-1468: the Shadow ability saves through the SAME path — optional,
          // so a Light-only Lot sends an empty string (FT-1467 round-trips it).
          shadowAbility: f.shadowAbility.trim(),
          icon: f.icon || "",
          firstNight: f.firstNight,
          otherNight: f.otherNight,
          reminders,
          setup: f.setup,
          authorName: f.authorName.trim() || undefined,
          // FT-1484 step 2: Public / Private — never for an official, which
          // the server keeps public regardless
          ...(this.visibilityFixed ? {} : { visibility: f.visibility }),
          night,
          sections,
          // FT-1546: the card art — null takes it off the Lot
          cardArt: cardArtOf(f.cardArt),
        });
        this.roleShelf = roleLib.getRecents();
        // the library's "Yours" picks up the new Lot / its new visibility
        this.refreshMyRoles();
        const appRole = roleLib.toAppRole(role);
        // FT-1486: the Lot's own words ride the script snapshot too — from
        // the form, so they hold even while the server's row does not carry
        // them back yet
        if (Object.keys(night.copy).length) appRole.golemNightCopy = night.copy;
        else delete appRole.golemNightCopy;
        // FT-856: a baked icon rides the APP role (script snapshots carry
        // it); the server library keeps only official borrow ids.
        if (f.iconData) {
          appRole.golemIconData = f.iconData;
          appRole.golemIconRef = f.iconRef || "";
          appRole.golemIconSeed = f.iconSeed || 0;
          appRole.image = f.iconData;
        }
        // FT-1042: the art's fit rides the APP role, snapshot semantics like
        // the baked icon (the script carries it whole; the library row does
        // not). Only a non-stock fit writes fields — a role left at the
        // stock fit serializes exactly as before.
        if (f.artScale !== 1 || f.artX !== 0 || f.artY !== 0) {
          appRole.golemArtScale = sanArtScale(f.artScale);
          appRole.golemArtX = sanArtOffset(f.artX);
          appRole.golemArtY = sanArtOffset(f.artY);
        }
        // FT-1040 → FT-1538: the night rides the APP role, snapshot semantics
        // like the baked icon — the steps composed into golemNight (a WAKING
        // Lot with at least one step; none keeps it an ordinary unlisted
        // one, the free-text fallback), the reminders, the spent token, and
        // the whole night for the next edit. From the FORM's night, so it
        // holds even while the server's row does not carry it back yet.
        roleLib.applyRoleNight(appRole, night);
        // FT-1491: the sections ride the APP role from the FORM, so they hold
        // even while the server's row does not carry them back yet
        if (sections.length) appRole.golemSections = sections;
        else delete appRole.golemSections;
        // FT-1546: the card art rides the APP role from the FORM, the same way
        const cardArt = cardArtOf(f.cardArt);
        if (cardArt) appRole.golemCardArt = cardArt;
        else delete appRole.golemCardArt;
        // FT-1486: a Lot opened from the LIST (its pen, or Make a copy) that
        // this script doesn't use stays out of it — editing a Lot is not
        // adding it. Everything else lands in the script as before.
        const inScript = this.scriptRoles.some(
          (r) =>
            r.id === f.appId ||
            (appRole.golemRoleId && r.golemRoleId === appRole.golemRoleId),
        );
        if (this.formFromList && !inScript) {
          flashHint("Saved — every script that uses it gets your version");
        } else {
          this.insertRoleIntoEdition(appRole, f.appId);
          flashHint(
            forked
              ? "Forked into your own copy — script updated"
              : created
              ? "Saved to the role library — added to this script"
              : "Updated — script refreshed",
          );
        }
        this.closeRoleForm();
      } catch (e) {
        this.roleError = "Save failed: " + e.message;
      }
    },
    /** Click a shelf/browse row: fetch the role and add it to the script. */
    async addLibraryRole(id) {
      this.roleError = "";
      try {
        const role = await roleLib.loadRole(id);
        this.roleShelf = roleLib.getRecents();
        this.insertRoleIntoEdition(roleLib.toAppRole(role), null);
        flashHint(`${role.name} added to this script`);
      } catch (e) {
        this.roleError = e.message;
      }
    },
    /** FT-1484 step 2: the signed-in account's OWN Lots, public and private
     *  — "Yours" in the library lists them beside this browser's shelf, from
     *  any device. Quiet: a failure leaves the list as it was. */
    async refreshMyRoles() {
      if (!this.accountId) {
        this.myRoles = [];
        return;
      }
      try {
        // FT-1485: an official Lot's library row is already listed as the
        // built-in Lot (which carries the owner's edits), so it is left out
        // here rather than shown twice.
        this.myRoles = (
          await roleLib.browseRoles({ mine: true, limit: 50 })
        ).filter((r) => !r.officialId);
      } catch (e) {
        /* the shelf still lists what this browser made */
      }
    },
    /** FT-1484 step 2: the toggle's opening value — a Lot this browser holds
     *  the key to opens on its shelf's memory (the row's own lands right
     *  after, see openRoleForm); a new Lot, or a fork of someone else's,
     *  starts PRIVATE, as every new Lot does. */
    seedVisibility(role) {
      const libId = role && role.isCustom && role.golemRoleId;
      const shelved =
        libId && this.roleShelf.find((e) => e.id === libId && e.editKey);
      return shelved ? shelved.visibility || "public" : "private";
    },
    /** FT-1484 step 2: the row's visibility, landed after the form opened —
     *  FT-1501: without a clean form turning "unsaved". */
    applyRowVisibility(row) {
      const f = this.roleForm;
      if (!f || !row) return;
      const clean = !!this.formBaseline && !this.formDirty();
      f.visibility = row.visibility === "public" ? "public" : "private";
      f.officialRow = row.sourceType === "official";
      if (clean) this.markFormClean();
    },
    /** Browse the library (shelf rows are listed separately, so dedupe). */
    async searchRoles() {
      this.roleError = "";
      try {
        // FT-855: a single shown-only team narrows the library browse too.
        const inc = Object.keys(this.teamState).filter(
          (t) => this.teamState[t] === 1,
        );
        const type = inc.length === 1 ? inc[0] : "";
        const rows = await roleLib.browseRoles({
          q: this.roleQuery.trim(),
          type,
          limit: 20,
        });
        const shelfIds = new Set(this.roleShelf.map((e) => e.id));
        // FT-1485: official Lots already list as the built-in entry
        this.roleResults = rows.filter(
          (r) => !shelfIds.has(r.id) && !r.officialId,
        );
        this.browseDown = false;
      } catch (e) {
        // FT-981: a failed library browse is NOT an error banner.
        //
        // This runs on a 400ms debounce behind every keystroke, and it used to
        // set `roleError` — which paints large red text across the script
        // view. So with the library unreachable, typing anything at all
        // stamped "Browse failed" over the author's script, once per pause,
        // while the local search underneath was working perfectly.
        //
        // The browse SUPPLEMENTS a search over the official roles and the
        // local shelf; losing it narrows the haystack, it does not break the
        // box. Recorded quietly so the empty state can mention it if the
        // author ends up with nothing, and left out of the way otherwise.
        this.browseDown = true;
        this.roleResults = [];
      }
    },
    /**
     * Insert (or replace) a role in the CURRENT script — snapshot semantics,
     * saveToVault's collapse: official ids as {id} refs, custom roles whole
     * (minus the store's derived display field). An official edition growing
     * a custom role becomes a custom script.
     */
    insertRoleIntoEdition(appRole, replaceAppId) {
      const list = this.collapseScript();
      const at = list.findIndex(
        (r) =>
          (replaceAppId && r.id === replaceAppId) ||
          (appRole.golemRoleId && r.golemRoleId === appRole.golemRoleId) ||
          r.id === appRole.id,
      );
      // FT-1484: an in-place save keeps the id — drop the loaded copy so the
      // saved version is the one the script shows
      if (at > -1 && list[at].id === appRole.id) {
        this.$store.commit("forgetLoadedRole", appRole.id);
      }
      if (at > -1) list.splice(at, 1, appRole);
      else list.push(appRole);
      this.$store.commit("setCustomRoles", list);
      if (this.$store.state.edition.id !== "custom") {
        const meta = this.$store.state.edition;
        this.$store.commit("setEdition", {
          id: "custom",
          name: meta.name || "Custom script",
        });
      }
    },
    // ── Golem fork (FT-854): the workbench ───────────────────────────────
    /**
     * The current script, collapsed to its storable form: official roles as
     * {id} refs — CARRYING night-order overrides when they differ from the
     * base — custom roles whole minus the derived display field. One helper,
     * used by every mutation and by the vault save.
     */
    collapseScript(excludeAppId) {
      const base = this.$store.getters.rolesJSONbyId;
      const list = [];
      this.$store.state.roles.forEach((role) => {
        if (excludeAppId && role.id === excludeAppId) return;
        const b = base.get(role.id);
        if (b) {
          const ref = { id: role.id };
          if (role.firstNight !== b.firstNight)
            ref.firstNight = role.firstNight;
          if (role.otherNight !== b.otherNight)
            ref.otherNight = role.otherNight;
          list.push(ref);
        } else {
          const rest = { ...role };
          delete rest.imageAlt;
          // image is re-derived from golemIconData on load — one copy only
          if (rest.golemIconData) delete rest.image;
          list.push(rest);
        }
      });
      return list;
    },
    /**
     * setEdition (and friends) close the modal as a side effect — upstream's
     * flow ended there. The workbench keeps working, so re-open in place.
     */
    ensureOpen() {
      if (!this.$store.state.modals.edition)
        this.$store.commit("toggleModal", "edition");
      this.isCustom = true;
    },
    onScriptPick(card) {
      if (card.id === "__all") {
        // the whole book: every playable official (travellers stay town-side)
        const all = rolesJSON
          .filter((r) =>
            ["townsfolk", "outsider", "minion", "demon"].includes(
              normTeam(r.team),
            ),
          )
          .map((r) => ({ id: r.id }));
        this.$store.commit("setCustomRoles", all);
        this.$store.commit("setEdition", {
          id: "custom",
          name: "All of Blood on the Clocktower",
          logo: "__gold",
        });
        this.vaultSourceId = null;
        this.ensureOpen();
        this.setBaseline();
        return;
      }
      const edition = editionJSON.find((e) => e.id === card.id);
      if (edition) {
        this.$store.commit("setEdition", edition);
        this.vaultSourceId = null;
        this.ensureOpen();
        this.setBaseline();
      } else {
        this.loadFromVault(card.id).then(() => this.ensureOpen());
      }
    },
    /** FT-1043: New script IS the builder — no modal. An empty custom script
     *  lands in the pane: the stock mark, a blank name focused and ready,
     *  the night tabs live from the first moment; roles come in from the
     *  shelf. Naming and the icon live on the builder head now. */
    newScript() {
      // hand the pane back if the forge held it
      if (this.roleForm) this.closeRoleForm();
      this.$store.commit("setCustomRoles", []);
      this.$store.commit("setEdition", { id: "custom", name: "" });
      this.vaultSourceId = null;
      this.ensureOpen();
      this.setBaseline();
      this.$nextTick(() => this.$refs.bhName && this.$refs.bhName.focus());
      flashHint("A blank page — name it, and add roles from the shelf");
    },
    // ── FT-1043: the script icon (builder head + overlay) ────────────────
    /** Write the script's icon (edition.logo — travels in _meta on save).
     *  Empty returns it to the stock custom mark. */
    setScriptIcon(logo) {
      const meta = this.$store.state.edition;
      this.$store.commit("setEdition", {
        ...meta,
        id: "custom",
        logo: logo || undefined,
      });
      this.ensureOpen();
    },
    /** The overlay's grid pick: land it and close — one click. */
    pickScriptIcon(id) {
      this.setScriptIcon(id);
      this.iconPickOpen = false;
    },
    /** FT-1043b: a library pick runs the same bake a forge pick gets
     *  (shadedRender -> stylizeIcon) — neutral tint, matching how an
     *  uploaded image gets inked — so the script logo lands as a baked
     *  image, not a bare ref. Land it and close, same one click. */
    async pickScriptLibraryIcon(entry) {
      const data = await stylizeIcon(iconLib.shadedRender(entry), {
        tint: "neutral",
      });
      this.setScriptIcon(data);
      this.iconPickOpen = false;
    },
    async openIconPick() {
      this.nsIconSearch = "";
      this.iconPickOpen = true;
      // FT-1043b: the overlay now browses the icon library — load its
      // chunk once, into the same cache the forge feed uses.
      if (!this.ilLoaded) {
        this.$options.ilList = await iconLib.loadIcons();
        this.$options.ilThumbs = this.$options.ilThumbs || new Map();
        this.$options.ilBakes = this.$options.ilBakes || new Map();
        this.ilLoaded = true;
      }
    },
    // ── dirty tracking (Save/Discard live in the meter, only when dirty) ─
    setBaseline() {
      this.scriptBaseline = this.currentScriptSnapshot;
    },
    /** Back to the last loaded/saved state. */
    discardEdits() {
      if (!this.scriptBaseline) return;
      const b = JSON.parse(this.scriptBaseline);
      if (b.e !== "custom") {
        const edition = editionJSON.find((x) => x.id === b.e);
        if (edition) this.$store.commit("setEdition", edition);
      } else {
        this.$store.commit("setCustomRoles", b.r);
        this.$store.commit("setEdition", {
          id: "custom",
          name: b.n,
          logo: b.l || undefined,
          // FT-1403 phase 1: the baseline's night copy comes back with it
          nightCopy: b.c || undefined,
        });
      }
      this.ensureOpen();
      flashHint("Edits discarded");
    },
    /** The shelf hover card — RoleHoverCard pins itself beside the row and
     *  clamps to the viewport; all the shelf owns is which row. Hover only. */
    showRoleTip(e, entry) {
      if (!window.matchMedia("(hover: hover)").matches) return;
      this.roleTipAnchor = e.currentTarget;
      // FT-1491: the entry plus the sections its character carries, so the
      // card lists them (a list entry is a summary, not the character)
      this.roleTip = { ...entry, golemSections: this.entrySections(entry) };
    },
    /** FT-1491: the sections a list entry's character carries — an
     *  official's from the house overlay (on the bundled object), a library
     *  character's from the script's copy when the script has it, else its
     *  library row. */
    entrySections(entry) {
      if (!entry) return [];
      if (entry.official) {
        return sectionsOf(this.$store.getters.rolesJSONbyId.get(entry.id));
      }
      const inScript = this.scriptRoles.find(
        (r) => r.golemRoleId && r.golemRoleId === entry.libId,
      );
      if (inScript) return sectionsOf(inScript);
      const row = [...this.roleResults, ...this.myRoles].find(
        (r) => r.id === entry.libId,
      );
      return sectionsOf(row);
    },
    hideRoleTip() {
      this.roleTip = null;
      this.roleTipAnchor = null;
    },
    /** Sidebar click: in the script → out; not in it → in. */
    async toggleRole(entry) {
      this.roleError = "";
      if (entry.official) {
        if (entry.inScript) this.removeRole(entry.id);
        else {
          this.insertRoleIntoEdition({ id: entry.id }, null);
          this.ensureOpen();
        }
        return;
      }
      // library entries: the script carries snapshots keyed by golemRoleId
      if (entry.inScript) {
        const role = this.scriptRoles.find(
          (r) => r.golemRoleId === entry.libId,
        );
        if (role) this.removeRole(role.id);
      } else {
        await this.addLibraryRole(entry.libId);
        this.ensureOpen();
      }
    },
    // ── FT-1486: THE PEN IN THE LOT LIST ─────────────────────────────────
    /** May this list entry's pen edit it in place? A library Lot: `mine`
     *  (its row, or this browser's edit key). An official Lot: never here —
     *  L.O.T.S. has no house editing — so it offers a copy. */
    ownsEntry(entry) {
      return !entry.official && !!entry.mine;
    },
    entryActionTitle(entry) {
      return this.ownsEntry(entry) ? "Edit this Lot" : "Make a copy to edit";
    },
    /** The row's pen (yours) or copy (not yours). */
    entryAction(entry) {
      if (this.ownsEntry(entry)) this.editEntry(entry);
      else this.copyEntry(entry);
    },
    /** Open a Lot you own in its editor, from the list. */
    async editEntry(entry) {
      this.roleError = "";
      const inScript = this.scriptRoles.find(
        (r) => r.golemRoleId === entry.libId,
      );
      if (inScript) {
        this.openRoleForm(inScript);
        return;
      }
      const row = await roleLib.libraryRow(entry.libId);
      if (!row) {
        flashHint(`Couldn't open ${entry.name} — try again`);
        return;
      }
      this.openRoleForm({ ...roleLib.toAppRole(row), isCustom: true });
      this.formFromList = true;
    },
    /**
     * FT-1486: "Make a copy to edit" — a Lot you don't own becomes a new Lot
     * of your own through the existing fork path (a create with the source as
     * its parent: owned by the signed-in account, or — signed out — kept by
     * the edit key this browser shelves, as today), PRIVATE, then opens in
     * its editor. The script is not touched.
     */
    async copyEntry(entry) {
      if (this.copyingEntry) return;
      this.roleError = "";
      this.copyingEntry = entry.key;
      try {
        const source = entry.official
          ? this.officialForkSource(entry.id)
          : await this.libraryForkSource(entry.libId);
        const { role } = await roleLib.saveRole({
          ...source,
          authorName: localStorage.getItem("golem.playerName") || undefined,
          visibility: "private",
        });
        this.roleShelf = roleLib.getRecents();
        this.refreshMyRoles();
        this.openRoleForm({ ...roleLib.toAppRole(role), isCustom: true });
        this.formFromList = true;
        flashHint(`${role.name} is yours to edit — private until you share it`);
      } catch (e) {
        flashHint("Couldn't make a copy: " + e.message);
      } finally {
        this.copyingEntry = "";
      }
    },
    /** A copy's name: the original's, marked, inside the 40-character cap. */
    copyName(name) {
      const base = String(name || "").trim();
      const mark = " (copy)";
      return (
        (base.length + mark.length > 40
          ? base.slice(0, 40 - mark.length).trim()
          : base) + mark
      );
    },
    /** An official Lot as the fork's fields — as this game shows it, its
     *  library row (from the one officials read) as the parent. */
    officialForkSource(officialId) {
      const role = this.$store.getters.rolesJSONbyId.get(officialId);
      if (!role) throw new Error("unknown Lot");
      const row = houseRoles.cachedOfficialRow(officialId);
      // a seed only — where it wakes is each script's own night order
      const seed = (n) =>
        n > 0 ? Math.min(200, Math.max(1, Math.round(n))) : 0;
      return {
        sourceId: row ? row.id : null,
        name: this.copyName(role.name),
        roleType: roleLib.roleTypeFromTeam(role.team),
        ability: role.ability,
        shadowAbility: role.shadowAbility || "",
        icon: (row && row.icon) || role.imageAlt || role.id,
        firstNight: seed(role.firstNight),
        otherNight: seed(role.otherNight),
        reminders: Array.isArray(role.reminders) ? role.reminders.slice() : [],
        setup: !!role.setup,
        ...(row && row.night && typeof row.night === "object"
          ? { night: row.night }
          : {}),
      };
    },
    /** A library Lot as the fork's fields, read fresh. */
    async libraryForkSource(libId) {
      const row = await roleLib.libraryRow(libId);
      if (!row) throw new Error("the Lot couldn't be read");
      return {
        sourceId: row.id,
        name: this.copyName(row.name),
        roleType: row.roleType,
        ability: row.ability,
        shadowAbility: row.shadowAbility || "",
        icon: row.icon || "",
        firstNight: row.firstNight || 0,
        otherNight: row.otherNight || 0,
        reminders: Array.isArray(row.reminders) ? row.reminders : [],
        setup: !!row.setup,
        ...(row.night && typeof row.night === "object"
          ? { night: row.night }
          : {}),
      };
    },
    /** Remove one role from the current script (never blocks on composition). */
    removeRole(appId) {
      const list = this.collapseScript(appId);
      this.$store.commit("setCustomRoles", list);
      if (this.$store.state.edition.id !== "custom") {
        const meta = this.$store.state.edition;
        this.$store.commit("setEdition", {
          id: "custom",
          name: meta.name || "Custom script",
        });
      }
      this.ensureOpen();
    },
    // ── FT-857: the night-order drag lives in ScriptView; the WRITE stays
    //    here, where the store is owned. The view emits set-night with the
    //    already-computed midpoint (or 0 for "stops waking").
    onSetNight({ id, prop, value }) {
      this.setNight(id, prop, value);
    },
    /** Write one role's night number through the collapse (persists on the
     *  script entry — official refs carry it as an override). */
    setNight(appId, prop, value) {
      const list = this.collapseScript();
      const entry = list.find((r) => r.id === appId);
      if (!entry) return;
      entry[prop] = value;
      this.$store.commit("setCustomRoles", list);
      if (this.$store.state.edition.id !== "custom") {
        const meta = this.$store.state.edition;
        this.$store.commit("setEdition", {
          id: "custom",
          name: meta.name || "Custom script",
        });
      }
      this.ensureOpen();
    },
    // ── FT-854: the tri-state tag filter ─────────────────────────────────
    /** Everything provable about a shelf entry, as tag ids.
     *  FT-981: `allShelfEntries` calls this once per entry and keeps the
     *  result on `entry.tags`; every later caller reads that cache. */
    entryTags(entry) {
      if (entry.tags) return entry.tags;
      const tags = new Set(["team:" + entry.team]);
      if (entry.isLib) {
        tags.add(entry.mine ? "src:mine" : "src:lib");
      } else {
        if (["tb", "bmr", "snv"].includes(entry.edition))
          tags.add("src:" + entry.edition);
        else if (LUF_ROLES.has(entry.id)) tags.add("src:luf");
        else tags.add("src:exp");
        // FT-887: one definition of "wakes" (golem/nightInfo), so filtering
        // the shelf by "Wakes first night" returns exactly the characters
        // whose hover card carries that chip — Demons and Minions included,
        // whose night-one wake is the group step and not their own number.
        // Still exhaustive: every entry lands in exactly one of the three.
        const wakes = wakesOn(entry);
        if (wakes.first) tags.add("night:first");
        if (wakes.other) tags.add("night:other");
        if (!wakes.first && !wakes.other) tags.add("night:never");
        if (entry.setup) tags.add("flag:setup");
      }
      if (entry.inScript) tags.add("flag:inscript");
      return tags;
    },
    // ── FT-855: matchers ─────────────────────────────────────────────────
    /**
     * FT-981: NAME **AND** ABILITY, every token, in any order.
     *
     * It used to be `name.includes(query)` — one raw substring against one
     * field — and that is not how an author looks for a character. They think
     * in EFFECTS: "who poisons?", "who dies tonight?", "what nominates?".
     * Measured on the shipped build, `poison` returned the Poisoner and
     * nothing else — not Pukka, not the Vigormortis — `drunk` returned only
     * the Drunk, and `nominate` and `dies tonight` returned nothing at all.
     *
     * Tokens are ANDed, so word order stops mattering (`advocate devil` finds
     * the Devil's Advocate) and a two-word query can straddle the two fields
     * (`demon poison` finds the ability that does both). Both sides run
     * through `normalizeSearch`, which is what makes `devils advocate` and
     * `pit hag` findable.
     */
    matchesSearch(entry) {
      const tokens = this.searchTokens;
      if (!tokens.length) return true;
      const hay = entry.search || normalizeSearch(entry.name);
      return tokens.every((t) => hay.includes(t));
    },
    /** FT-981: 1 when every query token is in the NAME, 0 otherwise — the
     *  sort's "you probably meant this one" tier. 0 for everything when no
     *  query is set, which leaves the resting order alphabetical. */
    nameHitRank(entry) {
      const tokens = this.searchTokens;
      if (!tokens.length) return 0;
      const name = entry.searchName || normalizeSearch(entry.name);
      return tokens.every((t) => name.includes(t)) ? 1 : 0;
    },
    matchesTeams(entry) {
      if (this.teamState[entry.team] === -1) return false;
      const inc = Object.keys(this.teamState).filter(
        (t) => this.teamState[t] === 1,
      );
      return !inc.length || inc.includes(entry.team);
    },
    /** Pills: includes OR within a facet, AND across facets; every is-not
     *  pill excludes. excludeFacet skips one facet's pills (its own counts). */
    matchesPills(entry, excludeFacet) {
      const active = this.pills.filter((p) => p.facet !== excludeFacet);
      if (!active.length) return true;
      const tags = this.entryTags(entry);
      if (active.some((p) => p.not && tags.has(p.id))) return false;
      const facets = [
        ...new Set(active.filter((p) => !p.not).map((p) => p.facet)),
      ];
      return facets.every((f) =>
        active.some((p) => !p.not && p.facet === f && tags.has(p.id)),
      );
    },
    // ── FT-855: controls ─────────────────────────────────────────────────
    /** show only → hide → off, on the button itself (user call). Hiding
     *  ALL four teams is a dead end that reads as "no filter, no roles" —
     *  it auto-resets to neutral (everything shows). */
    toggleTeam(team) {
      const next = { ...this.teamState };
      if (next[team] === 1) next[team] = -1;
      else if (next[team] === -1) delete next[team];
      else next[team] = 1;
      const teams = ["townsfolk", "outsider", "minion", "demon"];
      if (teams.every((t) => next[t] === -1)) this.teamState = {};
      else this.teamState = next;
    },
    teamTitle(t) {
      const s = this.teamState[t.team];
      return (
        t.label +
        (s === 1
          ? " — showing only (click to hide)"
          : s === -1
          ? " — hidden (click to reset)"
          : " — click to show only")
      );
    },
    /**
     * Typing (debounced) asks the community library too — the Browse button
     * retired. (FT-1039: the filter box this used to be careful not to
     * auto-open dissolved into the always-visible chip rows.)
     */
    onSearchInput() {
      clearTimeout(this.__searchDebounce);
      this.__searchDebounce = setTimeout(() => {
        if (this.roleQuery.trim()) this.searchRoles();
        else this.roleResults = [];
      }, 400);
    },
    /** FT-981: the empty state's two ways out, each undoing only its own
     *  half — clearing a search must not silently drop the author's filters. */
    clearSearch() {
      this.roleQuery = "";
      this.roleResults = [];
      clearTimeout(this.__searchDebounce);
    },
    clearFilters() {
      this.pills = [];
      this.teamState = {};
    },
    pillFor(id) {
      return this.pills.find((p) => p.id === id) || null;
    },
    // ── FT-1039: the chip rows (pills stay the single truth underneath) ──
    /** 1 shown, -1 hidden, 0 neutral — the states the team row taught. */
    chipState(id) {
      const p = this.pillFor(id);
      return p ? (p.not ? -1 : 1) : 0;
    },
    chipClass(tag) {
      const s = this.chipState(tag.id);
      return {
        on: s === 1,
        exc: s === -1,
        zero: !s && this.countFor(tag) === 0,
      };
    },
    /** The FULL label lives here — the chip face wears a mark or one word. */
    chipTitle(tag) {
      const s = this.chipState(tag.id);
      return (
        tag.label +
        (s === 1
          ? " — showing only (click to hide)"
          : s === -1
          ? " — hidden (click to reset)"
          : "")
      );
    },
    chipWord(tag) {
      return CHIP_WORDS[tag.id] || tag.label;
    },
    /** An edition's mark, for the chips of the editions that have one. */
    editionMark(id) {
      return EDITION_ICONS[id.replace("src:", "")] || "";
    },
    nightMark(id) {
      if (id === "night:first") return moonFirst;
      if (id === "night:other") return moonOther;
      return "";
    },
    /** Multi-select rows cycle neutral → shown → hidden → neutral. */
    cycleChip(tag) {
      const existing = this.pillFor(tag.id);
      if (!existing)
        this.pills = [
          ...this.pills,
          { id: tag.id, facet: this.facetKeyOf(tag.id), not: false },
        ];
      else if (!existing.not)
        this.pills = this.pills.map((p) =>
          p === existing ? { ...p, not: true } : p,
        );
      else this.pills = this.pills.filter((p) => p !== existing);
    },
    /** The night row is a LENS — one at a time, click the lit one to clear. */
    toggleLens(tag) {
      const had = this.pillFor(tag.id);
      const rest = this.pills.filter((p) => p.facet !== "night");
      this.pills = had
        ? rest
        : [...rest, { id: tag.id, facet: "night", not: false }];
    },
    facetKeyOf(id) {
      const g = TAG_GROUPS.find((g) => g.tags.some((t) => t.id === id));
      return g ? g.key : "";
    },
    pillValueLabel(pill) {
      for (const g of TAG_GROUPS)
        for (const t of g.tags) if (t.id === pill.id) return t.label;
      return pill.id;
    },
    /** Live count for a facet value — read from the single-pass tally. */
    countFor(tag) {
      return this.facetCounts[tag.id] || 0;
    },
    /** A script logo: official role id, uploaded data URL, or the gold mark. */
    scriptLogoSrc(logo) {
      if (logo === "__gold") return goldLogo;
      return logo && logo.startsWith("data:") ? logo : this.iconUrl(logo);
    },
    /** FT-1546: open the picker for the Lot's card art. */
    pickCardArt() {
      if (this.cardArtBusy || !this.accountId) return;
      this.cardArtError = "";
      const input = this.$refs.cardArtInput;
      if (input) input.click();
    },
    /** FT-1546: a picture picked — up to the asset store, then onto the form
     *  (Save puts it on the Lot). A failure keeps whatever art was there and
     *  says why, in the upload's own words. */
    async onCardArtFile(e) {
      const input = e.target;
      const file = input.files && input.files[0];
      input.value = "";
      if (!file || !this.roleForm) return;
      const form = this.roleForm;
      this.cardArtBusy = true;
      this.cardArtError = "";
      try {
        const art = await uploadCardArt(file);
        // the form this upload was for may have closed meanwhile
        if (this.roleForm === form) form.cardArt = art;
      } catch (err) {
        if (this.roleForm === form) {
          this.cardArtError = (err && err.message) || "The upload failed";
        }
      } finally {
        this.cardArtBusy = false;
      }
    },
    /** FT-1546: take the card art off — the bare tarot card comes back. */
    removeCardArt() {
      if (!this.roleForm || this.cardArtBusy) return;
      this.roleForm.cardArt = null;
      this.cardArtError = "";
    },
    onNsUpload(e) {
      const file = e.target.files && e.target.files[0];
      if (file) this.iconIntakeFile(file);
      e.target.value = "";
    },
    /** An image dropped straight onto the builder head's icon (FT-1043) —
     *  the upload path without opening the overlay at all. */
    onIconDrop(e) {
      this.iconDragOver = false;
      const file =
        e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (file) this.iconIntakeFile(file);
    },
    /** Downscale any dropped/uploaded image into a 128px data URL — small
     *  enough to travel inside the script's _meta — and run the FT-856
     *  stylizer over it. Inked lands on the script at once; the overlay's
     *  toggle keeps Original a click away. */
    iconIntakeFile(file) {
      if (!/^image\//.test(file.type)) {
        flashHint("That file isn't an image");
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        const img = new Image();
        img.onload = async () => {
          const SIZE = 128;
          const canvas = document.createElement("canvas");
          canvas.width = SIZE;
          canvas.height = SIZE;
          const g = canvas.getContext("2d");
          const scale = Math.min(SIZE / img.width, SIZE / img.height);
          const w = img.width * scale;
          const h = img.height * scale;
          g.drawImage(img, (SIZE - w) / 2, (SIZE - h) / 2, w, h);
          const original = canvas.toDataURL("image/png");
          let styled = "";
          try {
            // stylize from the FULL-RES source, not the 128 downscale
            styled = await stylizeIcon(reader.result, { tint: "neutral" });
          } catch (e) {
            styled = "";
          }
          // the script may have changed under a slow decode — write only
          // while the builder still holds the pane
          if (!this.builderOpen) return;
          this.iconUp = { original, styled };
          this.setScriptIcon(styled || original);
        };
        img.onerror = () => flashHint("Could not read that image");
        img.src = reader.result;
      };
      reader.readAsDataURL(file);
    },
    // FT-857: roleIconUrl moved into ScriptView with the cards it paints.
    /**
     * Mark (or clear) a vault script's non-conforming flag — DERIVED from the
     * meter at load/save time, stored so the selector can badge without
     * fetching. Marking is the whole enforcement: play proceeds regardless.
     */
    markNC(scriptId) {
      if (!scriptId) return;
      const nc = !this.servableCounts.length;
      const map = { ...this.ncMap };
      if (nc) map[scriptId] = true;
      else delete map[scriptId];
      this.ncMap = map;
      localStorage.setItem("golem.scriptNC", JSON.stringify(map));
    },
    // ── upstream methods ─────────────────────────────────────────────────
    /** Load a script array: its `_meta` entry becomes the edition. */
    parseRoles(roles) {
      if (!roles || !roles.length) return;
      // captured BEFORE the commits below auto-close the modal — a silent
      // ?script= arrival (modal never open) must stay silent
      const wasOpen = this.$store.state.modals.edition;
      roles = roles.map((role) =>
        typeof role === "string" ? { id: role } : role,
      );
      const meta = roles.find((role) => role.id === "_meta") || {};
      roles = roles.filter((role) => role !== meta);
      this.$store.commit("setCustomRoles", roles);
      this.$store.commit(
        "setEdition",
        Object.assign({}, meta, { id: "custom" }),
      );
      const knownFabled = this.$store.state.fabled;
      const fabledKey = (entry) => entry.id || entry;
      if (roles.some((entry) => knownFabled.has(fabledKey(entry)))) {
        const fabled = [];
        roles.forEach((entry) => {
          const key = fabledKey(entry);
          if (knownFabled.has(key)) {
            fabled.push(knownFabled.get(key));
          }
        });
        // the script's fabled replace the whole line-up
        this.$store.commit("players/setFabled", { fabled });
      }
      // Golem fork (FT-854): loading a script LANDS IN the workbench (the
      // setEdition side effect above closed the modal; upstream also bounced
      // back to the tiles). A silent ?script= auto-load — modal never open —
      // stays silent. A parsed load is pristine until edited.
      if (wasOpen) this.ensureOpen();
      this.setBaseline();
    },
    ...mapMutations(["toggleModal", "setEdition"]),
  },
};
</script>

<style scoped lang="scss">
// FT-981: the shared control tokens, for $grimoire-plum — the grimoire cover's
// own darkest purple, worn here by the bench's two + buttons. Variables and
// mixins only, so importing it adds no rules to this sheet.
@import "../../controls.scss";
// FT-1188: the big surface's shared chrome — this title, and the head rows
// below. The Chronicle reads the same file.
@import "../../surface.scss";
// FT-1501: the drawers' glass (face-disc-menu-plate, by way of drawer.scss,
// which reads faceDisc) and the phone test, for the Lot editor — mixins only
@import "../../drawer.scss";
// Golem fork: the surface title — one word, centred, with its hotkey letter as
// a blood drop-cap. FT-1188 moved the declarations to `surface.scss` so the
// Chronicle wears the same treatment rather than a second copy of it.
//
// The `.blood-cap-a` sizing that used to live here is STOOD DOWN with the
// `almanacCap` computed that fed it (see the script): the cap is a KeyCap now
// and KeyCap sizes its own art from the asset metrics.
.almanac-title {
  @include surface-title;
}

// both hidden intakes (script file, script icon) live in the workbench
.custom input[type="file"] {
  display: none;
}

// Golem fork (FT-851): the custom-role form + library browser.
.role-form,
.role-library {
  input:not([type="checkbox"]),
  select,
  textarea {
    background: rgba(0, 0, 0, 0.5);
    color: white;
    border: 1px solid #666;
    border-radius: 4px;
    padding: 4px 6px;
    margin: 3px;
    font-size: 14px;
    font-family: inherit;
  }
  .row {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
  }
  textarea,
  input.wide {
    width: 70%;
  }
  input[type="number"] {
    width: 60px;
  }
}
.role-form .nights small {
  opacity: 0.6;
  margin-left: 6px;
}
.icon-picker {
  margin: 5px auto;
  .icon-grid {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    max-height: 150px;
    overflow-y: auto;
    margin: 5px auto;
    width: 90%;
    border: 1px solid #444;
    border-radius: 6px;
    background: rgba(0, 0, 0, 0.3);
  }
  .icon-cell {
    width: 72px;
    padding: 4px 0;
    cursor: pointer;
    border-radius: 6px;
    .icon {
      display: block;
      width: 40px;
      height: 40px;
      margin: 0 auto;
      background-size: cover;
      background-position: center;
    }
    .label {
      display: block;
      font-size: 10px;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      padding: 0 3px;
    }
    &:hover {
      background: rgba(255, 0, 0, 0.25);
    }
    &.selected {
      outline: 2px solid red;
    }
  }
}
.role-error {
  color: #ff6b6b;
  margin: 5px;
  font-weight: bold;
}

// ── Golem fork (FT-854): the workbench ─────────────────────────────────
$team-colors: (
  "townsfolk": #1f65ff,
  "outsider": #46d5ff,
  "minion": #ff6900,
  "demon": #ce0100,
  "traveler": #cc04ff,
);

// FT-1040: the forge speaks chips — the FT-1039 idiom (one plate for grouped
// cells, standalone chips beside it, the shared lit state), scoped to the
// forge overlay. The checkboxes these replaced were retired by that pass.
//
// FT-1041b: ONE FORM COLUMN under the living coin — identity, then the Tags
// chip row (wakes + setup) with the composer beneath, then reminder pills —
// and the ENTIRE right side is the art feed, always open, each region
// scrolling on its own. Below ~1024px viewport the two stack (form above
// feed) and the pane scrolls as one.
.role-form .forge-head {
  position: sticky;
  top: 0;
  z-index: 6;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
  margin-bottom: 4px;
  padding-bottom: 4px;
  // FT-1501: the drawer is glass, and the head scrolls with nothing (the
  // column scrolls under its own top) — no plate of its own. Save/Discard
  // moved to the shell's corner, which still sits over this head (Modal's
  // .top-right-buttons), wider than the plain × once dirty: room is kept so
  // a long title never runs under the pair.
  background: transparent;
  padding-right: 170px;
  .forge-title {
    margin: 0;
  }
  // the base .forge-acts (overlay era) floats at height 0 — in the head row
  // it is an ordinary flex item again
  .forge-acts {
    position: static;
    height: auto;
    display: flex;
    gap: 6px;
    margin: 0;
    .button {
      margin: 0;
    }
  }
}
.role-form .forge-cols {
  display: flex;
  align-items: stretch;
  gap: 14px;
  flex: 1 1 auto;
  min-height: 0;
  // the ONE form column — FT-1501: the drawer's whole width (the drawer's
  // own minimum keeps the Tags row's three chips on one line), its own scroll
  .forge-col.fc-identity {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
    overflow-y: auto;
    padding-right: 2px;
  }
}
// FT-1501 (Clocktower's FT-1476): the corner's own plate. The corner slot
// renders into Modal's own chrome, outside .custom.workbench, so this is a
// small self-contained copy of the purple-plate-over-flat-buttons idiom (it
// targets .corner-acts directly, which still carries THIS component's scope
// attribute wherever it lands in Modal's DOM).
.corner-acts {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 3px 6px;
  border: 1px solid $grimoire-plum;
  border-radius: 8px;
  background: rgba(8, 8, 10, 0.9);
  .button {
    margin: 0;
    padding: 3px 10px;
    border: 1px solid #3d3d3d;
    background: rgba(0, 0, 0, 0.65);
    box-shadow: none;
    font-weight: normal;
    font-size: 13px;
    line-height: 1.6;
    color: #e8e4da;
    cursor: pointer;
    &:before,
    &:after {
      content: none;
    }
    &:hover,
    &:focus-visible {
      border-color: #6a6a6a;
      outline: none;
    }
  }
}
// the LOUDER save — the plate's primary act: filled plum, bone-white words,
// a ring on keyboard focus
.corner-acts .button.forge-save {
  background: $grimoire-plum;
  border-color: lighten($grimoire-plum, 22%);
  color: #fff;
  font-weight: bold;
  &:hover,
  &:focus-visible {
    background: lighten($grimoire-plum, 8%);
    color: #fff;
    outline: none;
    box-shadow: 0 0 0 2px rgba(185, 166, 224, 0.6);
  }
}
// FT-1501: THE STAGE BAR. It leaves the drawer: the real table is the
// preview, in the room right of the dock (the unscoped rule below moves it
// there); only the bar is ours, floating along its foot.
.role-form .forge-stage {
  position: fixed;
  left: var(--studio-dock, 520px);
  right: 0;
  bottom: 14px;
  z-index: 5;
  display: flex;
  justify-content: center;
  pointer-events: none;
}
// in a town the table keeps its own session strip along the foot; the bar
// (and the art above it) lift clear of it
.role-form .forge-stage.in-town {
  bottom: 60px;
}
.role-form .stage-bar {
  pointer-events: auto;
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 6px 10px;
  border: 1px solid $grimoire-plum;
  border-radius: 10px;
  background: rgba(8, 8, 10, 0.92);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.7);
}
// the stepper's dot — hollow where the state/view shows this Lot's night
// text on its default, filled where some is your words
.role-form .stage-bar .stage-dot {
  display: inline-block;
  box-sizing: border-box;
  width: 7px;
  height: 7px;
  margin-left: 5px;
  vertical-align: middle;
  border: 1.5px solid #e6c27a;
  border-radius: 50%;
  background: transparent;
  &.own {
    background: #e6c27a;
  }
}
// where the preview can't run, the bar holds one line saying why
.role-form .stage-bar.stage-note {
  padding: 8px 14px;
  font-size: 13px;
  font-style: italic;
  color: rgba(255, 255, 255, 0.72);
  .stage-resume {
    font-style: normal;
  }
}
// FT-1501: THE ART OVERLAY — a glass panel over the right side, above the
// table and clear of the stage bar at its foot; the feed inside scrolls on
// its own. Fixed, so it is placed against the window (the drawer carries no
// filter — see .forge-drawer), right of the drawer at its live width.
.role-form .forge-art {
  position: fixed;
  z-index: 7;
  top: 16px;
  bottom: 72px;
  left: calc(var(--studio-dock, 520px) + 16px);
  right: 16px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px 14px 12px;
  text-align: left;
  @include face-disc-menu-plate($r: 900px, $radius: 10px);
  .art-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }
  .art-title {
    margin: 0;
    font-size: 20px;
  }
  .art-close {
    flex: none;
    margin: 0;
    padding: 2px;
    border: 0;
    border-radius: 6px;
    background: none;
    line-height: 0;
    cursor: pointer;
    .close-mark {
      width: 26px;
      height: auto;
    }
    &:focus-visible {
      outline: 2px solid rgba(185, 166, 224, 0.8);
      outline-offset: 2px;
    }
  }
  .forge-feed {
    flex: 1 1 0;
    min-height: 0;
    overflow-y: auto;
  }
  &.in-town {
    bottom: 118px;
  }
  // a phone's drawer is a bottom sheet: the art takes the top of the screen
  @media #{$phone-sheet} {
    top: 8px;
    left: 8px;
    right: 8px;
    bottom: auto;
    height: 46vh;
  }
}
// the drawer's grip on its RIGHT edge — the right-hand drawers' `.sd-grip`,
// mirrored (drawer.scss owns that one's look)
.role-form .ed-grip {
  position: absolute;
  top: 0;
  bottom: 0;
  right: 0;
  z-index: 8;
  // FT-1530 (ported, FT-1535): 4px, not 7 — the blood-drip line rides 5px
  // from this edge (its trail ink ends ~5.7px in, its drop's ink ~4.7px in),
  // so a 4px grip leaves every red pixel on the SCROLL side: hovering the
  // line grabs and drags the form, the outer 4px resizes. The grip (z 8,
  // above the track's z 6) wins the overlap with the drip's grab strip.
  width: 4px;
  cursor: ew-resize;
  background: transparent;
  transition: background 150ms;
  // the drag must not be read as a page pan (golem/rightDrawer's note)
  touch-action: none;
  &:hover,
  &:active,
  &:focus-visible {
    background: rgba(150, 130, 175, 0.45);
    outline: none;
  }
  @media #{$phone-sheet} {
    display: none;
  }
}
// the Tags row — the wakes plate and the setup chip, one inline row
.role-form .forge-tags {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  // the row's three chips share one line at the column's width
  .fg-wakes .forge-cell {
    padding: 4px 8px;
  }
}
// the FEED's furniture — sticky search bar, sticky group mini-headers, an
// auto-fill grid that takes as many icons per row as the width gives
.role-form .forge-feed {
  .feed-top {
    position: sticky;
    top: 0;
    z-index: 5;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 2px 2px 6px;
    background: rgba(8, 8, 10, 0.97);
    .feed-search {
      flex: 1 1 auto;
      min-width: 0;
      margin: 0;
    }
    .feed-source {
      flex: none;
    }
    .ip-current {
      display: inline-flex;
      align-items: center;
      gap: 5px;
      img {
        width: 30px;
        height: 30px;
      }
      .ip-reroll {
        cursor: pointer;
        opacity: 0.7;
        font-size: 12px;
        &:hover {
          opacity: 1;
          color: #ff6b6b;
        }
      }
    }
  }
  .feed-head {
    position: sticky;
    top: 40px;
    z-index: 4;
    background: rgba(8, 8, 12, 0.95);
    font-size: 12px;
    text-transform: uppercase;
    letter-spacing: 1.5px;
    opacity: 0.8;
    padding: 4px 6px 2px;
    small {
      opacity: 0.6;
      letter-spacing: 0;
    }
  }
  .feed-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(76px, 1fr));
    gap: 2px;
    padding: 4px 0 8px;
    .icon-cell {
      padding: 4px 0;
      cursor: pointer;
      border-radius: 6px;
      text-align: center;
      .icon {
        display: block;
        width: 40px;
        height: 40px;
        margin: 0 auto;
        background-size: cover;
        background-position: center;
      }
      .il-thumb {
        display: block;
        width: 40px;
        height: 40px;
        margin: 0 auto;
        opacity: 0.9;
      }
      .label {
        display: block;
        font-size: 10px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        padding: 0 3px;
      }
      &:hover {
        background: rgba(255, 0, 0, 0.25);
      }
      &.selected {
        outline: 1px solid #a01414;
        background: rgba(160, 20, 20, 0.14);
      }
    }
  }
  .feed-none {
    padding: 14px;
    font-size: 13px;
    opacity: 0.6;
    text-align: center;
  }
  .il-loading {
    padding: 12px;
    opacity: 0.7;
  }
}
// the LIVE COIN — the real Token, sized like a generous seat. FT-1042: the
// WRAPPER is a control now (drag places the art); the Token inside stays
// inert so the preview neither hovers a card nor clicks.
.role-form .forge-coin {
  width: 172px;
  height: 172px;
  margin: 0 auto;
  cursor: grab;
  touch-action: none;
  &.placing {
    cursor: grabbing;
  }
  // FT-1501: the coin is a door to the art too — reachable by keyboard
  &:focus-visible {
    outline: 2px solid rgba(185, 166, 224, 0.8);
    outline-offset: 4px;
    border-radius: 50%;
  }
  .token {
    width: 100%;
    height: 100%;
    pointer-events: none;
  }
}
// FT-1042: the art's dials — the size scrub and the reset chip, one quiet
// row under the coin
.role-form .art-dials {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  .forge-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 1.4px;
    opacity: 0.55;
  }
  // FT-1126: the X and Y labels sit closer to their own dial than to the
  // pair before them, so the row reads as three groups rather than seven
  // loose things.
  .ad-gap {
    margin-left: 6px;
  }
  .ad-unit {
    font-size: 11px;
    opacity: 0.55;
  }
  .ad-reset {
    margin-left: 4px;
  }
}
// ONE plated ground for every group — the composer's own plate, promoted to
// the shared idiom; fields inside take the column's one width
// FT-1494: THE EDITABLE CARD's place — the card, then its chips (author,
// visibility, + Section) under it. FT-1498: + the section panel below that.
.role-form .forge-card {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.role-form .card-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  .cm-author.hint {
    font-style: italic;
    opacity: 0.7;
  }
  .cm-author-input {
    flex: 0 1 240px;
    min-width: 0;
    font-size: 13px;
  }
  .cm-vis-fixed {
    font-size: 12px;
    font-style: italic;
    color: rgba(255, 255, 255, 0.6);
  }
  // FT-1498: "+ Section" rides the same chip row as author/visibility
  .cm-add-section {
    margin-left: auto;
    &:disabled {
      opacity: 0.4;
      cursor: not-allowed;
    }
  }
}
// FT-1498 (user: "the add section probably shouldn't go on the card itself"
// -> "move '+ Section' and section editing under the card, keeping only
// section names on the card"): the selected section's own editor, under the
// chip row — title, text, then its tools (quick titles, who reads it, "Only
// with…", move / remove — FT-1491's Sections group, moved off the card).
.role-form .card-sec-panel {
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding: 10px;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid #2c2c2c;
  border-radius: 8px;
}
.role-form .cs-head {
  display: flex;
  align-items: center;
  gap: 6px;
}
// FT-1498: sizing only — the app's own input/textarea chrome (App.vue's
// dark plate + blood-red focus glow) already applies app-wide.
.role-form .cs-title-input {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
}
.role-form .cs-close {
  flex-shrink: 0;
}
.role-form .cs-text-input {
  width: 100%;
  box-sizing: border-box;
  font-size: 14px;
  line-height: 1.35;
  resize: vertical;
}
.role-form .card-sec-tools {
  display: flex;
  flex-direction: column;
  gap: 6px;
  font-size: 13px;
  .fgi-acts {
    display: flex;
    gap: 5px;
  }
  .fgi-remove {
    margin-left: auto;
  }
  .fgi-move {
    min-width: 0;
    padding: 4px 7px;
    flex-shrink: 0;
    &:disabled {
      opacity: 0.35;
      cursor: not-allowed;
    }
  }
  .fgi-picks {
    display: flex;
    flex-wrap: wrap;
    gap: 4px;
    .fgi-pick {
      padding: 2px 8px;
      font-size: 12px;
    }
  }
  .fgi-foot {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 8px;
  }
  .fgi-with-label {
    font-size: 12px;
    opacity: 0.6;
  }
  .fgi-with {
    flex: 1 1 120px;
    min-width: 0;
  }
}
// FT-1494: a finger needs a 40px target on the card's chips and controls.
// FT-1498: + the panel's own title input and close button.
@media (pointer: coarse) {
  .role-form .card-meta .forge-chip,
  .role-form .card-meta .forge-cell,
  .role-form .card-meta .cm-author-input,
  .role-form .card-sec-panel .cs-title-input,
  .role-form .card-sec-panel .cs-close,
  .role-form .card-sec-tools .forge-chip,
  .role-form .card-sec-tools .forge-cell,
  .role-form .forge-card .team-pick .team-btn {
    min-height: 40px;
  }
  .role-form .card-sec-tools .fgi-with ::v-deep .cp-trigger {
    min-height: 40px;
  }
}
// FT-1546: the card art — the real card on the left, its controls beside it
.role-form .fg-cardart {
  .ca-body {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    gap: 12px;
  }
  .ca-card {
    flex: 0 0 auto;
  }
  .ca-controls {
    flex: 1 1 140px;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    min-width: 0;
    .forge-chip:disabled {
      opacity: 0.5;
      cursor: progress;
    }
  }
  .ca-note {
    margin: 0;
    font-size: 12px;
    line-height: 1.4;
    color: rgba(255, 255, 255, 0.6);
  }
  .ca-error {
    color: #e79a8f;
  }
  .ca-file {
    display: none;
  }
}
.role-form .forge-group {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 5px;
  padding: 10px 12px;
  background: rgba(0, 0, 0, 0.25);
  border: 1px solid #2c2c2c;
  border-radius: 8px;
  text-align: left;
  .forge-label {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 1.4px;
    opacity: 0.55;
    margin-top: 3px;
    &:first-child {
      margin-top: 0;
    }
  }
  input:not([type="checkbox"]),
  textarea {
    width: 100%;
    box-sizing: border-box;
    margin: 0;
  }
}
// one plate, cells inside — the workbench night lens's own construction
.role-form .forge-seg {
  display: inline-flex;
  background: rgba(0, 0, 0, 0.45);
  border: 1px solid #3d3d3d;
  border-radius: 6px;
  overflow: hidden;
  .forge-cell {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 11px;
    background: transparent;
    border: 0;
    border-right: 1px solid $control-divider;
    color: rgba(255, 255, 255, 0.75);
    font-family: inherit;
    font-size: 13px;
    cursor: pointer;
    white-space: nowrap;
    &:last-child {
      border-right: 0;
    }
    .moon {
      width: 14px;
      height: 14px;
      object-fit: contain;
      opacity: 0.9;
    }
    &:hover {
      background: rgba(255, 255, 255, 0.08);
    }
    &:focus-visible {
      @include control-focus-ring;
    }
    &.on {
      background: $control-on-bg;
      color: $control-on-color;
    }
  }
}
// a standalone chip — the workbench facet chip's plate and lit state
// FT-1043: the pane speaks the same chip — the builder head's Import door
// and the import view's Back wear the forge's idiom verbatim.
.role-form .forge-chip,
.wb-pane .forge-chip {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: rgba(0, 0, 0, 0.45);
  border: 1px solid #3d3d3d;
  border-radius: 6px;
  color: rgba(255, 255, 255, 0.75);
  font-family: inherit;
  font-size: 13px;
  cursor: pointer;
  &:hover {
    border-color: #666;
  }
  &:focus-visible {
    @include control-focus-ring;
  }
  &.on {
    border-color: $control-on-edge;
    background: $control-on-bg;
    color: $control-on-color;
  }
  /* FT-1242: the deal-rule chip's baked hand, sized to the chip's text. */
  .rem-deal-mark {
    width: 15px;
    height: 15px;
    object-fit: contain;
  }
}
// FT-1040: the night action composer — visible only while a wakes chip is
// lit. FT-1040c: it lives INSIDE the wakes group's plate now, so it wears a
// seam instead of its own box (one plated group, not a box in a box).
.role-form .night-composer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  margin: 5px 0 0;
  padding: 8px 0 0;
  border-top: 1px solid #2c2c2c;
  .nc-title {
    font-size: 13px;
    opacity: 0.8;
  }
  .nc-shapes {
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    gap: 4px;
  }
  .nc-dials {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .row {
    width: 100%;
    margin: 0;
  }
  // FT-1410: the row's two labels, stacked under the prompt they belong to.
  // FULL WIDTH rather than side by side: each box's placeholder names the
  // slot AND the shipped word it stands in for ("Seat label — Player:"), and
  // half a plate clips that to nonsense.
  .nc-labels {
    display: flex;
    flex-direction: column;
    gap: 4px;
    input {
      width: 100%;
    }
  }
}
// FT-1538: THE NIGHT PANEL — Clocktower's step builder (FT-1475 / FT-1514 /
// FT-1529), ported. NO BOX INSIDE A BOX: the group's own plate is the only
// frame; a step is separated from the next by a hairline and air.
.role-form .fg-wakeline {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  .fgw-word {
    font-size: 13px;
    opacity: 0.75;
    margin-right: 2px;
  }
  .forge-chip {
    padding: 4px 9px;
  }
  .moon {
    height: 13px;
    margin-right: 4px;
    vertical-align: -2px;
  }
}
.role-form .night-steps {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin: 9px 0 0;
  align-items: stretch;
  .nst-head {
    margin-bottom: 2px;
  }
  .nst-list {
    display: flex;
    flex-direction: column;
    margin: 0;
    padding: 0;
    list-style: none;
  }
  // a step is a LINE of the panel, not a card in it
  .nst-row {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 7px 2px;
    border-bottom: 1px solid rgba(255, 255, 255, 0.07);
    &:last-child {
      border-bottom: 0;
    }
  }
  .nst-n {
    flex: none;
    width: 12px;
    padding-top: 4px;
    font-size: 13px;
    text-align: left;
    opacity: 0.35;
  }
  // FT-1529: THE CONTROL ROW — who · picks/told · how many · what, wrapping
  // inside the column as two whole groups; the row's own actions at its end.
  .nst-ctrls {
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 5px;
    .forge-cell {
      padding: 3px 9px;
    }
  }
  .nst-grp {
    display: flex;
    align-items: center;
    gap: 5px;
  }
  .nst-count,
  .nst-noun {
    font-size: 13px;
  }
  // ── WHO CHOOSES, IN THEIR OWN COLOUR (FT-1529, L.O.T.S.'s FT-1535 rule) ──
  // INK AND EDGE, NEVER A FILL: every other toggle in this panel lights up as
  // a solid plate (the Picks | Told toggle right beside this one), so a
  // filled chooser cell would read as "merely selected". The chosen cell of
  // THIS toggle is a dark cell with coloured words and a coloured hairline.
  //   Reader  the Reader's colour, lifted for small text and turned off the
  //           Light/Shadow hues (golem/readerColor's --reader-mark-rgb).
  //   Player  the seat's own side — and a Lot on the bench has no seat, so
  //           no side colour: the chosen cell keeps the plain ink, marked by
  //           weight and a neutral hairline only.
  .nst-by .forge-cell.on {
    font-weight: 600;
    text-shadow: none;
  }
  .nst-by .nst-by-player.on {
    color: rgba(255, 255, 255, 0.92);
    background: rgba(255, 255, 255, 0.04);
    box-shadow:
      inset 0 0 0 1px rgba(255, 255, 255, 0.45),
      inset 0 2px 6px rgba(0, 0, 0, 0.35);
  }
  .nst-by .nst-by-reader.on {
    color: rgb(var(--reader-mark-rgb, var(--reader-rgb)));
    background: rgba(var(--reader-rgb), 0.1);
    box-shadow:
      inset 0 0 0 1px rgba(var(--reader-mark-rgb, var(--reader-rgb)), 0.95),
      inset 0 2px 6px rgba(0, 0, 0, 0.35);
  }
  // the row's own actions sit QUIETLY at its end
  .nst-acts {
    flex: none;
    display: inline-flex;
    gap: 1px;
    padding-top: 2px;
  }
  .nst-act {
    min-width: 0;
    padding: 2px 5px;
    background: none;
    border: 0;
    border-radius: 4px;
    color: rgba(255, 255, 255, 0.35);
    font-size: 12px;
    cursor: pointer;
    &:hover:not(:disabled) {
      background: rgba(var(--reader-rgb), 0.25);
      color: rgba(255, 255, 255, 0.85);
    }
    &:focus-visible {
      @include control-focus-ring;
    }
    &:disabled {
      opacity: 0.3;
      cursor: default;
    }
  }
  .nst-preview.on {
    color: rgb(var(--reader-mark-rgb, var(--reader-rgb)));
  }
  // FT-1514: the template dropdown SEEDS the list, so it stands with
  // "+ Add a step"
  .nst-foot {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
  }
  .nst-start {
    font-size: 13px;
  }
}
// FT-1514: the preview's own foot — the checklist row it shows, and the
// switch that says whose eyes it is seen through. Below every edit control,
// because it edits nothing.
.role-form .nst-previewfoot {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 12px;
  padding-top: 10px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  .nst-viewbar {
    display: flex;
    align-items: center;
    gap: 9px;
  }
  .nsv-word {
    font-size: 12px;
    opacity: 0.6;
  }
}
.role-form .nst-view {
  flex-shrink: 0;
}
// FT-1514: the "Ability spent token" chip, home with the Reminder tokens —
// it hugs its own words instead of stretching to the group's width.
.role-form .fg-rem .fg-spent {
  align-self: flex-start;
  margin-top: 6px;
}
// FT-1040: reminder tokens as pills — type + Enter mints one, the × removes.
// FT-1120: the pill is TWO controls in one plate now — the name opens that
// token's deal rule, the × still removes it — so the box moved from the button
// to the wrapper and the two children sit inside it unplated. The look is
// unchanged for a pill nobody has given a rule.
.role-form .rem-pills {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
  width: 100%;
  .rem-pill {
    display: inline-flex;
    align-items: center;
    background: rgba(0, 0, 0, 0.45);
    border: 1px solid #3d3d3d;
    border-radius: 10px;
    color: rgba(255, 255, 255, 0.85);
    font-size: 12px;
    button {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      background: transparent;
      border: 0;
      color: inherit;
      font-family: inherit;
      font-size: inherit;
      cursor: pointer;
      &:focus-visible {
        @include control-focus-ring;
      }
    }
    .rem-name {
      padding: 2px 3px 2px 9px;
    }
    .rem-x {
      padding: 2px 8px 2px 4px;
      opacity: 0.6;
      &:hover {
        color: #ff8a8a;
        opacity: 1;
      }
    }
    // THE MARK a rule-bearing pill wears: the chip family's own lit state
    // (`$control-on-*`, the same blood the wakes cells and the Affects-setup
    // chip light up in) plus the hand the popover's toggle wears, so the pill
    // and the control that set it say the same thing with the same glyph.
    // Not a new idiom — the one this file already uses for "this one is on".
    &.dealt {
      border-color: $control-on-edge;
      background: $control-on-bg;
      color: $control-on-color;
    }
    .rem-mark {
      font-size: 10px;
      opacity: 0.85;
    }
    &:hover {
      border-color: #a01414;
    }
    // the pill whose rule is open — the panel below is about THIS one
    &.editing {
      border-color: rgba(var(--reader-rgb), 0.9);
    }
  }
  .rem-input {
    flex: 1;
    min-width: 150px;
  }
}
// FT-1120: THE DEAL POPOVER — one toggle, and the rule's three questions when
// it is on. In flow beneath the pill row (see the template note: the identity
// column is a scroll box, so a floating panel opened at its foot would be
// clipped), with a tail pointing at the pill it belongs to.
.role-form .rem-pop {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 7px;
  margin-top: 8px;
  padding: 9px 10px;
  background: rgba(12, 9, 14, 0.96);
  border: 1px solid rgb(var(--reader-deep-rgb));
  border-radius: 6px;
  box-shadow: 0 6px 18px rgba(0, 0, 0, 0.6);
  .rem-tail {
    position: absolute;
    top: -10px;
    color: rgb(var(--reader-deep-rgb));
    font-size: 12px;
    line-height: 1;
    pointer-events: none;
  }
  .rem-pop-title {
    font-size: 12px;
    letter-spacing: 0.6px;
    color: rgba(255, 255, 255, 0.85);
  }
  .rem-pop-rows {
    display: flex;
    flex-direction: column;
    gap: 6px;
    width: 100%;
  }
  .rem-pop-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    margin: 0;
  }
  .rem-pop-lbl {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.7);
  }
  .rem-pop-done {
    align-self: flex-end;
  }
}
// the forge's team choice wears the workbench toggle look, not a native select
.role-form .team-pick {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  .team-btn {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 5px 11px;
    background: rgba(0, 0, 0, 0.45);
    border: 1px solid #3d3d3d;
    border-radius: 6px;
    color: rgba(255, 255, 255, 0.75);
    cursor: pointer;
    font-family: inherit;
    font-size: 13px;
    text-transform: capitalize;
    svg {
      width: 14px;
      height: 14px;
    }
    .demon-glyph {
      // width AND height so every button matches — object-fit keeps the wide
      // townsfolk art (160×96) letterboxed instead of squeezed square (the
      // count-icon idiom; user report, FT-1040 rider)
      width: 15px;
      height: 15px;
      object-fit: contain;
    }
    &:not(.on) .demon-glyph {
      filter: grayscale(1) brightness(1.35);
      opacity: 0.75;
    }
    &:hover {
      border-color: #666;
    }
    @each $team, $color in $team-colors {
      &.team-#{$team}.on {
        border-color: $color;
        background: rgba($color, 0.16);
        color: lighten($color, 22%);
      }
    }
  }
}

.custom.workbench {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  text-align: left;

  // The + buttons: square, icon-only, the book's purple on hover.
  // `.button.wb-plus`, not `.wb-plus`: the workbench's own `.button:hover`
  // (further down this sheet) paints every button #ff7070, and at equal
  // specificity the later rule won — which is why the plus kept flashing red
  // on hover whatever its own rule said. Three classes beats two.
  .button.wb-plus {
    // FT-981 (user call): THE GRIMOIRE'S DARKEST PLUM, glyph and edge both.
    //
    // It rested on rgb(120, 105, 135) — RoleDrawer's lighter control purple —
    // over the shared button's grey #3d3d3d edge, which read as a grey box
    // with a lavender plus in it rather than as one purple control. Taking
    // $grimoire-plum for BOTH is what makes it one object, and it is the exact
    // value the grimoire drawer's cover edge wears.
    //
    // Measured at the size it actually renders (26x26, 15px glyph) on the
    // near-black this bench composites, rgb(1,1,1): the plum ink is 1.98:1,
    // under WCAG's 3:1 for a graphical object. Kept anyway, with eyes open —
    // see $grimoire-plum's own note. The plus is a thick, unmistakable shape,
    // the matching edge doubles the cue, and hover goes to 6.07:1, so the
    // control announces itself the moment it is approached. In the team row it
    // sits beside four grey-edged toggles and is the only purple thing there,
    // which is the reading that matters: the one ACTION among the filters.
    padding: 0 !important;
    width: 26px;
    height: 26px;
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    color: rgb(var(--reader-deep-rgb));
    border-color: rgb(var(--reader-deep-rgb));
    svg {
      width: 15px;
      height: 15px;
      display: block;
      margin: 0;
    }
    // the edge follows the glyph — the shared button hover reddens it too
    &:hover {
      color: rgb(var(--reader-rgb));
      border-color: rgba(var(--reader-rgb), 0.85);
    }
  }
  // FT-970: delete-for-everyone, sitting beside the plus in the same 26px
  // square. It is the ONE control on this bench that wears blood at rest:
  // the plus makes a script, this ends one for everybody who has the link,
  // and the pair must not read as two shades of the same act. Three classes
  // for the same specificity reason the plus documents above.
  .button.wb-destroy {
    padding: 0 !important;
    width: 26px;
    height: 26px;
    display: inline-flex !important;
    align-items: center;
    justify-content: center;
    color: rgba(200, 70, 70, 0.75);
    svg {
      width: 14px;
      height: 14px;
      display: block;
      margin: 0;
    }
    &:hover {
      color: #ff7070;
      border-color: #a01414;
      background: rgba(160, 20, 20, 0.28);
    }
  }
  // FT-1490: a real button (keyboard-reachable), still worn as a link, now
  // beside the + in the head's actions
  .ns-export {
    margin: 0 0 0 6px;
    padding: 0;
    align-self: center;
    background: none;
    border: 0;
    color: inherit;
    font-family: inherit;
    font-size: 12px;
    opacity: 0.7;
    cursor: pointer;
    text-decoration: underline dotted;
    &:hover,
    &:focus-visible {
      color: #ff8a8a;
      opacity: 1;
    }
  }
  // Our buttons, not upstream's shiny pills: small, flat, dark, hairline.
  // Pixel-sized — the app's base font is viewport-huge, so percentages lie.
  .button {
    margin: 0;
    padding: 2px 9px;
    border: 1px solid #3d3d3d;
    border-radius: 5px;
    background: rgba(0, 0, 0, 0.65);
    box-shadow: none;
    font-weight: normal;
    font-size: 13px;
    line-height: 1.6;
    &:before,
    &:after {
      content: none;
    }
    &:hover {
      border-color: #a01414;
      color: #ff7070;
    }
  }

  // FT-1188: the head is the big surface's shared head now (surface.scss) —
  // the same three mixins the Chronicle includes, so the two surfaces cannot
  // drift apart on the rule, the gap or the centring.
  .wb-top {
    @include surface-head;
    // The title, centered, alone on its line (the shell's close × is the
    // only other thing at this height).
    .wb-row1 {
      @include surface-head-title-row;
    }
    // the picker sits CENTERED under the title (user call); the actions
    // keep the right flank — a 1fr/auto/1fr grid holds the center true
    .wb-row2 {
      @include surface-head-control-row;
      .wb-script-picker {
        grid-column: 2;
        width: 300px;
      }
      .wb-actions {
        // hug the selector, not the far edge (user call)
        grid-column: 3;
        justify-self: start;
        display: flex;
        flex-wrap: wrap;
        justify-content: flex-start;
        gap: 4px;
      }
    }
  }

  .wb-body {
    display: flex;
    flex-grow: 1;
    min-height: 0;
    gap: 14px;
    padding-top: 8px;
  }

  // ── FT-1043: the PANE — the builder head over the script view ──────────
  .wb-pane {
    flex: 1 1 0;
    min-width: 0;
    min-height: 0;
    display: flex;
    flex-direction: column;
  }
  // the head: icon, name, and the Save/Discard plate at
  // the far right — everything the retired New-script modal asked for,
  // living where the script lives
  .builder-head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 6px;
    .bh-icon {
      width: 42px;
      height: 42px;
      flex-shrink: 0;
      border: 1px solid transparent;
      border-radius: 8px;
      background-size: contain;
      background-position: center;
      background-repeat: no-repeat;
      background-origin: content-box;
      padding: 2px;
      cursor: pointer;
      transition:
        border-color 150ms,
        background-color 150ms;
      &:hover,
      &.dragover {
        border-color: rgb(var(--reader-deep-rgb));
        background-color: rgba(var(--reader-deep-rgb), 0.22);
      }
    }
    .bh-name {
      flex: 0 1 300px;
      min-width: 120px;
      background: rgba(0, 0, 0, 0.5);
      color: white;
      border: 1px solid #666;
      border-radius: 4px;
      padding: 5px 10px;
      font-size: 16px;
      font-family: inherit;
      &:focus {
        outline: none;
        border-color: $control-focus;
      }
    }
  }
  // ONE Save/Discard idiom (FT-1043): the purple plate, top right, worn by
  // the forge's header and the builder's alike. $grimoire-plum because both
  // are CONSTRUCTIVE surfaces — the same claim the + and the forge's own
  // border already make.
  .acts-plate {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-left: auto;
    padding: 3px 6px;
    border: 1px solid rgb(var(--reader-deep-rgb));
    border-radius: 8px;
    background: rgba(var(--reader-deep-rgb), 0.14);
    .button {
      margin: 0;
    }
  }

  // ── the small-screen stand-in (see SMALL_BENCH in the script) ───────────
  // Not a stripped-down bench: a short note, the loaded script, and a way
  // out. The picker above it is still live, which is the whole reason this
  // is a note inside the modal rather than a modal that refuses to open.
  .wb-small {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 9px;
    padding: 18px 6px 8px;
    text-align: center;

    // a landscape phone gives the modal 345px; the note tightens to fit it
    // rather than leaning on the slot's scroll for its own Close button
    @media (max-height: 519px) {
      gap: 5px;
      padding: 6px 6px 4px;
      .ws-body {
        font-size: 82%;
      }
      .ws-note {
        display: none;
      }
    }

    .ws-lead {
      font-family: PiratesBay, sans-serif;
      letter-spacing: 1px;
      font-size: 118%;
      margin: 0;
      color: #d8cdb4;
    }
    .ws-body,
    .ws-note {
      margin: 0;
      max-width: 34em;
      font-size: 88%;
      line-height: 1.45;
      opacity: 0.72;
    }
    .ws-note {
      font-size: 82%;
      opacity: 0.55;
    }

    .ws-loaded {
      display: flex;
      align-items: center;
      justify-content: center;
      gap: 9px;
      margin-top: 4px;
      .ws-mark {
        width: 34px;
        height: 34px;
        object-fit: contain;
      }
      .ws-name {
        font-family: PiratesBay, sans-serif;
        letter-spacing: 1px;
        font-size: 108%;
      }
    }

    .ws-counts {
      display: flex;
      gap: 10px;
    }
    .ws-count {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-size: 92%;
      opacity: 0.85;
      img,
      svg {
        width: 16px;
        height: 16px;
        object-fit: contain;
      }
      @each $team, $color in $team-colors {
        &.team-#{$team} {
          color: $color;
        }
      }
    }
    .ws-serves {
      margin: 0;
      font-size: 84%;
      opacity: 0.6;
    }

    // The shell's × is a 30px glyph in the corner — fine for a cursor, thin
    // for a thumb. The note carries its own way out at full tap size.
    .ws-close {
      margin-top: 6px;
      min-height: 44px;
      padding: 0 26px;
      font-family: PiratesBay, sans-serif;
      letter-spacing: 1px;
      font-size: 100%;
      color: white;
      background: rgba(0, 0, 0, 0.6);
      border: 1px solid #3d3d3d;
      border-radius: 7px;
      cursor: pointer;
      &:hover,
      &:focus-visible {
        outline: none;
        border-color: #a01414;
        color: #ff8a8a;
      }
    }
  }

  .wb-sidebar {
    width: 270px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    min-height: 0;
    // FT-1039: the search field — the tally rides inside it (feedback on
    // the search), the ✕ beside the tally when there is anything to clear.
    .wb-searchwrap {
      display: flex;
      flex: none; // the role LIST is the shrink region, never the controls
      align-items: center;
      gap: 4px;
      background: rgba(0, 0, 0, 0.5);
      border: 1px solid #666;
      border-radius: 4px;
      padding: 0 6px;
      margin-bottom: 6px;
      &:focus-within {
        border-color: $control-focus;
      }
      .wb-search {
        flex: 1 1 auto;
        min-width: 0;
        background: transparent;
        border: 0;
        color: white;
        padding: 4px 0;
        font-family: inherit;
        &:focus {
          outline: none;
        }
      }
      .ws-tally {
        flex-shrink: 0;
        font-size: 11px;
        opacity: 0.55;
        font-variant-numeric: tabular-nums;
        pointer-events: none;
      }
      .ws-clear {
        flex-shrink: 0;
        background: none;
        border: 0;
        color: rgba(255, 255, 255, 0.6);
        font-size: 15px;
        line-height: 1;
        padding: 0 2px;
        cursor: pointer;
        &:hover {
          color: #ff8a8a;
        }
        &:focus-visible {
          @include control-focus-ring;
        }
      }
    }
    // FT-1039: the facet CHIP ROWS — the fold dissolved; the facets stand in
    // the open and answer in the states the team row taught: lit when shown,
    // dimmed behind a blood hairline when hidden, greyed at zero.
    .wb-chips {
      display: flex;
      flex: none;
      flex-wrap: wrap;
      gap: 4px;
      margin-bottom: 5px;
    }
    .wb-chip {
      display: inline-flex;
      align-items: center;
      gap: 4px;
      padding: 2px 7px;
      background: rgba(0, 0, 0, 0.45);
      border: 1px solid #3d3d3d;
      border-radius: 6px;
      color: rgba(255, 255, 255, 0.75);
      font-family: inherit;
      font-size: 12px;
      cursor: pointer;
      .mark {
        width: 15px;
        height: 15px;
        object-fit: contain;
      }
      &:hover {
        border-color: #666;
      }
      &:focus-visible {
        @include control-focus-ring;
      }
      &.on {
        border-color: $control-on-edge;
      }
      &.exc {
        border-color: #7d0e0e;
      }
    }
    // one plate, cells inside — the night lens and the special flags
    .wb-seg {
      display: flex;
      flex: none;
      background: rgba(0, 0, 0, 0.45);
      border: 1px solid #3d3d3d;
      border-radius: 6px;
      overflow: hidden;
      margin-bottom: 5px;
      .wb-cell {
        flex: 1 1 0;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        gap: 4px;
        padding: 3px 4px;
        background: transparent;
        border: 0;
        border-right: 1px solid $control-divider;
        color: rgba(255, 255, 255, 0.75);
        font-family: inherit;
        font-size: 12px;
        cursor: pointer;
        white-space: nowrap;
        &:last-child {
          border-right: 0;
        }
        .moon {
          width: 13px;
          height: 13px;
          object-fit: contain;
          opacity: 0.9;
        }
        &:hover {
          background: rgba(255, 255, 255, 0.08);
        }
        &:focus-visible {
          @include control-focus-ring;
        }
      }
    }
    // the shared states — a chip and a cell answer alike
    .wb-chip,
    .wb-cell {
      .cnt {
        font-size: 11px;
        opacity: 0.7;
      }
      &.on {
        background: $control-on-bg;
        color: $control-on-color;
        .cnt {
          opacity: 1;
          font-weight: bold;
        }
      }
      &.exc {
        opacity: 0.45;
        .cnt {
          text-decoration: line-through;
        }
      }
      &.zero {
        opacity: 0.35;
      }
    }
    // FT-855: the team icon row — on/off toggles with live counts.
    .wb-team-row {
      display: flex;
      gap: 5px;
      margin: 7px 0 5px;
      .wb-team-toggle {
        flex: 1;
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 1px;
        padding: 5px 0 3px;
        background: rgba(0, 0, 0, 0.45);
        border: 1px solid #3d3d3d;
        border-radius: 6px;
        color: rgba(255, 255, 255, 0.75);
        cursor: pointer;
        font-family: inherit;
        svg {
          width: 16px;
          height: 16px;
        }
        .demon-glyph {
          width: 17px;
          height: 17px;
          // the wide townsfolk art keeps its ratio (FT-1040 rider) — the
          // same fix RoleDrawer's copy of this rule already carries
          object-fit: contain;
        }
        // img glyphs can't inherit the grey text color — grey them by hand
        // whenever the team isn't the active "show only" pick
        &:not(.on) .demon-glyph {
          filter: grayscale(1) brightness(1.35);
          opacity: 0.75;
        }
        .cnt {
          font-size: 12px;
          opacity: 0.7;
        }
        &:hover {
          border-color: #666;
        }
        @each $team, $color in $team-colors {
          &.team-#{$team}.on {
            border-color: $color;
            background: rgba($color, 0.16);
            color: lighten($color, 22%);
            .cnt {
              opacity: 1;
              font-weight: bold;
            }
          }
        }
        // hidden: dimmed behind a blood hairline, count struck through
        &.exc {
          border-color: #7d0e0e;
          opacity: 0.45;
          .cnt {
            text-decoration: line-through;
          }
        }
      }
    }
    .wb-all-roles {
      flex-grow: 1;
      overflow-y: auto;
      display: block;
      min-height: 0;
      // FT-1039: the modal's upstream flex rules leave align-content:center
      // on this ul, and block layout HONORS align-content now (Chrome 123+)
      // — a short filtered list was floating vertically centered in the rail.
      align-content: start;
      // FT-981: the nothing-matched panel. Written BEFORE the generic `li`
      // below so the row rules that follow (flex, pointer, the team stripe)
      // still win where they should — this one opts out of all three: it is
      // not a role, not clickable, and belongs to no team.
      li.wb-none {
        display: block;
        cursor: default;
        border-left: none;
        border-radius: 0;
        padding: 14px 10px;
        text-align: center;
        &:hover {
          background: none;
        }
        .wn-lead {
          margin: 0;
          font-size: 13px;
          line-height: 1.45;
          opacity: 0.75;
        }
        .wn-outs {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 6px;
          margin-top: 10px;
        }
        .wn-out {
          font-family: inherit;
          font-size: 12px;
          padding: 3px 10px;
          color: #d8cdb4;
          background: rgba(0, 0, 0, 0.55);
          border: 1px solid rgb(var(--reader-deep-rgb));
          border-radius: 4px;
          cursor: pointer;
          &:hover {
            color: #fff;
            border-color: rgba(var(--reader-rgb), 0.75);
            background: rgba(var(--reader-ground-rgb), 0.95);
          }
        }
        .wn-aside {
          margin: 10px 0 0;
          font-size: 11px;
          line-height: 1.4;
          opacity: 0.5;
        }
      }
      // FT-1489: a SOLID heading (see botc) — opaque ground, only the text
      // dimmed, stacked above the rows' buttons, so nothing reads through.
      li.wb-shelf-head {
        position: sticky;
        top: 0;
        z-index: 3;
        background: rgb(8, 8, 12);
        color: rgba(255, 255, 255, 0.75);
        font-size: 12px;
        text-transform: uppercase;
        letter-spacing: 1.5px;
        padding: 4px 6px 2px;
        cursor: default;
        border-left: none;
        border-radius: 0;
        &:hover {
          background: rgb(8, 8, 12);
        }
        small {
          color: rgba(255, 255, 255, 0.45);
          letter-spacing: 0;
        }
      }
      li {
        display: flex;
        align-items: center;
        gap: 6px;
        padding: 2px 6px;
        cursor: pointer;
        // square left corner, thin team stripe (user call)
        border-radius: 0 4px 4px 0;
        border-left: 2px solid transparent;
        @each $team, $color in $team-colors {
          &.team-#{$team} {
            border-left-color: rgba($color, 0.65);
          }
        }
        &:hover {
          background: rgba(255, 255, 255, 0.12);
        }
        .icon {
          width: 26px;
          height: 26px;
          background-size: cover;
          background-position: center;
          flex-shrink: 0;
        }
        .wb-role-name {
          flex-grow: 1;
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        small {
          color: rgba(255, 255, 255, 0.45);
        }
        // FT-1484 step 2: the Private badge — a small outlined tag
        .wb-private {
          flex-shrink: 0;
          padding: 0 4px;
          border: 1px solid rgba(230, 194, 122, 0.55);
          border-radius: 3px;
          color: #e6c27a;
          font-size: 10px;
          letter-spacing: 0.5px;
          text-transform: uppercase;
        }
        .wb-in {
          color: #7ed67e;
          font-weight: bold;
        }
        // FT-1486: the row's pen / copy — its own click target, shown while
        // the row is hovered or holds keyboard focus (always on touch)
        .wb-edit {
          flex-shrink: 0;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 22px;
          height: 22px;
          padding: 0;
          font-size: 11px;
          color: rgba(255, 255, 255, 0.75);
          background: rgba(0, 0, 0, 0.35);
          border: 1px solid transparent;
          border-radius: 4px;
          cursor: pointer;
          opacity: 0;
          transition:
            opacity 120ms ease,
            color 120ms ease,
            border-color 120ms ease;
          &:hover,
          &:focus-visible {
            color: #fff;
            border-color: rgba(var(--reader-rgb), 0.75);
            background: rgba(var(--reader-ground-rgb), 0.95);
          }
          &:focus-visible {
            outline: 1px solid rgba(var(--reader-rgb), 0.75);
            outline-offset: 1px;
          }
          &.busy {
            cursor: progress;
          }
          @media (prefers-reduced-motion: reduce) {
            transition: none;
          }
        }
        &:hover .wb-edit,
        &:focus-within .wb-edit {
          opacity: 1;
        }
        @media (pointer: coarse) {
          .wb-edit {
            opacity: 1;
          }
        }
      }
    }
    .button-group {
      margin-top: 6px;
    }
  }

  // The overlay panels (new-script, fork, ask, destroy) float over the
  // workbench — wearing the WORKBENCH's chrome (dark, blood hairline), not
  // upstream's white box. The role FORGE shares this base but is re-homed
  // as the Lot editor's left drawer (FT-1501) — see .forge-drawer below.
  .role-form {
    position: absolute;
    left: 50%;
    top: 50%;
    transform: translate(-50%, -50%);
    background: rgba(8, 8, 10, 0.97);
    border: 1px solid #4a0d0d;
    border-radius: 10px;
    padding: 14px 18px 18px;
    z-index: 30;
    width: min(680px, 92%);
    max-height: 88%;
    overflow-y: auto;
    text-align: center;
    box-shadow: 0 10px 50px #000;
    .forge-title {
      margin: 0 0 8px;
      font-size: 26px;
    }
    // Save/Discard ride the top right, staying put while the form scrolls
    .forge-acts {
      position: sticky;
      top: 0;
      z-index: 6;
      display: flex;
      justify-content: flex-end;
      gap: 6px;
      height: 0;
      margin: 0;
      overflow: visible;
      .button {
        margin: 0;
      }
    }
  }

  // FT-1040b (user call): the forge works WHERE THE SCRIPT LIVES — it takes
  // the script view's flex slot in wb-body instead of floating over it, so
  // the modal-backdrop chrome (centering transform, drop shadow, z-index)
  // goes. FT-1501 (user: "Give L.O.T.S. the same character preview, so both
  // games share this layout"): ONE SHELL — it is always the editor's
  // full-height LEFT DRAWER (the modal shell docks it: the unscoped rule at
  // the foot of this file), in the drawers' purple GLASS at its live width,
  // flush to the left edge so only its right corners round. The pane is a
  // COLUMN that does not scroll itself; the form column scrolls on its own.
  //
  // THE GLASS RIDES ITS OWN LAYER (`.ed-glass`, the form's first child,
  // behind everything): a backdrop-filter makes its element the containing
  // block of every `position: fixed` descendant, and the stage bar and the
  // art overlay are exactly that — fixed over the table, right of the
  // drawer. So the form carries no plate itself and only isolates, so the
  // layer's -1 lands behind the content and nowhere else.
  .wb-body .role-form.forge-drawer {
    position: relative;
    left: auto;
    top: auto;
    transform: none;
    flex: 1 1 0;
    min-width: 0;
    width: auto;
    height: 100%;
    max-height: none;
    min-height: 0;
    box-sizing: border-box;
    // FT-1528 + FT-1530 (ported, FT-1535): the right padding is the 4px
    // grip's and nothing more — the scrolling column reserves its own lane
    // for the blood-drip scrollbar, and the form columns bleed back through
    // this padding (.forge-cols below) so the scroll host's right edge is the
    // drawer's; the host then asks the directive for an edge inset.
    padding: 12px 4px 12px 16px;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    border: 0;
    border-radius: 0 10px 10px 0;
    background: transparent;
    box-shadow: none;
    z-index: auto;
    isolation: isolate;
    > .ed-glass {
      position: absolute;
      inset: 0;
      z-index: -1;
      pointer-events: none;
      @include face-disc-menu-plate(
        $r: var(--studio-dock, 520px),
        $radius: 0 10px 10px 0
      );
    }
    // FT-1530 (ported, FT-1535): the form columns bleed through the grip's
    // padding, so the scroll host (and its drip) reaches the drawer's edge
    > .forge-cols {
      margin-right: -4px;
    }
  }
  // while the forge holds the pane, the sidebar stays put but visibly paused
  // (its clicks would edit the script view you cannot see)
  .wb-body .wb-sidebar.inert {
    opacity: 0.45;
    pointer-events: none;
  }

  // the "name your copy" panel — one field, so it stays narrow and reads as
  // a question rather than a form.
  .fork-form {
    width: min(460px, 92%);
    text-align: left;
    h3 {
      margin: 0 0 8px;
      font-size: 22px;
    }
    .fk-note {
      margin: 0 0 12px;
      font-size: 13px;
      line-height: 1.45;
      opacity: 0.7;
      strong {
        opacity: 1;
      }
    }
    label {
      display: block;
      font-size: 12px;
      text-transform: uppercase;
      letter-spacing: 1.5px;
      opacity: 0.6;
      margin-bottom: 4px;
    }
    .fk-name {
      width: 100%;
      font-size: 17px;
      padding: 7px 12px;
      margin: 0;
    }
    .role-error {
      margin: 8px 0 0;
      font-size: 13px;
    }
    .fk-fix {
      display: inline-block;
      margin-left: 6px;
      font-weight: normal;
      color: rgb(var(--reader-rgb));
      text-decoration: underline;
      cursor: pointer;
      &:hover {
        color: #fff;
      }
    }
    .fk-acts {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 14px;
      .button {
        margin: 0;
      }
      // the fork itself wears the book's purple, not the blood
      .fk-go {
        border-color: rgba(var(--reader-rgb), 0.85);
        background: rgba(var(--reader-rgb), 0.42);
        &:hover {
          background: rgba(var(--reader-rgb), 0.55);
          color: white;
        }
        // FT-1043: dark until the copy's name differs from the source's
        &.disabled {
          opacity: 0.4;
          pointer-events: none;
        }
      }
      // ...and the delete wears the blood, overriding it (FT-970). The purple
      // means "this makes something"; nothing on this bench should be able to
      // destroy a script for other people while wearing the constructive
      // colour. Same shape, opposite signal — Menu's own danger button is the
      // reference, down to the #a01414 edge.
      .fk-go.destroy-go {
        border-color: #a01414;
        background: rgba(160, 20, 20, 0.32);
        color: #ff9a9a;
        &:hover {
          background: rgba(160, 20, 20, 0.55);
          color: white;
        }
      }
    }
  }

  // FT-970: the delete confirm's own heading goes blood, so the panel is
  // recognisable as the dangerous one before any of its words are read.
  .destroy-form h3 {
    color: #ff9a9a;
  }

  // FT-1043: the ICON OVERLAY — the retired New-script modal's art browser,
  // floating alone. Purple, not blood: picking a mark is constructive.
  .icon-overlay {
    width: min(760px, 94%);
    text-align: left;
    display: flex;
    flex-direction: column;
    border-color: rgb(var(--reader-deep-rgb));
    h3 {
      text-align: center;
      margin: 0 0 12px;
    }
    .io-tools {
      display: flex;
      align-items: center;
      gap: 10px;
      margin-bottom: 10px;
    }
    // FT-856: an upload arrives twice — inked and untouched; one click apart
    .ns-style-toggle {
      display: inline-flex;
      border: 1px solid #3d3d3d;
      border-radius: 6px;
      overflow: hidden;
      font-size: 13px;
      span {
        padding: 2px 12px;
        cursor: pointer;
        &.on {
          background: rgba(var(--reader-deep-rgb), 0.55);
          font-weight: bold;
        }
        &:not(.on):hover {
          background: rgba(255, 255, 255, 0.08);
        }
      }
    }
    .ns-browse {
      .ns-search {
        width: 100%;
        margin: 0 0 6px;
      }
      // the grid carries its own cell dress: the .icon-picker base rules
      // never reached the old ns-grid either (no .icon-picker ancestor), so
      // the cells spell out block icons and small labels themselves
      .ns-grid {
        max-height: 300px;
        width: 100%;
        margin: 0;
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(86px, 1fr));
        overflow-y: auto;
        border: 1px solid #444;
        border-radius: 6px;
        background: rgba(0, 0, 0, 0.3);
        padding: 4px;
        .icon-cell {
          width: auto;
          padding: 4px 0;
          cursor: pointer;
          border-radius: 6px;
          text-align: center;
          .il-thumb {
            display: block;
            width: 48px;
            height: 48px;
            margin: 0 auto;
            opacity: 0.9;
          }
          .label {
            display: block;
            font-size: 10px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            padding: 0 3px;
          }
          &:hover {
            background: rgba(var(--reader-deep-rgb), 0.35);
          }
          &.selected {
            outline: 2px solid rgb(var(--reader-deep-rgb));
          }
        }
      }
      .feed-none {
        padding: 14px;
        font-size: 13px;
        opacity: 0.6;
        text-align: center;
      }
      .il-loading {
        padding: 12px;
        opacity: 0.7;
      }
    }
    .fk-acts {
      display: flex;
      justify-content: flex-end;
      gap: 8px;
      margin-top: 12px;
    }
  }

  // FT-858: the shelf's hover card — icon left, bold name, ability body,
  // team-coloured border — moved WHOLESALE into RoleHoverCard.vue, which the
  // seats and the grimoire drawer now render too. Its rule had to leave this
  // block as well as this file: the card parks itself on document.body, and a
  // rule nested under a consumer's root selector stops matching the moment it
  // does (the ScriptPicker tooltip's bug, verbatim).

  .wb-error {
    position: absolute;
    bottom: 6px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 0, 0, 0.85);
    padding: 4px 14px;
    border-radius: 6px;
    z-index: 40;
  }
}
</style>

<!-- FT-1501: THE LOT EDITOR'S DOCK (Clocktower's Role Studio) — UNSCOPED on
     purpose: while the editor is open, the rules reach the MODAL SHELL
     (Modal.vue's backdrop and dialog) and the TOWN (TownSquare's
     #townsquare), neither of which carries this file's scope id. Keyed on
     `body.studio-docked`, which the editor sets the whole time it is open
     (preview or not) and clears the moment it closes.

     The move: the Scripts window docks to the left edge as the editor's
     drawer (the script row and the paused Lot rail step out), the backdrop
     stops dimming and stops catching clicks, and the town slides right by
     half the dock. The dial, the face and everything registered to
     --face-cx ride App.vue's --bg-off-x (which adds --studio-shift); the ring
     rides #townsquare's padding (its radius comes from the box's HEIGHT, so
     padding the left only moves the centre), with the ring capped to the
     room left over. No transforms anywhere: the night threads measure real
     coin rects, and a transformed ancestor would draw them twice-moved. -->
<style lang="scss">
@import "../../vars.scss";
@import "../../drawer.scss";

// `--studio-dock` is the drawer's live width, published on <body> by the
// editor (EditionModal publishDock, off the remembered width and every step
// of a grip drag), so the table's room follows the drag; the fallback is
// the drawer's default.
body.studio-docked {
  --studio-shift: calc(var(--studio-dock, 520px) / 2);
}
.modal-backdrop.editions.workbench.studio-docked {
  background: transparent;
  justify-content: flex-start;
  align-items: stretch;
  padding: 0;
  pointer-events: none;
  .modal {
    pointer-events: auto;
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    width: var(--studio-dock, 520px);
    max-width: none;
    height: 100vh;
    max-height: none;
    padding: 0;
    border-radius: 0;
    background: transparent;
    box-shadow: none;
  }
  .wb-top,
  .wb-sidebar {
    display: none;
  }
}
body.studio-docked #townsquare {
  padding-left: calc(20px + var(--studio-dock, 520px));
  .circle {
    max-height: #{"min(calc(100vw - var(--studio-dock, 520px) - var(--seat-reserve) - 40px), calc(100vh - 150px))"};
  }
}
// THE READOUT RIDES THE SAME SHIFT (Clocktower's FT-1479): TownInfo's
// `.info` is a flex-centred child of #app; the dial and the ring move right
// by half the dock, so it does too. `translate`, not `transform` — TownInfo's
// own phone rules put a `transform` on it, and a second one would replace
// rather than add. `.info-night` already lands on the dial's true centre by
// its own maths (it reads --face-cx, and so the shift), so it takes the
// opposite translate and rides the shift once, not twice.
body.studio-docked .info {
  translate: var(--studio-shift, 0px) 0;
  .info-night {
    translate: calc(var(--studio-shift, 0px) * -1) 0;
  }
}
// ON A PHONE it is a bottom sheet, like every other drawer: the table keeps
// the top of the screen and does not shift.
@media #{$phone-sheet} {
  body.studio-docked {
    --studio-shift: 0px;
  }
  .modal-backdrop.editions.workbench.studio-docked .modal {
    @include bottom-sheet(#4b3565);
  }
  body.studio-docked #townsquare {
    padding-left: 0;
  }
}
</style>
